import React, { useState, useEffect, useCallback, useRef } from 'react';
import { usePaged, Pagination } from '../utils';
import {
  Users, Phone, MessageCircle, Calendar, BarChart2, Trophy,
  XCircle, Clock, TrendingUp, AlertCircle, Activity,
  UserCheck, Star, Download, RefreshCw, Eye,
  MessageSquare, CheckCircle, X, Search, Send,
  Zap, Bell, ChevronDown, ChevronUp, DollarSign,
  Edit2, Trash2, Plus, Shield, FileText,
  Lock, Save, ChevronRight, Settings, Folder,
  UserPlus, Grid, Flame, Thermometer, Snowflake, Target, Bot,
  FileSpreadsheet} from 'lucide-react';
function toWA(p){const r=(p||'').replace(/\D/g,'');return r.startsWith('593')?r:r.startsWith('0')&&r.length===10?'593'+r.substring(1):r.length===9?'593'+r:r;}
// DS is now a function that takes '#4f6ef7' color from props
const makeDS = (p = '#4f6ef7') => ({
  bg:'#f5f7fb', bg2:'#f8fafc', bg3:'#f1f5f9',
  surface:'#ffffff', surf2:'#f8fafc', surf3:'#f1f5f9',
  border:'1px solid #eaecf0', border2:'1.5px solid #e2e8f0',
  shadow:'0 1px 3px rgba(0,0,0,0.06),0 4px 16px rgba(0,0,0,0.03)',
  shadow2:'0 4px 20px rgba(0,0,0,0.09)',
  glass:'rgba(0,0,0,0.02)', glass2:'#ffffff',
  text1:'var(--dark,#0f172a)', text2:'#475569', text3:'#94a3b8',
  red:'#ef4444', green:'#10b981', yellow:'#f59e0b',
  blue: p, primary: p,
  purple:'#8b5cf6',
  fontHead:"'DM Sans',sans-serif", fontBody:"'DM Sans',sans-serif",
});
let DS = makeDS();
let CB = '#ffffff'; // card background (updated per company)
let CBorder = '#4f6ef720'; // card border
let CShadow = '0 2px 12px #4f6ef714'; // card shadow
// Estilo de Inicio/Pipeline compartido por todas las pestañas; NAVY = "Color de textos" de superadmin
const NAVY = 'var(--dark,#0f172a)';
function PageHead({ title, sub, right }) {
  return (
    <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-end', gap:12, flexWrap:'wrap', marginBottom:20, color:NAVY }}>
      <div>
        <h1 style={{ margin:0, fontSize:MOB?22:28, fontWeight:800, letterSpacing:'-0.5px' }}>{title}</h1>
        {sub && <p style={{ margin:'4px 0 0', fontSize:14, color:'#64748b' }}>{sub}</p>}
      </div>
      {right}
    </div>
  );
}
function Kpi({ l, v, c, onClick, active }) {
  return (
    <div onClick={onClick} style={{ background:'var(--card-bg,#fff)', borderRadius:16, border:`1px solid ${active ? c : '#e5e7eb'}`, padding:MOB?14:20, cursor:onClick?'pointer':'default' }}>
      <div style={{ display:'flex', alignItems:'center', gap:7, fontSize:14, color:'#475569' }}><span style={{ width:8, height:8, borderRadius:'50%', background:c }}/>{l}</div>
      <div style={{ fontSize:MOB?24:30, fontWeight:800, margin:'8px 0 0', letterSpacing:'-0.5px', color:NAVY }}>{v}</div>
    </div>
  );
}
// Para reusar Inicio/Pipeline desde otro panel (asesor): DS y MOB de este módulo solo se fijan al renderizar DashboardAdmin
export function syncAdminTheme(primary) { DS = makeDS(primary); MOB = window.innerWidth < 768; }
let _setConfirmA = null;
function askConfirm(msg, onYes) { if (_setConfirmA) _setConfirmA({ msg, onYes }); else if (window.confirm(msg)) onYes(); }
function ConfirmModal({ msg, onYes, onClose }) {
  return (
    <div style={{position:'fixed',inset:0,background:'rgba(15,23,42,0.55)',backdropFilter:'blur(4px)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:9999}} onClick={onClose}>
      <div style={{background:'#fff',borderRadius:20,padding:'28px 32px',width:'min(340px,92vw)',boxShadow:'0 24px 64px rgba(0,0,0,0.18)',border:'1px solid #e2e8f0'}} onClick={e=>e.stopPropagation()}>
        <div style={{fontSize:18,fontWeight:800,color:'var(--dark,#0f172a)',marginBottom:8}}>¿Estás seguro?</div>
        <div style={{fontSize:13,color:'#64748b',marginBottom:24,lineHeight:1.6}}>{msg}</div>
        <div style={{display:'flex',gap:10}}>
          <button onClick={onClose} style={{flex:1,padding:'11px',borderRadius:10,border:'1px solid #e2e8f0',background:'#f8fafc',color:'#64748b',fontWeight:700,fontSize:13,cursor:'pointer'}}>No, cancelar</button>
          <button onClick={()=>{onYes();onClose();}} style={{flex:1,padding:'11px',borderRadius:10,border:'none',background:'#ef4444',color:'#fff',fontWeight:700,fontSize:13,cursor:'pointer'}}>Sí, eliminar</button>
        </div>
      </div>
    </div>
  );
}
let MOB = false; // true cuando pantalla < 768px
const API = process.env.REACT_APP_API_URL
  ? process.env.REACT_APP_API_URL.replace(/\/api\/?$/, '')
  : `${window.location.protocol}//${window.location.hostname}:3001`;
const tok = () => localStorage.getItem('crm_token');

// Funcion de exportacion
async function exportarCSV(tipo) {
  try {
    const token = localStorage.getItem('crm_token');
    const res = await fetch(`${API}/api/export/${tipo}`, {
      headers: { 'Authorization': `Bearer ${token}` }
    });
    if (!res.ok) throw new Error('Error exportando');
    const blob = await res.blob();
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href     = url;
    a.download = `${tipo}_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  } catch(err) {
    alert('Error al exportar: ' + err.message);
  }
}

async function apiFetch(method, path, body) {
  const res = await fetch(`${API}${path}`, {
    method,
    headers: { 'Content-Type':'application/json', Authorization:`Bearer ${tok()}` },
    body: body ? JSON.stringify(body) : undefined,
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || 'Error');
  return data;
}

// Metas de venta — persisten en la BD (tabla sales_goals vía /api/supervisor/metas,
// compartida con el supervisor: mismo endpoint que team-activities/asesores). Antes
// vivían solo en localStorage y admin/supervisor veían valores distintos.
function useMetas() {
  const [metas, setMetas]           = useState({});
  const [metaGlobal, setMetaGlobal] = useState(null);
  useEffect(() => {
    apiFetch('GET', '/api/supervisor/metas')
      .then(d => { setMetas(d.perAdvisor || {}); setMetaGlobal(d.global ?? null); })
      .catch(() => {});
  }, []);
  const saveMeta = async (advisorId, val) => {
    const target = parseInt(val) || 10;
    setMetas(m => ({ ...m, [advisorId]: target }));
    await apiFetch('PUT', '/api/supervisor/metas', { advisorId, target });
  };
  const saveMetaGlobal = async (val) => {
    const target = parseInt(val) || 0;
    setMetaGlobal(target);
    await apiFetch('PUT', '/api/supervisor/metas', { advisorId: null, target });
  };
  return { metas, metaGlobal, saveMeta, saveMetaGlobal };
}

// Fecha real de la venta (metadata.won_at, la pone el trigger trg_client_won_at); updated_at solo para ventas sin ella
const fechaVenta = c => c.metadata?.won_at || c.updated_at || c.created_at;

const MESES = ['Ene','Feb','Mar','Abr','May','Jun','Jul','Ago','Sep','Oct','Nov','Dic'];

// Logos de canal (los mismos de siempre), a cualquier tamaño
const canalLogo = (k, s = 28) => ({

              facebook:  <svg viewBox="0 0 24 24" width={s} height={s} fill="#1877F2"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>,
              instagram: <svg viewBox="0 0 24 24" width={s} height={s}><defs><radialGradient id="igc" cx="30%" cy="107%"><stop offset="0%" stopColor="#fdf497"/><stop offset="45%" stopColor="#fd5949"/><stop offset="60%" stopColor="#d6249f"/><stop offset="90%" stopColor="#285AEB"/></radialGradient></defs><path fill="url(#igc)" d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/></svg>,
              tiktok:    <svg viewBox="0 0 24 24" width={s} height={s}><path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z" fill="#010101"/></svg>,
              whatsapp:  <svg viewBox="0 0 24 24" width={s} height={s} fill="#25D366"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>,
})[k];

/* ══════════════════════════ UI ATOMS ══════════════════════════ */
function Av({ ini, color=DS.primary, size=34 }) {
  return (
    <div style={{ width:size, height:size, borderRadius:'50%', background:`${color}22`, display:'flex', alignItems:'center', justifyContent:'center', fontSize:size*0.33, fontWeight:800, color, flexShrink:0 }}>
      {ini}
    </div>
  );
}

function Tag({ label, color, bg }) {
  return <span style={{ padding:'2px 9px', borderRadius:999, fontSize:11, fontWeight:700, color, background:bg, whiteSpace:'nowrap' }}>{label}</span>;
}

function Card({ title, Icon, iconColor=DS.primary, children, action, raw }) {
  return (
    <div style={{ background:'var(--card-bg,#fff)', borderRadius:16, border:'1px solid #e5e7eb', overflow:'hidden' }}>
      {title && (
        <div style={{ padding:'16px 20px', borderBottom:'1px solid #eef0f3', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
          <div style={{ fontSize:16, fontWeight:700, color:NAVY, display:'flex', alignItems:'center', gap:8 }}>
            <Icon size={14} color={iconColor}/> {title}
          </div>
          {action}
        </div>
      )}
      <div style={{ padding: raw ? 0 : 18 }}>{children}</div>
    </div>
  );
}

function BarChart({ data, color=DS.primary, hl }) {
  const max = Math.max(...data) || 1;
  return (
    <div style={{ display:'flex', gap:3, alignItems:'flex-end', height:72 }}>
      {data.map((v,i) => (
        <div key={i} style={{ flex:1, display:'flex', flexDirection:'column', alignItems:'center', gap:2 }}>
          <div style={{ width:'100%', background:`${color}18`, borderRadius:4, height:60, display:'flex', alignItems:'flex-end' }}>
            <div style={{ width:'100%', background: i===hl ? '#f59e0b' : color, borderRadius:4, height:`${(v/max)*100}%` }}/>
          </div>
          <div style={{ fontSize:7, color:'#9aa5be', fontWeight:600 }}>{MESES[i]}</div>
        </div>
      ))}
    </div>
  );
}

function ActIcon({ tipo, color }) {
  const icons = { phone:Phone, msg:MessageCircle, calendar:Calendar, trophy:Trophy, x:XCircle };
  const Ic = icons[tipo] || Activity;
  return (
    <div style={{ width:34, height:34, borderRadius:9, background:`${color}18`, display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
      <Ic size={15} color={color}/>
    </div>
  );
}

function fmt(n) { return '$'+Number(n).toLocaleString('es-MX'); }

/* ══════════════════════════ MENSAJES (interno) ══════════════════════════ */
const INIT_MSGS = [
  { id:'m1', de:'u5', deNombre:'Rosa Peña',     deRol:'supervisor', para:'admin', texto:'El equipo cumplió el 80% de la meta esta semana.', hora:'hace 30 min', leido:false },
  { id:'m2', de:'u1', deNombre:'María García',  deRol:'asesor',     para:'admin', texto:'¿Puedo hacer un descuento especial a Roberto?',     hora:'hace 2 h',   leido:false },
  { id:'m3', de:'admin', deNombre:'Admin',       deRol:'admin',     para:'u5',    texto:'Excelente trabajo Rosa, sigan así.',                hora:'hace 4 h',   leido:true  },
  { id:'m4', de:'u6', deNombre:'Jorge Salinas', deRol:'supervisor', para:'admin', texto:'¿Puedo añadir un nuevo asesor esta semana?',         hora:'hace 6 h',   leido:true  },
];

const INIT_LEADS_ADMIN = [
  { id:'la1', nombre:'Andrés Castillo', telefono:'+52551098765', email:'andres@gmail.com',    msgBot:'Vi su anuncio, me interesa el plan premium.',        nivel:'caliente', score:90, asesor:'María García', origen:'WhatsApp Bot', fecha:new Date(Date.now()-1*3600000).toISOString()  },
  { id:'la2', nombre:'Patricia Núñez',  telefono:'+52339012345', email:'patricia@corp.mx',    msgBot:'¿Pueden enviarme información sobre sus planes?',     nivel:'caliente', score:82, asesor:'Carlos Ruiz',  origen:'WhatsApp Bot', fecha:new Date(Date.now()-2*3600000).toISOString()  },
  { id:'la3', nombre:'Fernando Reyes',  telefono:'+52554567890', email:'',                    msgBot:'Me recomendaron su empresa, quisiera cotización.',    nivel:'caliente', score:78, asesor:'María García', origen:'WhatsApp Bot', fecha:new Date(Date.now()-4*3600000).toISOString()  },
  { id:'la4', nombre:'Claudia Herrera', telefono:'+52813456789', email:'claudia@hotmail.com', msgBot:'¿Cuánto cuesta el plan básico?',                     nivel:'tibio',    score:55, asesor:'Luis Mendoza', origen:'WhatsApp Bot', fecha:new Date(Date.now()-6*3600000).toISOString()  },
  { id:'la5', nombre:'Ricardo Méndez',  telefono:'+52556789012', email:'ricardo@empresa.com', msgBot:'Hola, quiero información general.',                  nivel:'tibio',    score:50, asesor:'Ana Jiménez',  origen:'WhatsApp Bot', fecha:new Date(Date.now()-8*3600000).toISOString()  },
  { id:'la6', nombre:'Valentina Cruz',  telefono:'+52332345678', email:'',                    msgBot:'¿Tienen soporte en español?',                        nivel:'tibio',    score:42, asesor:'Carlos Ruiz',  origen:'WhatsApp Bot', fecha:new Date(Date.now()-10*3600000).toISOString() },
  { id:'la7', nombre:'Jorge Ponce',     telefono:'+52557890123', email:'',                    msgBot:'Ok',                                                 nivel:'frio',     score:18, asesor:'Luis Mendoza', origen:'WhatsApp Bot', fecha:new Date(Date.now()-14*3600000).toISOString() },
  { id:'la8', nombre:'Mónica Vargas',   telefono:'+52551234000', email:'',                    msgBot:'¿Esto es real o spam?',                              nivel:'frio',     score:12, asesor:'María García', origen:'WhatsApp Bot', fecha:new Date(Date.now()-18*3600000).toISOString() },
  { id:'la9', nombre:'Alejandro Ponce', telefono:'+52339876001', email:'',                    msgBot:'No gracias, solo curioseando.',                       nivel:'frio',     score:5,  asesor:'Ana Jiménez',  origen:'WhatsApp Bot', fecha:new Date(Date.now()-24*3600000).toISOString() },
];
const LEAD_COLS_ADMIN = {
  caliente:{ label:'Caliente', sub:'Muy interesados',          color:'#ef4444', bg:'#fff5f5', border:'#fecaca' },
  tibio:   { label:'Tibio',   sub:'Medianamente interesado', color:'#f59e0b', bg:'#fffbeb', border:'#fde68a' },
  frio:    { label:'Frío',    sub:'Poco interés / poca info', color:'#4f6ef7', bg:'#f0f1ff', border:'#c7d2fe' },
};
const ago = d => { const s=(Date.now()-new Date(d))/1000; if(s<60)return 'ahora'; if(s<3600)return `${Math.floor(s/60)}m`; if(s<86400)return `${Math.floor(s/3600)}h`; return `${Math.floor(s/86400)}d`; };

/* ══════════════════════════ TAB DASHBOARD ══════════════════════════ */

// ── Botón de exportación reutilizable ────────────────────────────────────────
function ExportMenu({ endpoint, label = 'Exportar', color, withMonth = false }) {
  const p = color || DS.primary;
  const [open, setOpen] = React.useState(false);
  const ref = React.useRef();

  React.useEffect(() => {
    function h(e) { if (ref.current && !ref.current.contains(e.target)) setOpen(false); }
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  // Selección de año y mes para exportar
  const nowY = new Date().getFullYear();
  const anios = [nowY, nowY - 1, nowY - 2];
  const nombresMes = ['Enero','Febrero','Marzo','Abril','Mayo','Junio','Julio','Agosto','Septiembre','Octubre','Noviembre','Diciembre'];
  const [anio, setAnio] = React.useState(nowY);
  const [mesNum, setMesNum] = React.useState(new Date().getMonth() + 1);

  function doExport(fmt, mes) {
    setOpen(false);
    const token = localStorage.getItem('crm_token');
    const url = `${API}/api/export/${endpoint}?format=${fmt}${mes ? `&mes=${mes}` : ''}`;
    fetch(url, { headers:{ Authorization:`Bearer ${token}` }})
      .then(r => {
        if (!r.ok) throw new Error('Error al exportar');
        return r.blob();
      })
      .then(blob => {
        const a = document.createElement('a');
        a.href = URL.createObjectURL(blob);
        a.download = `${endpoint}.${fmt === 'csv' ? 'csv' : 'xlsx'}`;
        a.click();
      })
      .catch(e => alert('Error: ' + e.message));
  }

  return (
    <div ref={ref} style={{position:'relative',display:'inline-block'}}>
      <button onClick={()=>setOpen(!open)}
        style={{display:'flex',alignItems:'center',gap:6,padding:'8px 14px',borderRadius:9,border:`1px solid ${p}30`,
          background:`${p}08`,color:p,fontWeight:700,fontSize:12,cursor:'pointer',transition:'all .15s'}}
        onMouseEnter={e=>{e.currentTarget.style.background=`${p}15`;}}
        onMouseLeave={e=>{e.currentTarget.style.background=`${p}08`;}}>
        <Download size={13}/> {label} <ChevronDown size={11}/>
      </button>
      {open && (
        <div style={{position:'absolute',right:0,top:'110%',background:'#fff',borderRadius:10,
          boxShadow:'0 8px 24px rgba(0,0,0,0.12)',border:'1px solid #eaecf0',zIndex:999,minWidth:180,maxHeight:320,overflowY:'auto'}}>
          {withMonth ? (
            <div style={{padding:'14px 16px',display:'flex',flexDirection:'column',gap:10}}>
              <div style={{fontSize:11,fontWeight:800,color:'#64748b'}}>Elige año y mes</div>
              <div style={{display:'flex',gap:8}}>
                <select value={anio} onChange={e=>setAnio(+e.target.value)}
                  style={{flex:1,padding:'8px 10px',borderRadius:8,border:'1px solid #e2e8f0',fontSize:12,color:'var(--dark,#0f172a)',cursor:'pointer'}}>
                  {anios.map(y => <option key={y} value={y}>{y}</option>)}
                </select>
                <select value={mesNum} onChange={e=>setMesNum(+e.target.value)}
                  style={{flex:1.4,padding:'8px 10px',borderRadius:8,border:'1px solid #e2e8f0',fontSize:12,color:'var(--dark,#0f172a)',cursor:'pointer'}}>
                  {nombresMes.map((n,i) => <option key={i} value={i+1}>{n}</option>)}
                </select>
              </div>
              <button onClick={()=>doExport('csv', `${anio}-${String(mesNum).padStart(2,'0')}`)}
                style={{display:'flex',alignItems:'center',justifyContent:'center',gap:8,padding:'10px',borderRadius:8,border:'none',
                  background:p,color:'#fff',cursor:'pointer',fontSize:12,fontWeight:700}}>
                <Download size={14}/> Descargar CSV
              </button>
              <button onClick={()=>doExport('csv')}
                style={{display:'flex',alignItems:'center',justifyContent:'center',gap:8,padding:'8px',borderRadius:8,border:'1px solid #e2e8f0',
                  background:'none',color:'#64748b',cursor:'pointer',fontSize:11,fontWeight:600}}>
                <FileText size={13}/> Descargar todo (histórico)
              </button>
            </div>
          ) : (
            <>
              <button onClick={()=>doExport('excel')}
                style={{display:'flex',alignItems:'center',gap:10,width:'100%',padding:'11px 16px',border:'none',
                  background:'none',cursor:'pointer',fontSize:12,fontWeight:600,color:'var(--dark,#0f172a)',textAlign:'left'}}
                onMouseEnter={e=>{e.currentTarget.style.background='#f0fdf4';}}
                onMouseLeave={e=>{e.currentTarget.style.background='none';}}>
                <FileSpreadsheet size={14} color="#10b981"/> Excel (.xlsx)
              </button>
              <button onClick={()=>doExport('csv')}
                style={{display:'flex',alignItems:'center',gap:10,width:'100%',padding:'11px 16px',border:'none',
                  background:'none',cursor:'pointer',fontSize:12,fontWeight:600,color:'var(--dark,#0f172a)',textAlign:'left'}}
                onMouseEnter={e=>{e.currentTarget.style.background='#eff6ff';}}
                onMouseLeave={e=>{e.currentTarget.style.background='none';}}>
                <FileText size={14} color={DS.primary}/> CSV (.csv)
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
}


function ReporteVentasBtn() {
  const p = DS.primary;
  const [open, setOpen]   = React.useState(false);
  const [desde, setDesde] = React.useState(() => {
    const d = new Date(); d.setDate(1);
    return d.toISOString().split('T')[0];
  });
  const [hasta, setHasta] = React.useState(() => new Date().toISOString().split('T')[0]);
  const [loading, setLoading] = React.useState(false);

  async function descargar() {
    setLoading(true);
    try {
      const token = localStorage.getItem('crm_token');
      const res = await fetch(`${API}/api/export/reporte-ventas?desde=${desde}&hasta=${hasta}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!res.ok) throw new Error('Error generando reporte');
      const blob = await res.blob();
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement('a');
      a.href     = url;
      a.download = `reporte_ventas_${desde}_${hasta}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      setOpen(false);
    } catch(err) {
      alert('Error: ' + err.message);
    }
    setLoading(false);
  }

  return (
    <>
      {open && (
        <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.4)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:9999}}>
          <div style={{background:'#fff',borderRadius:18,padding:28,width:'min(360px,92vw)',boxShadow:'0 20px 60px rgba(0,0,0,0.2)'}}>
            <div style={{fontSize:16,fontWeight:800,color:'var(--dark,#0f172a)',marginBottom:4}}>Reporte de ventas</div>
            <div style={{fontSize:12,color:'#94a3b8',marginBottom:20}}>Selecciona el período — descarga en Excel</div>
            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12,marginBottom:20}}>
              <div>
                <div style={{fontSize:11,fontWeight:700,color:'#64748b',marginBottom:6}}>DESDE</div>
                <input type="date" value={desde} onChange={e=>setDesde(e.target.value)}
                  style={{width:'100%',padding:'9px 12px',borderRadius:9,border:'1px solid #e2e8f0',fontSize:13,outline:'none',boxSizing:'border-box'}}/>
              </div>
              <div>
                <div style={{fontSize:11,fontWeight:700,color:'#64748b',marginBottom:6}}>HASTA</div>
                <input type="date" value={hasta} onChange={e=>setHasta(e.target.value)}
                  style={{width:'100%',padding:'9px 12px',borderRadius:9,border:'1px solid #e2e8f0',fontSize:13,outline:'none',boxSizing:'border-box'}}/>
              </div>
            </div>
            <div style={{background:'#f8fafc',borderRadius:10,padding:'12px 14px',marginBottom:16,fontSize:12,color:'#64748b'}}>
              El reporte incluye: resumen por asesor, por canal, y detalle de cada venta cerrada.
            </div>
            <div style={{display:'flex',gap:8}}>
              <button onClick={()=>setOpen(false)}
                style={{flex:1,padding:'10px',borderRadius:9,border:'none',background:'#f1f5f9',cursor:'pointer',fontSize:13,fontWeight:700,color:'#64748b'}}>
                Cancelar
              </button>
              <button onClick={descargar} disabled={loading}
                style={{flex:2,padding:'10px',borderRadius:9,border:'none',background:p,color:'#fff',cursor:'pointer',fontSize:13,fontWeight:700,display:'flex',alignItems:'center',justifyContent:'center',gap:6}}>
                <Download size={14}/>{loading ? 'Generando...' : 'Descargar Excel'}
              </button>
            </div>
          </div>
        </div>
      )}
      <button onClick={()=>setOpen(true)}
        style={{display:'flex',alignItems:'center',gap:7,padding:'9px 18px',borderRadius:10,border:'none',background:p,color:'#fff',fontSize:13,fontWeight:700,cursor:'pointer',boxShadow:`0 4px 14px ${p}40`}}>
        <FileSpreadsheet size={14}/> Reporte de ventas
      </button>
    </>
  );
}

function ExportButtons({ roles = ['leads','clients','ventas','equipo'] }) {
  const p = DS.primary;
  const opts = [
    { key:'leads',   label:'Leads',   Icon:Grid },
    { key:'clients', label:'Clientes',Icon:Users },
    { key:'ventas',  label:'Ventas',  Icon:DollarSign },
    { key:'equipo',  label:'Equipo',  Icon:UserCheck },
  ].filter(o => roles.includes(o.key));

  return (
    <div style={{background:'#fff',borderRadius:14,padding:'16px 20px',border:'1px solid #eaecf0',marginBottom:16}}>
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:12}}>
        <div style={{fontSize:16,fontWeight:700,color:NAVY}}>Exportar reportes</div>
        <div style={{fontSize:11,color:'#94a3b8'}}>Formato CSV — abre en Excel</div>
      </div>
      <div style={{display:'flex',gap:8,flexWrap:'wrap'}}>
        {opts.map(o=>(
          <button key={o.key} onClick={()=>exportarCSV(o.key)}
            style={{display:'flex',alignItems:'center',gap:6,padding:'8px 14px',borderRadius:9,border:`1px solid ${p}30`,background:`${p}08`,color:p,fontSize:12,fontWeight:700,cursor:'pointer',transition:'all .2s'}}
            onMouseEnter={e=>{e.currentTarget.style.background=p;e.currentTarget.style.color='#fff';}}
            onMouseLeave={e=>{e.currentTarget.style.background=`${p}08`;e.currentTarget.style.color=p;}}>
            <Download size={13}/>
            {o.label}
          </button>
        ))}
      </div>
    </div>
  );
}

// Periodo Hoy/7 días/Mes/Año/Rango/Todo → [start, end, prevStart) (lo usan Inicio y Tablero)
function usePeriodo(inicial = 'mes') {
  const hoyISO = new Date().toLocaleDateString('en-CA'); // YYYY-MM-DD local
  const [period,  setPeriod]  = React.useState(inicial);
  const [mesSel,  setMesSel]  = React.useState(hoyISO.slice(0,7));
  const [anioSel, setAnioSel] = React.useState(+hoyISO.slice(0,4));
  const [desde,   setDesde]   = React.useState(hoyISO.slice(0,8)+'01');
  const [hasta,   setHasta]   = React.useState(hoyISO);
  const dia = d => { const x = new Date(d); x.setHours(0,0,0,0); return x; };
  const [start, end, prevStart] = (() => {
    const t = dia(new Date()), mañana = new Date(t); mañana.setDate(t.getDate() + 1);
    if (period === 'hoy') { const a = new Date(t); a.setDate(t.getDate() - 1); return [t, mañana, a]; }
    if (period === '7d')  { const s0 = new Date(t); s0.setDate(t.getDate() - 6); const a = new Date(s0); a.setDate(s0.getDate() - 7); return [s0, mañana, a]; }
    if (period === 'mes') { const [y,m] = mesSel.split('-').map(Number); return [new Date(y,m-1,1), new Date(y,m,1), new Date(y,m-2,1)]; }
    if (period === 'año') return [new Date(anioSel,0,1), new Date(anioSel+1,0,1), new Date(anioSel-1,0,1)];
    if (period === 'rango') { const a = dia(desde+'T00:00'), b = dia(hasta+'T00:00'); b.setDate(b.getDate() + 1); return [a, b, new Date(a - (b - a))]; }
    return [new Date(0), new Date(8.64e15), null]; // todo
  })();
  const inP = d => d && new Date(d) >= start && new Date(d) < end;
  return { period, setPeriod, mesSel, setMesSel, anioSel, setAnioSel, desde, setDesde, hasta, setHasta, hoyISO, start, end, prevStart, inP };
}

function PeriodoPicker({ P }) {
  const { period, setPeriod, mesSel, setMesSel, anioSel, setAnioSel, desde, setDesde, hasta, setHasta, hoyISO } = P;
  const dateInp = { padding:'8px 10px', borderRadius:10, border:'1px solid #e5e7eb', background:'#fff', fontSize:13, color:NAVY, fontFamily:'inherit' };
  return (
    <div style={{ display:'flex', alignItems:'center', gap:8, flexWrap:'wrap' }}>
      {period === 'mes'   && <input type="month" value={mesSel} max={hoyISO.slice(0,7)} onChange={e => e.target.value && setMesSel(e.target.value)} style={dateInp}/>}
      {period === 'año'   && <select value={anioSel} onChange={e => setAnioSel(+e.target.value)} style={dateInp}>
        {Array.from({ length:6 }, (_, k) => +hoyISO.slice(0,4) - k).map(y => <option key={y} value={y}>{y}</option>)}</select>}
      {period === 'rango' && <><input type="date" value={desde} max={hasta} onChange={e => e.target.value && setDesde(e.target.value)} style={dateInp}/>
        <span style={{ color:'#94a3b8', fontSize:13 }}>a</span>
        <input type="date" value={hasta} min={desde} max={hoyISO} onChange={e => e.target.value && setHasta(e.target.value)} style={dateInp}/></>}
      <div style={{ display:'flex', background:'#fff', border:'1px solid #e5e7eb', borderRadius:10, padding:3, flexWrap:'wrap' }}>
        {[['hoy','Hoy'],['7d','7 días'],['mes','Mes'],['año','Año'],['rango','Rango'],['todo','Todo']].map(([k,l]) => (
          <button key={k} onClick={() => setPeriod(k)} style={{ padding:'7px 14px', borderRadius:8, border:'none', cursor:'pointer', fontSize:13, fontWeight:600,
            background:period===k?NAVY:'transparent', color:period===k?'#fff':'#475569' }}>{l}</button>
        ))}
      </div>
    </div>
  );
}

// mine: Inicio del asesor (sus leads/clientes, sin tabla Equipo; actividad = la suya)
export function TabDashboard({ user, users, leads: allLeads, clients: realClients, onNavigate, mine, activities }) {
  const p = DS.primary;
  const nav = onNavigate || (() => {});
  const leads   = allLeads || [];
  const clients = realClients || [];
  const P = usePeriodo('mes');
  const { period, anioSel, start, prevStart, inP } = P;
  const [acts,   setActs]   = React.useState([]);
  const { metas, metaGlobal } = useMetas();
  React.useEffect(() => {
    if (mine) return;
    apiFetch('GET', '/api/supervisor/team-activities').then(r => setActs(Array.isArray(r) ? r.slice(0, 5) : [])).catch(() => {});
  }, [mine]);
  const recientes = mine ? [...(activities || [])].sort((a,b) => new Date(b.created_at || b.at) - new Date(a.created_at || a.at)).slice(0, 5)
    .map(a => ({ ...a, created_at:a.created_at || a.at, advisor_name:'Tú', client_name:a.client_name || a.cname })) : acts;

  const now = new Date();
  const inPrev = d => prevStart && d && new Date(d) >= prevStart && new Date(d) < start;
  const prevLabel = { hoy:'ayer', '7d':'7 días previos', mes:prevStart && prevStart.toLocaleDateString('es-EC',{month:'long'}), año:String(anioSel-1), rango:'periodo anterior' }[period];
  const isWon    = c => (c.result || c.metadata?.result) === 'won';
  const amount   = c => Number(c.metadata?.saleAmount ?? c.saleAmount) || 0;
  const contacted= l => !!(l.metadata?.asesor_contacto || l.asesor_contacto);
  const wonAt    = fechaVenta;
  const fLeads   = leads.filter(l => inP(l.created_at));
  const pLeads   = leads.filter(l => inPrev(l.created_at));
  const ventas   = clients.filter(c => isWon(c) && inP(wonAt(c)));
  const pVentas  = clients.filter(c => isWon(c) && inPrev(wonAt(c)));
  const ingresos = ventas.reduce((s,c) => s + amount(c), 0);
  const conv     = fLeads.length ? (ventas.length / fLeads.length) * 100 : 0;
  const delta    = (a, b) => prevStart && b ? Math.round(((a - b) / b) * 100) : null;
  const team     = (users || []).filter(u => ['advisor','asesor','supervisor'].includes(u.role) && u.status === 'active');
  const meta     = mine ? (metas[user?.id] || 0) : (metaGlobal || team.reduce((s,a) => s + (metas[a.id] || 0), 0));
  const money    = v => v >= 1000 ? `$${(v/1000).toFixed(v >= 10000 ? 0 : 1)}k` : `$${v}`;
  const fmt      = n => n.toLocaleString('es-EC');

  // ── Pendientes ──
  const hotSinContacto = leads.filter(l => l.nivel === 'caliente' && !contacted(l));
  const sinContacto24  = clients.filter(c => !c.result && (Date.now() - new Date(c.last_contact || c.created_at)) / 3600000 > 24);
  const propuestas     = clients.filter(c => !c.result && (c.metadata?.producto || c.producto || c.metadata?.saleAmount != null));

  // ── Embudo ──
  const funnel = [
    { l:'Captados',    v:fLeads.length },
    { l:'Contactados', v:fLeads.filter(contacted).length },
    { l:'Propuesta',   v:clients.filter(c => inP(c.created_at) && (c.metadata?.producto || c.producto || c.metadata?.saleAmount != null)).length },
    { l:'Ganados',     v:ventas.length },
  ];
  const maxF = Math.max(funnel[0].v, 1);
  const drops = funnel.slice(1).map((s,i) => ({ i:i+1, pct: funnel[i].v ? Math.round((1 - s.v / funnel[i].v) * 100) : 0 }));
  const peor = drops.reduce((a,b) => b.pct > a.pct ? b : a, drops[0]);

  // ── Equipo ──
  const equipo = team.map(a => {
    const ml = fLeads.filter(l => l.advisor_id === a.id || l.asesorId === a.id);
    return { ...a, leads: ml.length, ventas: ventas.filter(c => c.assigned_advisor_id === a.id || c.asesorId === a.id).length,
      contacto: ml.length ? Math.round(ml.filter(contacted).length / ml.length * 100) : null };
  }).sort((a,b) => b.ventas - a.ventas || b.leads - a.leads).slice(0, 5);

  // ── Canales ──
  const keyOf = s => { const c = (s||'').toLowerCase();
    return c.includes('facebook')||c.includes('fb') ? 'facebook' : c.includes('instagram')||c.includes('ig') ? 'instagram'
         : c.includes('tiktok')||c.includes('tik') ? 'tiktok' : 'whatsapp'; };
  const CANAL = { facebook:{l:'Facebook',c:'#1877F2'}, whatsapp:{l:'WhatsApp',c:'#25D366'}, instagram:{l:'Instagram',c:'#E1306C'}, tiktok:{l:'TikTok',c:'#010101'} };
  const norm = t => (t||'').replace(/\D/g,'');
  const canalPorTel = {}; leads.forEach(l => { const k = norm(l.telefono); if (k) canalPorTel[k] = l.canal || l.origen; });
  const canales = Object.keys(CANAL).map(k => ({ k, n: fLeads.filter(l => keyOf(l.canal || l.origen) === k).length,
    v: ventas.filter(c => keyOf(canalPorTel[norm(c.phone)] || c.metadata?.source || 'whatsapp') === k).length }))
    .filter(c => c.n > 0 || c.v > 0).sort((a,b) => b.n - a.n || b.v - a.v); // ventas de un canal sin leads en el periodo también cuentan
  const totalC = fLeads.length || 1;
  const mejor = canales.filter(c => c.n).sort((a,b) => b.v/b.n - a.v/a.n)[0];

  const TIPO = { venta:['Venta','#16a34a','#f0fdf4'], cambio_estado:['Estado','#dc2626','#fef2f2'], whatsapp:['WhatsApp','#16a34a','#f0fdf4'],
    mensaje_wa:['WhatsApp','#16a34a','#f0fdf4'], llamada:['Llamada','#2563eb','#eff6ff'], nota:['Nota','#475569','#f1f5f9'], reasignacion:['Asignación','#475569','#f1f5f9'] };

  // ── Estilos base ──
  const card = { background:'var(--card-bg,#fff)', borderRadius:16, border:'1px solid #e5e7eb', padding:MOB?16:24 };
  const link = { background:'none', border:'none', color:p, fontWeight:700, fontSize:13, cursor:'pointer', padding:0, display:'flex', alignItems:'center', gap:4 };
  const h2 = { fontSize:16, fontWeight:700, color:NAVY };
  const sub = { fontSize:12, color:'#94a3b8', marginTop:3 };
  const Delta = ({ v }) => v == null ? null : (
    <span style={{ fontSize:12, fontWeight:700, padding:'2px 7px', borderRadius:6, color:v>=0?'#16a34a':'#dc2626', background:v>=0?'#f0fdf4':'#fef2f2' }}>{v>=0?'▲':'▼'} {Math.abs(v)}%</span>
  );
  const hora = now.getHours();
  const saludo = hora < 12 ? 'Buenos días' : hora < 19 ? 'Buenas tardes' : 'Buenas noches';
  const fecha = now.toLocaleDateString('es-EC', { weekday:'long', day:'numeric', month:'long' });

  return (
    <div style={{ fontFamily:"'DM Sans',sans-serif", color:NAVY }}>
      {/* Saludo + periodo */}
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-end', gap:12, flexWrap:'wrap', marginBottom:22 }}>
        <div>
          <h1 style={{ margin:0, fontSize:MOB?22:28, fontWeight:800, letterSpacing:'-0.5px' }}>{saludo}, {(user?.name || '').split(' ')[0]}</h1>
          <p style={{ margin:'4px 0 0', fontSize:14, color:'#64748b' }}>{fecha.charAt(0).toUpperCase() + fecha.slice(1)} · esto es lo que necesita tu atención hoy</p>
        </div>
        <div style={{ display:'flex', alignItems:'center', gap:8, flexWrap:'wrap' }}>
          <PeriodoPicker P={P}/>
        </div>
      </div>

      {/* Pendientes de hoy */}
      <div style={{ ...card, padding:8, display:'grid', gridTemplateColumns:MOB?'1fr':'220px 1fr 1fr 1fr', gap:8, marginBottom:16 }}>
        <div style={{ background:NAVY, borderRadius:10, padding:'16px 20px', color:'#fff', display:'flex', flexDirection:'column', justifyContent:'center' }}>
          <div style={{ fontSize:16, fontWeight:700 }}>Pendientes de hoy</div>
          <div style={{ fontSize:12, color:'rgba(255,255,255,0.7)', marginTop:3 }}>3 acciones por impacto</div>
        </div>
        {[
          { Icon:Flame,    c:'#ef4444', n:hotSinContacto.length, t:'leads calientes', s:'Sin contactar por el asesor', b:'Ver', go:'pipeline' },
          { Icon:Clock,    c:'#f59e0b', n:sinContacto24.length,  t:'clientes esperan +24 h', s:'Riesgo de perderlos', b:'Ver lista', go:'clients' },
          { Icon:FileText, c:'#3b82f6', n:propuestas.length,     t:'propuestas abiertas', s:'Enviar seguimiento', b:'Revisar', go:'pipeline' },
        ].map((x,i) => (
          <div key={i} style={{ display:'flex', alignItems:'center', gap:12, padding:'12px 14px', borderLeft:!MOB&&i?'1px solid #eef0f3':'none' }}>
            <div style={{ width:40, height:40, borderRadius:10, background:`${x.c}14`, display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}><x.Icon size={18} color={x.c}/></div>
            <div style={{ flex:1, minWidth:0 }}>
              <div style={{ fontSize:14, fontWeight:700 }}>{fmt(x.n)} {x.t}</div>
              <div style={{ fontSize:12, color:'#64748b' }}>{x.s}</div>
            </div>
            <button onClick={() => nav(x.go)} style={{ padding:'7px 12px', borderRadius:8, border:'1px solid #e5e7eb', background:'#fff', fontSize:12, fontWeight:700, cursor:'pointer', whiteSpace:'nowrap', color:NAVY }}>{x.b} →</button>
          </div>
        ))}
      </div>

      {/* KPIs */}
      <div style={{ display:'grid', gridTemplateColumns:MOB?'1fr 1fr':'repeat(4,1fr)', gap:16, marginBottom:16 }}>
        {[
          { l:'Leads captados', v:fmt(fLeads.length), d:delta(fLeads.length, pLeads.length), go:'pipeline' },
          { l:'Ventas cerradas', v:fmt(ventas.length), d:delta(ventas.length, pVentas.length), go:mine ? 'clients' : 'sales' },
        ].map((k,i) => (
          <div key={i} style={card}>
            <div style={{ display:'flex', justifyContent:'space-between', fontSize:14, color:'#475569' }}>{k.l}<button onClick={() => nav(k.go)} style={{ ...link, color:'#94a3b8', fontWeight:500 }}>Ver →</button></div>
            <div style={{ fontSize:MOB?28:36, fontWeight:800, margin:'10px 0 8px', letterSpacing:'-1px' }}>{k.v}</div>
            <div style={{ display:'flex', alignItems:'center', gap:8, fontSize:12, color:'#94a3b8' }}><Delta v={k.d}/>{k.d != null && `vs. ${prevLabel}`}</div>
          </div>
        ))}
        <div style={card}>
          <div style={{ display:'flex', justifyContent:'space-between', fontSize:14, color:'#475569' }}>Conversión<button onClick={() => nav('pipeline')} style={{ ...link, color:'#94a3b8', fontWeight:500 }}>Ver →</button></div>
          <div style={{ fontSize:MOB?28:36, fontWeight:800, margin:'10px 0 8px', letterSpacing:'-1px' }}>{conv.toFixed(2).replace('.', ',')}%</div>
          <div style={{ fontSize:12, color:'#94a3b8' }}>{fmt(ventas.length)} ventas de {fmt(fLeads.length)} leads</div>
        </div>
        <div style={{ ...card, background:NAVY, border:'none', color:'#fff' }}>
          <div style={{ fontSize:14, color:'rgba(255,255,255,0.8)' }}>Ingresos estimados</div>
          <div style={{ fontSize:MOB?28:36, fontWeight:800, margin:'10px 0 8px', color:p, letterSpacing:'-1px' }}>{money(ingresos)}</div>
          {meta > 0 ? (<>
            <div style={{ display:'flex', justifyContent:'space-between', fontSize:12, color:'rgba(255,255,255,0.7)', marginBottom:6 }}>
              <span>{Math.round(ventas.length / meta * 100)}% de la meta</span><span>{ventas.length}/{meta} ventas</span>
            </div>
            <div style={{ height:6, borderRadius:99, background:'rgba(255,255,255,0.12)' }}><div style={{ width:`${Math.min(ventas.length / meta * 100, 100)}%`, height:'100%', borderRadius:99, background:p }}/></div>
          </>) : <div style={{ fontSize:12, color:'rgba(255,255,255,0.6)' }}>Sin meta definida</div>}
        </div>
      </div>

      {/* Embudo + Equipo */}
      <div style={{ display:'grid', gridTemplateColumns:MOB||mine?'1fr':'1.45fr 1fr', gap:16, marginBottom:16 }}>
        <div style={card}>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start' }}>
            <div><div style={h2}>Dónde se pierden los leads</div><div style={sub}>Embudo del bot · el % rojo es cuántos se pierden entre etapas</div></div>
            <button onClick={() => nav('pipeline')} style={link}>Abrir pipeline →</button>
          </div>
          <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:MOB?6:12, alignItems:'end', height:MOB?160:240, marginTop:24, position:'relative' }}>
            {funnel.map((s,i) => {
              const h = s.v ? Math.min(Math.max(s.v / maxF * 100, 6), 100) : 2; // tope 100%: clientes llegan antes que leads (Captados=0) y las barras se salían de la tarjeta
              // ponytail: tonos del color de la empresa, de oscuro a claro como en el mockup; Ganados verde = éxito
              const shade = ['color-mix(in srgb, var(--primary) 70%, #000)','var(--primary)','color-mix(in srgb, var(--primary) 70%, #fff)','#16a34a'][i];
              return (
                <div key={i} style={{ height:`${h}%`, background:shade, borderRadius:8, position:'relative', display:'flex', justifyContent:'center', paddingTop:h > 12 ? 12 : 0 }}>
                  {h > 12 && <span style={{ color:'#fff', fontWeight:800, fontSize:MOB?14:20 }}>{fmt(s.v)}</span>}
                  {h <= 12 && <span style={{ position:'absolute', bottom:'100%', marginBottom:4, fontWeight:800, fontSize:14 }}>{fmt(s.v)}</span>}
                  {i > 0 && funnel[i-1].v > 0 && (
                    <span style={{ position:'absolute', left:MOB?-14:-22, top:-12, fontSize:11, fontWeight:700, padding:'3px 6px', borderRadius:6, zIndex:1,
                      background:drops[i-1].pct >= 50 ? '#e11d48' : '#fff', color:drops[i-1].pct >= 50 ? '#fff' : NAVY, border:'1px solid #e5e7eb' }}>−{drops[i-1].pct}%</span>
                  )}
                </div>
              );
            })}
          </div>
          <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:MOB?6:12, marginTop:10 }}>
            {funnel.map((s,i) => (
              <div key={i}><div style={{ fontSize:MOB?12:14, fontWeight:700 }}>{s.l}</div><div style={{ fontSize:12, color:'#94a3b8' }}>{Math.round(s.v / maxF * 100)}% del total</div></div>
            ))}
          </div>
          {fLeads.length > 0 && peor && peor.pct > 0 && (
            <div style={{ marginTop:18, background:'#fff1f2', borderRadius:10, padding:'12px 16px', display:'flex', alignItems:'center', gap:12, flexWrap:'wrap' }}>
              <AlertCircle size={18} color="#e11d48"/>
              <div style={{ flex:1, fontSize:13, color:'#9f1239', minWidth:180 }}>
                <b>El cuello de botella está en {funnel[peor.i].l.toLowerCase()}:</b> se pierde el {peor.pct}% entre {funnel[peor.i-1].l.toLowerCase()} y {funnel[peor.i].l.toLowerCase()}.
              </div>
              <button onClick={() => nav('pipeline')} style={{ padding:'7px 14px', borderRadius:8, border:'none', background:'#fff', color:'#e11d48', fontWeight:700, fontSize:13, cursor:'pointer' }}>Ver pipeline</button>
            </div>
          )}
        </div>

        {!mine && <div style={card}>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:16 }}>
            <div><div style={h2}>Equipo</div><div style={sub}>{team.length} asesores · ordenado por ventas</div></div>
            <button onClick={() => nav('rendimiento_equipo')} style={link}>Ver todo →</button>
          </div>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 56px 56px 80px', gap:8, fontSize:12, color:'#94a3b8', paddingBottom:10, borderBottom:'1px solid #eef0f3' }}>
            <span>Asesor</span><span>Leads</span><span>Ventas</span><span>Contactados</span>
          </div>
          {equipo.map((a,i) => (
            <div key={a.id} style={{ display:'grid', gridTemplateColumns:'1fr 56px 56px 80px', gap:8, alignItems:'center', padding:'12px 0', borderBottom:'1px solid #f3f4f6', fontSize:14 }}>
              <span style={{ display:'flex', alignItems:'center', gap:10, minWidth:0 }}>
                <span style={{ width:26, height:26, borderRadius:'50%', flexShrink:0, background:i < 3 ? `${p}18` : '#f1f5f9', color:i < 3 ? p : '#64748b', fontSize:12, fontWeight:700, display:'flex', alignItems:'center', justifyContent:'center' }}>{i+1}</span>
                <span style={{ fontWeight:600, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{a.name}</span>
              </span>
              <span>{fmt(a.leads)}</span>
              <span style={{ fontWeight:700, color:a.ventas ? NAVY : '#94a3b8' }}>{a.ventas}</span>
              <span>{a.contacto == null ? <span style={{ color:'#94a3b8' }}>—</span> : (
                <span style={{ fontSize:12, fontWeight:700, padding:'2px 8px', borderRadius:6,
                  color:a.contacto >= 80 ? '#16a34a' : a.contacto >= 50 ? '#d97706' : '#dc2626',
                  background:a.contacto >= 80 ? '#f0fdf4' : a.contacto >= 50 ? '#fffbeb' : '#fef2f2' }}>{a.contacto}%</span>)}</span>
            </div>
          ))}
          {equipo.length === 0 && <div style={{ padding:24, textAlign:'center', color:'#94a3b8', fontSize:13 }}>Sin asesores activos</div>}
        </div>}
      </div>

      {/* Canales + Actividad */}
      <div style={{ display:'grid', gridTemplateColumns:MOB?'1fr':'1fr 1.6fr', gap:16 }}>
        <div style={card}>
          <div style={h2}>Canales de captación</div>
          <div style={sub}>{fmt(fLeads.length)} leads en el periodo</div>
          <div style={{ marginTop:16, display:'flex', flexDirection:'column', gap:16 }}>
            {canales.map(c => {
              const pct = Math.round(c.n / totalC * 100);
              return (
                <div key={c.k} style={{ display:'flex', alignItems:'center', gap:12 }}>
                  <div style={{ flexShrink:0, transform:'scale(0.85)' }}>{canalLogo(c.k)}</div>
                  <div style={{ flex:1 }}>
                    <div style={{ display:'flex', justifyContent:'space-between', fontSize:14, marginBottom:6 }}>
                      <span style={{ fontWeight:600 }}>{CANAL[c.k].l}</span>
                      <span style={{ fontSize:12, color:'#94a3b8' }}>{pct}% · {c.v} ventas</span>
                    </div>
                    <div style={{ height:5, borderRadius:99, background:'#f1f5f9' }}><div style={{ width:`${pct}%`, height:'100%', borderRadius:99, background:CANAL[c.k].c }}/></div>
                  </div>
                  <div style={{ width:52, textAlign:'right', fontWeight:800, fontSize:16 }}>{fmt(c.n)}</div>
                </div>
              );
            })}
            {canales.length === 0 && <div style={{ color:'#94a3b8', fontSize:13 }}>Sin leads en el periodo</div>}
          </div>
          {mejor && mejor.v > 0 && <div style={{ marginTop:16, paddingTop:14, borderTop:'1px solid #eef0f3', fontSize:13, color:'#475569' }}>{CANAL[mejor.k].l} es el canal que mejor convierte ({(mejor.v / mejor.n * 100).toFixed(1)}%).</div>}
        </div>

        <div style={card}>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:12 }}>
            <div><div style={h2}>Actividad reciente</div><div style={sub}>{mine ? 'Tus últimos movimientos' : 'Últimos movimientos del equipo'}</div></div>
            <button onClick={() => nav(mine ? 'actions' : 'actividades_equipo')} style={link}>Ver toda →</button>
          </div>
          {recientes.map(a => {
            const [l,c,bg] = TIPO[a.type] || [a.type,'#475569','#f1f5f9'];
            return (
              <div key={a.id} style={{ display:'flex', alignItems:'center', gap:12, padding:'11px 0', borderTop:'1px solid #f3f4f6', fontSize:14 }}>
                <span style={{ width:44, flexShrink:0, color:'#94a3b8', fontSize:13 }}>{new Date(a.created_at).toLocaleTimeString('es-EC',{hour:'2-digit',minute:'2-digit',hour12:false})}</span>
                <span style={{ flexShrink:0, fontSize:12, fontWeight:700, padding:'2px 8px', borderRadius:6, color:c, background:bg }}>{l}</span>
                <span style={{ overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                  {a.advisor_name || 'Sistema'}{a.client_name && <> · <b>{a.client_name}</b></>}{(a.resultado || a.notas) && <span style={{ color:'#64748b' }}> · {a.resultado || a.notas}</span>}
                </span>
              </div>
            );
          })}
          {recientes.length === 0 && <div style={{ padding:20, textAlign:'center', color:'#94a3b8', fontSize:13 }}>Sin actividad registrada</div>}
        </div>
      </div>
    </div>
  );
}

function TabClientes({ clients: realClients, users, onRefresh }) {
  const p = DS.primary;
  const clients = (realClients && realClients.length > 0) ? realClients : [];
  const [search,   setSearch]   = useState('');
  const [fResult,  setFResult]  = useState('all');
  const [fAsesor,  setFAsesor]  = useState('all');
  const [fAnio,    setFAnio]    = useState('all');
  const [fMes,     setFMes]     = useState('all');
  const [fFecha,   setFFecha]   = useState('');
  const [detail,   setDetail]   = useState(null);
  const [mark,     setMark]     = useState(null); // {type:'won'|'lost', amount, concept, reason}
  const [saving,   setSaving]   = useState(false);

  // Misma ruta que usa el asesor: guarda en clients.metadata (result/saleAmount/lossReason),
  // así el panel del asesor, el supervisor y las métricas leen el mismo dato.
  async function guardarResultado(body) {
    setSaving(true);
    try {
      await apiFetch('PUT', `/api/asesor/clients/${detail.id}`, body);
      setDetail(null); setMark(null);
      if (onRefresh) onRefresh();
    } catch(e) { alert('Error: '+e.message); }
    setSaving(false);
  }

  const asesores = [...new Set(clients.map(c => c.asesor || c.advisor_name || 'Sin asignar').filter(Boolean))];
  const anios    = [...new Set(clients.map(c => (c.created_at||'').slice(0,4)).filter(Boolean))].sort().reverse();
  const MESES = ['Enero','Febrero','Marzo','Abril','Mayo','Junio','Julio','Agosto','Septiembre','Octubre','Noviembre','Diciembre'];

  const list = clients.filter(c => {
    const name  = c.name || '';
    const phone = c.phone || '';
    const result = c.result || c.metadata?.result || null;
    const asesor = c.asesor || c.advisor_name || '';
    const created = c.created_at || '';   // ISO: filtramos por substring, sin líos de zona horaria
    const ms = name.toLowerCase().includes(search.toLowerCase()) || phone.includes(search);
    const mr = fResult==='all' || (fResult==='proceso'&&!result) || (fResult==='won'&&result==='won') || (fResult==='lost'&&result==='lost');
    const ma = fAsesor==='all' || asesor===fAsesor;
    const my = fAnio==='all'  || created.slice(0,4)===fAnio;
    const mm = fMes==='all'   || created.slice(5,7)===fMes;
    const md = !fFecha        || created.slice(0,10)===fFecha;
    return ms&&mr&&ma&&my&&mm&&md;
  });

  const { paged, ...pg } = usePaged(list, 12);

  return (
    <div style={{ display:'grid', gridTemplateColumns:MOB?'1fr':'1fr 260px', gap:18 }}>
      <div>
        {/* Mini KPIs */}
        <div style={{ display:'grid', gridTemplateColumns:MOB?'repeat(2,1fr)':'repeat(4,1fr)', gap:16, marginBottom:16 }}>
          {[
            { l:'Total',      v:clients.length,  c:DS.primary },
            { l:'En proceso', v:clients.filter(c=>!(c.result||c.metadata?.result)).length, c:'#f59e0b' },
            { l:'Ganados',    v:clients.filter(c=>(c.result||c.metadata?.result)==='won').length,  c:'#10b981' },
            { l:'Perdidos',   v:clients.filter(c=>(c.result||c.metadata?.result)==='lost').length, c:'#ef4444' },
          ].map((s,i) => (
            <Kpi key={i} {...s} active={fResult===['all','proceso','won','lost'][i]} onClick={()=>setFResult(['all','proceso','won','lost'][i])}/>
          ))}
        </div>

        {/* Filtros */}
        <div style={{ display:'flex', gap:8, marginBottom:12, overflowX:'auto', flexWrap:'nowrap', WebkitOverflowScrolling:'touch', paddingBottom:4 }}>
          <div style={{ flex:1, position:'relative', minWidth:160 }}>
            <Search size={13} color="#94a3b8" style={{ position:'absolute', left:10, top:'50%', transform:'translateY(-50%)' }}/>
            <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="Buscar cliente o teléfono..."
              style={{ width:'100%', padding:'9px 12px 9px 30px', border:'1px solid #e5e7eb', borderRadius:10, background:'#fff', fontSize:13, outline:'none', boxSizing:'border-box' }}/>
          </div>
          <select value={fResult} onChange={e=>setFResult(e.target.value)} style={{ padding:'9px 12px', border:'1px solid #e5e7eb', borderRadius:10, background:'#fff', fontSize:13, outline:'none', cursor:'pointer' }}>
            <option value="all">Todos</option>
            <option value="proceso">En proceso</option>
            <option value="won">Ganados</option>
            <option value="lost">Perdidos</option>
          </select>
          <select value={fAsesor} onChange={e=>setFAsesor(e.target.value)} style={{ padding:'9px 12px', border:'1px solid #e5e7eb', borderRadius:10, background:'#fff', fontSize:13, outline:'none', cursor:'pointer' }}>
            <option value="all">Todos los asesores</option>
            {asesores.map(a=><option key={a} value={a}>{a}</option>)}
          </select>
          <select value={fAnio} onChange={e=>setFAnio(e.target.value)} style={{ padding:'9px 12px', border:'1px solid #e5e7eb', borderRadius:10, background:'#fff', fontSize:13, outline:'none', cursor:'pointer' }}>
            <option value="all">Todos los años</option>
            {anios.map(a=><option key={a} value={a}>{a}</option>)}
          </select>
          <select value={fMes} onChange={e=>setFMes(e.target.value)} style={{ padding:'9px 12px', border:'1px solid #e5e7eb', borderRadius:10, background:'#fff', fontSize:13, outline:'none', cursor:'pointer' }}>
            <option value="all">Todos los meses</option>
            {MESES.map((m,i)=><option key={m} value={String(i+1).padStart(2,'0')}>{m}</option>)}
          </select>
          <input type="date" value={fFecha} onChange={e=>setFFecha(e.target.value)} title="Filtrar por fecha exacta"
            style={{ padding:'8px 12px', border:'1px solid #e5e7eb', borderRadius:10, background:'#fff', fontSize:13, outline:'none', cursor:'pointer' }}/>
        </div>

        <div style={{ background:'var(--card-bg,#fff)', borderRadius:16, border:'1px solid #e5e7eb', overflowX:'auto' }}>
          <table style={{ width:'100%', borderCollapse:'collapse', minWidth:680 }}>
            <thead>
              <tr style={{ borderBottom:'1px solid #eef0f3' }}>
                {['Cliente','Teléfono','Asesor','Clasificación','Estado','Producto/Servicio','Score','Acciones'].map(h=>(
                  <th key={h} style={{ padding:'9px 12px', textAlign:'left', fontSize:12, fontWeight:600, color:'#94a3b8' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {paged.map((c,i) => {
                const result = c.result || c.metadata?.result || null;
                const heat   = c.heat || c.metadata?.heat || 'tibio';
                const asesor = c.asesor || c.advisor_name || 'Sin asignar';
                const heatCfg = {
                  caliente:{label:'Caliente',color:'#ef4444',bg:'#fff5f5'},
                  tibio:   {label:'Tibio',   color:'#f59e0b',bg:'#fffbeb'},
                  frio:    {label:'Frío',    color:'#4f6ef7',bg:'#f0f1ff'},
                }[heat] || {label:heat,color:'#94a3b8',bg:'#f1f5f9'};
                return (
                  <tr key={c.id||i} style={{ borderTop:'1px solid #e2e8f0', cursor:'pointer' }} onClick={()=>{setDetail(c);setMark(null);}}>
                    <td style={{ padding:'11px 12px' }}>
                      <div style={{ fontSize:13, fontWeight:700, color:'var(--dark,#0f172a)' }}>{c.name}</div>
                      <div style={{ fontSize:10, color:'#9aa5be' }}>{c.email||'Sin email'}</div>
                    </td>
                    <td style={{ padding:'11px 12px', fontSize:12, color:'#4a5568' }}>{c.phone}</td>
                    <td style={{ padding:'11px 12px', fontSize:12, fontWeight:600, color:p }}>{asesor.split(' ')[0]}</td>
                    <td style={{ padding:'11px 12px' }}><Tag label={heatCfg.label} color={heatCfg.color} bg={heatCfg.bg}/></td>
                    <td style={{ padding:'11px 12px' }}>
                      {result==='won'  && <Tag label="Ganado"  color="#065f46" bg="#d1fae5"/>}
                      {result==='lost' && <Tag label="Perdido" color="#7f1d1d" bg="#fee2e2"/>}
                      {!result         && <Tag label="Proceso" color="#92400e" bg="#fef3c7"/>}
                    </td>
                    <td style={{ padding:'11px 12px', maxWidth:140 }}>
                      {(() => { const p = c.metadata?.producto || c.producto; return p
                        ? <span style={{ fontSize:11, fontWeight:700, color:'#1e40af', background:'#dbeafe', padding:'2px 8px', borderRadius:5 }}>{p}</span>
                        : <span style={{ color:'#d1d5db' }}>—</span>; })()}
                    </td>
                    <td style={{ padding:'11px 12px' }}>
                      <span style={{ padding:'2px 9px', borderRadius:999, fontSize:11, fontWeight:700,
                        color:(c.score||0)>=80?'#065f46':(c.score||0)>=50?'#92400e':'#7f1d1d',
                        background:(c.score||0)>=80?'#d1fae5':(c.score||0)>=50?'#fef3c7':'#fee2e2'
                      }}>{c.score||0}</span>
                    </td>
                    <td style={{ padding:'11px 12px' }} onClick={e=>e.stopPropagation()}>
                      <button onClick={()=>askConfirm('Este cliente será eliminado permanentemente.',async()=>{try{await apiFetch('DELETE',`/api/clients/${c.id}`);if(onRefresh)onRefresh();}catch(e){alert('Error: '+e.message);}})}
                        style={{padding:'6px 10px',borderRadius:7,border:'none',background:'#fee2e2',color:'#ef4444',fontWeight:700,fontSize:11,cursor:'pointer',display:'flex',alignItems:'center',gap:4}}>
                        <Trash2 size={12}/> Eliminar
                      </button>
                    </td>
                  </tr>
                );
              })}
              {!list.length && <tr><td colSpan={8} style={{ padding:32, textAlign:'center', color:'#9aa5be' }}>Sin resultados</td></tr>}
            </tbody>
          </table>
        </div>
        <Pagination {...pg} primary={p} label="clientes" />
      </div>

      {/* Sidebar */}
      <div style={{ display:'flex', flexDirection:'column', gap:14 }}>
        <Card title="Por asesor" Icon={UserCheck}>
          {asesores.map(a => {
            const ac  = clients.filter(c=>(c.asesor||c.advisor_name||'Sin asignar')===a);
            const won = ac.filter(c=>(c.result||c.metadata?.result)==='won').length;
            const pct = ac.length ? Math.round((won/ac.length)*100) : 0;
            return (
              <div key={a} style={{ padding:'9px 0', borderBottom:'1px solid #e2e8f0', cursor:'pointer' }} onClick={()=>setFAsesor(fAsesor===a?'all':a)}>
                <div style={{ display:'flex', justifyContent:'space-between', marginBottom:4 }}>
                  <span style={{ fontSize:12, fontWeight:700, color:fAsesor===a?DS.primary:'var(--dark,#0f172a)' }}>{a.split(' ')[0]}</span>
                  <span style={{ fontSize:11, fontWeight:800, color:DS.primary }}>{pct}%</span>
                </div>
                <div style={{ background:'rgba(var(--primary-rgb),0.10)', borderRadius:999, height:6 }}>
                  <div style={{ width:`${pct}%`, height:'100%', background:DS.primary, borderRadius:999 }}/>
                </div>
                <div style={{ fontSize:10, color:'#9aa5be', marginTop:3 }}>{ac.length} clientes · {won} ganados</div>
              </div>
            );
          })}
        </Card>

        <Card title="Sin contacto +24h" Icon={AlertCircle} iconColor="#ef4444">
          {(() => {
            const DAY = 24*3600*1000;
            const sinContacto = clients
              .filter(c => { const t = c.last_contact || c.lastContact; return t && (Date.now() - new Date(t).getTime()) > DAY; })
              .sort((a,b) => new Date(a.last_contact||a.lastContact) - new Date(b.last_contact||b.lastContact))
              .slice(0, 12);
            if (!sinContacto.length) return <div style={{ padding:'12px 0', fontSize:12, color:'#9aa5be' }}>Todos los clientes con contacto reciente</div>;
            return sinContacto.map(c => {
              const asesor = (c.asesor || c.advisor_name || 'Sin asignar').split(' ')[0];
              const hrs = Math.floor((Date.now() - new Date(c.last_contact||c.lastContact).getTime())/3600000);
              const label = hrs >= 48 ? `${Math.floor(hrs/24)} días` : `${hrs}h`;
              return (
                <div key={c.id} style={{ padding:'8px 0', borderBottom:'1px solid #e2e8f0' }}>
                  <div style={{ fontSize:12, fontWeight:700, color:'var(--dark,#0f172a)' }}>{c.name}</div>
                  <div style={{ fontSize:10, color:'#ef4444' }}>{asesor} · {label}</div>
                </div>
              );
            });
          })()}
        </Card>
      </div>

      {detail && (
        <div style={{ position:'fixed', inset:0, backdropFilter:'blur(12px)', WebkitBackdropFilter:'blur(8px)', background:'rgba(var(--primary-rgb),0.3)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:9999 }}>
          <div style={{ background:'#ffffff', borderRadius:20, padding:28, width:'min(460px,92vw)', boxShadow:'0 20px 60px rgba(0,0,0,0.25)', maxHeight:'85vh', overflowY:'auto' }}>
            <div style={{ display:'flex', justifyContent:'space-between', marginBottom:16 }}>
              <h3 style={{ margin:0, fontSize:18, fontWeight:800 }}>{detail.name}</h3>
              <button onClick={()=>setDetail(null)} style={{ border:'none', background:'none', cursor:'pointer' }}><X size={18} color="#94a3b8"/></button>
            </div>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10, marginBottom:14 }}>
              {[
                {l:'ASESOR',    v:detail.asesor},
                {l:'TELÉFONO',  v:detail.phone},
                {l:'EMAIL',     v:detail.email||'—'},
                {l:'SCORE',     v:detail.score},
                {l:'ESTADO',    v:detail.result==='won'?'Ganado':detail.result==='lost'?'Perdido':'En proceso'},
                {l:'CONTACTO',  v:detail.updated},
              ].map((f,i)=>(
                <div key={i} style={{ background:'#f5f7fb', borderRadius:9, padding:'10px 12px' }}>
                  <div style={{ fontSize:10, color:'#9aa5be', fontWeight:700, marginBottom:3 }}>{f.l}</div>
                  <div style={{ fontSize:13, fontWeight:600, color:'#4a5568' }}>{f.v}</div>
                </div>
              ))}
            </div>
            {detail.result==='lost' && (
              <div style={{ background:'#fef2f2', border:'1px solid #fecaca', borderRadius:10, padding:'12px 14px', marginBottom:14 }}>
                <div style={{ fontSize:11, fontWeight:700, color:'#dc2626', marginBottom:5 }}>RAZÓN DE PÉRDIDA</div>
                <div style={{ fontSize:13, color:'#7f1d1d' }}>{detail.lossReason || <i style={{color:'#9aa5be'}}>No registrada por el asesor</i>}</div>
              </div>
            )}
            {detail.result==='won' && Number(detail.metadata?.saleAmount)>0 && (
              <div style={{ background:'#f0fdf4', border:'1px solid #bbf7d0', borderRadius:10, padding:'12px 14px', marginBottom:14 }}>
                <div style={{ fontSize:11, fontWeight:700, color:'#16a34a', marginBottom:5 }}>VALOR DE VENTA</div>
                <div style={{ fontSize:16, fontWeight:800, color:'#16a34a' }}>${Number(detail.metadata.saleAmount).toLocaleString()} {detail.metadata?.saleConcept ? `· ${detail.metadata.saleConcept}` : ''}</div>
              </div>
            )}

            {/* Marcar resultado a nombre del asesor dueño del cliente */}
            {!mark && (
              <div style={{ display:'flex', gap:8, marginBottom:4 }}>
                {detail.result!=='won'  && <button disabled={saving} onClick={()=>setMark({type:'won', amount:'', concept:'', reason:''})} style={{flex:1,padding:'10px',borderRadius:10,border:'none',background:'#d1fae5',color:'#065f46',fontWeight:700,fontSize:13,cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center',gap:6}}><CheckCircle size={14}/> Vendido</button>}
                {detail.result!=='lost' && <button disabled={saving} onClick={()=>setMark({type:'lost', amount:'', concept:'', reason:''})} style={{flex:1,padding:'10px',borderRadius:10,border:'none',background:'#fee2e2',color:'#7f1d1d',fontWeight:700,fontSize:13,cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center',gap:6}}><X size={14}/> Perdido</button>}
                {detail.result && <button disabled={saving} onClick={()=>guardarResultado({ result:null })} style={{flex:1,padding:'10px',borderRadius:10,border:'none',background:'#fef3c7',color:'#92400e',fontWeight:700,fontSize:13,cursor:'pointer'}}>Reabrir</button>}
              </div>
            )}
            {mark && (
              <div style={{ background:'#f5f7fb', borderRadius:10, padding:12, marginBottom:4, display:'flex', flexDirection:'column', gap:8 }}>
                <div style={{ fontSize:12, fontWeight:700, color:mark.type==='won'?'#065f46':'#7f1d1d' }}>
                  {mark.type==='won' ? 'Marcar como VENDIDO' : 'Marcar como PERDIDO'} · asesor {detail.asesor || 'sin asignar'}
                </div>
                {mark.type==='won' ? (<>
                  <input type="number" min="0" placeholder="Valor de la venta ($)" value={mark.amount} onChange={e=>setMark(m=>({...m,amount:e.target.value}))} style={{padding:'9px 10px',borderRadius:8,border:'1px solid #e2e8f0',fontSize:13}}/>
                  <input placeholder="Concepto (ej. moto, plan...)" value={mark.concept} onChange={e=>setMark(m=>({...m,concept:e.target.value}))} style={{padding:'9px 10px',borderRadius:8,border:'1px solid #e2e8f0',fontSize:13}}/>
                </>) : (
                  <input placeholder="Razón de la pérdida" value={mark.reason} onChange={e=>setMark(m=>({...m,reason:e.target.value}))} style={{padding:'9px 10px',borderRadius:8,border:'1px solid #e2e8f0',fontSize:13}}/>
                )}
                <div style={{ display:'flex', gap:8 }}>
                  <button disabled={saving} onClick={()=>setMark(null)} style={{flex:1,padding:'9px',borderRadius:8,border:'1px solid #e2e8f0',background:'#fff',fontWeight:700,fontSize:12,cursor:'pointer'}}>Cancelar</button>
                  <button disabled={saving} onClick={()=>guardarResultado(mark.type==='won'
                      ? { result:'won', heat:'caliente', saleAmount:Number(mark.amount)||0, saleConcept:mark.concept }
                      : { result:'lost', lossReason:mark.reason })}
                    style={{flex:1,padding:'9px',borderRadius:8,border:'none',background:mark.type==='won'?'#10b981':'#ef4444',color:'#fff',fontWeight:700,fontSize:12,cursor:'pointer'}}>
                    {saving ? 'Guardando…' : 'Guardar'}
                  </button>
                </div>
              </div>
            )}
            <button onClick={()=>{ askConfirm('Este cliente será eliminado permanentemente.', async ()=>{ try{ await apiFetch('DELETE',`/api/clients/${detail.id}`); if(onRefresh) onRefresh(); setDetail(null); }catch(e){ alert('Error al eliminar: '+e.message); } }); }}
              style={{width:'100%',padding:'10px',borderRadius:10,border:'none',background:'#fef2f2',color:'#ef4444',fontWeight:700,fontSize:13,cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center',gap:6,marginTop:8}}
              onMouseEnter={e=>{e.currentTarget.style.background='#ef4444';e.currentTarget.style.color='#fff';}} onMouseLeave={e=>{e.currentTarget.style.background='#fef2f2';e.currentTarget.style.color='#ef4444';}}>
              <Trash2 size={14}/> Eliminar cliente
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

/* ══════════════════════════ TAB ACTIVIDADES EQUIPO (Admin) ══════════════════════════ */
function TabActividadesEquipo() {
  const p = DS.primary;
  const TIPOS = {
    llamada:       { l:'Llamada',          c:DS.primary, Icon:Phone         },
    reunion:       { l:'Reunión',          c:'#8b5cf6', Icon:Users         },
    email:         { l:'Email',            c:'#f59e0b', Icon:MessageSquare },
    visita:        { l:'Visita',           c:'#10b981', Icon:Target        },
    nota:          { l:'Nota',             c:'#64748b', Icon:FileText      },
    mensaje_wa:    { l:'Mensaje WA',       c:'#25d366', Icon:MessageCircle },
    whatsapp:      { l:'WhatsApp',         c:'#25d366', Icon:MessageCircle },
    cambio_estado: { l:'Cambio de estado', c:'#ef4444', Icon:RefreshCw     },
    cita:          { l:'Cita',             c:'#8b5cf6', Icon:Calendar      },
    reasignacion:  { l:'Reasignación',     c:'#2563eb', Icon:UserCheck     },
    venta:         { l:'Venta',            c:'#10b981', Icon:CheckCircle   },
  };
  const [acts,    setActs]    = React.useState([]);
  const [roster,  setRoster]  = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [filtroA, setFiltroA] = React.useState('all');
  const [filtroT, setFiltroT] = React.useState('all');

  React.useEffect(() => {
    Promise.all([
      apiFetch('GET', '/api/supervisor/team-activities').then(r => Array.isArray(r) ? r : []).catch(() => []),
      apiFetch('GET', '/api/supervisor/asesores').then(r => Array.isArray(r) ? r : []).catch(() => []),
    ]).then(([a, ro]) => { setActs(a); setRoster(ro); setLoading(false); });
  }, []);

  // ponytail: roster union activities — el DELETE semanal vacía activities, así que la lista de asesores sale del roster (users), no de las actividades
  const asesores = [...new Set([...roster.map(u => u.name), ...acts.map(a => a.advisor_name)].filter(Boolean))];
  const filtered = acts.filter(a =>
    (filtroA === 'all' || a.advisor_name === filtroA) &&
    (filtroT === 'all' || a.type === filtroT)
  );

  const byAsesor = asesores.map(n => ({
    name:  n,
    total: acts.filter(a => a.advisor_name === n).length,
    hoy:   acts.filter(a => a.advisor_name === n && new Date(a.created_at).toDateString() === new Date().toDateString()).length,
    tipos: Object.fromEntries(Object.keys(TIPOS).map(k => [k, acts.filter(a => a.advisor_name === n && a.type === k).length])),
  }));

  return (
    <div style={{ fontFamily:"'DM Sans',sans-serif" }}>
      <PageHead title="Actividades del equipo" sub="Todo lo que registra y hace el equipo de asesores"/>

      {/* Cards por asesor */}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(220px,1fr))', gap:14, marginBottom:24 }}>
        {byAsesor.map((a, i) => (
          <div key={i} onClick={() => setFiltroA(filtroA === a.name ? 'all' : a.name)}
            style={{ background:'#fff', borderRadius:14, padding:'18px', border:`2px solid ${filtroA === a.name ? p : '#eaecf0'}`, cursor:'pointer', transition:'all .2s',
              boxShadow: filtroA === a.name ? `0 6px 20px ${p}20` : 'none' }}>
            <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:12 }}>
              <div style={{ width:38, height:38, borderRadius:'50%', background:`${p}15`, display:'flex', alignItems:'center', justifyContent:'center', fontSize:15, fontWeight:800, color:p }}>{a.name.charAt(0).toUpperCase()}</div>
              <div>
                <div style={{ fontSize:14, fontWeight:800, color:'var(--dark,#0f172a)' }}>{a.name.split(' ')[0]}</div>
                <div style={{ fontSize:11, color:'#94a3b8' }}>{a.hoy} hoy · {a.total} total</div>
              </div>
            </div>
            <div style={{ display:'flex', flexWrap:'wrap', gap:5 }}>
              {Object.entries(a.tipos).filter(([,v]) => v > 0).map(([k, v]) => {
                const tip = TIPOS[k];
                return (
                  <span key={k} style={{ fontSize:10, fontWeight:700, padding:'2px 7px', borderRadius:999, background:`${tip?.c}15`, color:tip?.c, display:'inline-flex', alignItems:'center', gap:3 }}>
                    {tip?.Icon && <tip.Icon size={9}/>} {tip?.l} {v}
                  </span>
                );
              })}
            </div>
          </div>
        ))}
        {byAsesor.length === 0 && !loading && (
          <div style={{ gridColumn:'1/-1', padding:32, textAlign:'center', color:'#94a3b8', fontSize:13 }}>Sin actividades registradas por el equipo aún</div>
        )}
      </div>

      {/* Filtros */}
      <div style={{ display:'flex', gap:8, marginBottom:16, flexWrap:'wrap', alignItems:'center' }}>
        <span style={{ fontSize:11, color:'#94a3b8', fontWeight:700, marginRight:4 }}>ASESOR:</span>
        {['all', ...asesores].map(n => (
          <button key={n} onClick={() => setFiltroA(n)}
            style={{ padding:'5px 12px', borderRadius:7, border:'none', fontSize:11, fontWeight:700, cursor:'pointer',
              background: filtroA === n ? p : '#f1f5f9', color: filtroA === n ? '#fff' : '#64748b' }}>
            {n === 'all' ? 'Todos' : n.split(' ')[0]}
          </button>
        ))}
        <div style={{ width:'100%', height:1 }}/>
        <span style={{ fontSize:11, color:'#94a3b8', fontWeight:700, marginRight:4 }}>TIPO:</span>
        {['all', ...Object.keys(TIPOS)].map(k => {
          const t = k === 'all' ? { l:'Todos', c:'#64748b' } : TIPOS[k];
          return (
            <button key={k} onClick={() => setFiltroT(k)}
              style={{ padding:'5px 12px', borderRadius:7, border:'none', fontSize:11, fontWeight:700, cursor:'pointer',
                background: filtroT === k ? t.c : '#f1f5f9', color: filtroT === k ? '#fff' : '#64748b' }}>
              {k === 'all' ? 'Todos los tipos' : t.l}
            </button>
          );
        })}
      </div>

      {/* Lista de actividades */}
      <div style={{ background:'var(--card-bg,#fff)', borderRadius:16, border:'1px solid #e5e7eb', overflow:'hidden' }}>
        <div style={{ padding:'12px 20px', borderBottom:'1px solid #f1f5f9', display:'flex', justifyContent:'space-between', alignItems:'center', background:'#f8fafc' }}>
          <span style={{ fontSize:13, fontWeight:700, color:'var(--dark,#0f172a)' }}>{filtered.length} actividades</span>
        </div>
        {loading && <div style={{ padding:32, textAlign:'center', color:'#94a3b8' }}>Cargando actividades...</div>}
        {!loading && filtered.length === 0 && <div style={{ padding:32, textAlign:'center', color:'#94a3b8' }}>Sin resultados con los filtros actuales</div>}
        {filtered.map((a, i) => {
          const t = TIPOS[a.type] || TIPOS.nota;
          return (
            <div key={a.id || i} style={{ display:'flex', gap:14, padding:'14px 20px', borderBottom:'1px solid #f8fafc', alignItems:'flex-start' }}>
              <div style={{ width:38, height:38, borderRadius:10, background:`${t.c}12`, display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                {t.Icon && <t.Icon size={16} color={t.c} strokeWidth={2}/>}
              </div>
              <div style={{ flex:1, minWidth:0 }}>
                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:3 }}>
                  <div>
                    <span style={{ fontSize:13, fontWeight:700, color:'var(--dark,#0f172a)' }}>{t.l}</span>
                    {a.client_name && <span style={{ fontSize:12, color:'#94a3b8' }}> · {a.client_name}</span>}
                  </div>
                  <div style={{ textAlign:'right', flexShrink:0, marginLeft:12 }}>
                    <div style={{ fontSize:11, fontWeight:700, color:p }}>{a.advisor_name}</div>
                    <div style={{ fontSize:11, color:'#94a3b8' }}>
                      {a.created_at ? new Date(a.created_at).toLocaleString('es-EC', { day:'numeric', month:'short', hour:'2-digit', minute:'2-digit' }) : ''}
                    </div>
                  </div>
                </div>
                {a.resultado && <div style={{ fontSize:12, color:t.c, fontWeight:600, marginBottom:2 }}>↳ {a.resultado}</div>}
                {a.notas && <div style={{ fontSize:12, color:'#64748b', lineHeight:1.5 }}>{a.notas}</div>}
                {a.duration > 0 && <div style={{ fontSize:11, color:'#94a3b8', marginTop:3 }}>⏱ {a.duration} min</div>}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ══════════════════════════ TAB VENTAS ══════════════════════════ */

function TabVentas({ clients: realClients, users }) {
  const p = DS.primary;
  const clients  = realClients || [];
  const asesores = users?.filter(u=>['advisor','asesor'].includes(u.role))||[];

  // ── State ──
  const [periodo,      setPeriodo]      = useState('mensual');
  const [mesSel,       setMes]          = useState(MESES[new Date().getMonth()]);
  const [showMetaModal,setShowMetaModal]= useState(false);
  const [editTarget,   setEditTarget]   = useState(null); // {key,name,meta}
  const [showGlobalEdit,setShowGlobalEdit]=useState(false);
  const [globalInput,  setGlobalInput]  = useState('');
  const [showNotif,    setShowNotif]    = useState(null);

  // Metas — persisten en la BD (tabla sales_goals), compartidas con Pipeline y con el supervisor.
  const { metas, metaGlobal: metaMensualGlobal, saveMeta: saveMetaApi, saveMetaGlobal } = useMetas();

  const saveMeta = (key,val)=>{
    saveMetaApi(key, val).catch(e=>alert('Error al guardar meta: '+e.message));
    setShowMetaModal(false);
    setShowNotif('✅ Meta actualizada correctamente');
    setTimeout(()=>setShowNotif(null),2500);
  };
  const saveGlobalMeta = (val)=>{
    saveMetaGlobal(val).catch(e=>alert('Error al guardar meta: '+e.message));
    setShowGlobalEdit(false);
    setShowNotif('✅ Meta global guardada');
    setTimeout(()=>setShowNotif(null),2500);
  };

  // ── Real data from clients ──
  const won  = clients.filter(c=>c.result==='won');
  const lost = clients.filter(c=>c.result==='lost');
  const totalVentas = won.length;
  const ingrEstimados = won.reduce((s,c)=>s+(Number(c.metadata?.saleAmount ?? c.saleAmount)||0),0);
  const totalMeta = metaMensualGlobal ||
    asesores.reduce((s,a)=>s+(metas[a.id||a.name]||10),0) ||
    asesores.length * 10 || 10;
  const pctMeta = Math.min(Math.round((totalVentas/totalMeta)*100),100);
  const fmtMXN = n=>'$'+Number(n).toLocaleString('es-MX');
  const mesNombre = ['Enero','Febrero','Marzo','Abril','Mayo','Junio','Julio','Agosto','Septiembre','Octubre','Noviembre','Diciembre'][new Date().getMonth()];

  // Per-asesor real data
  const asesorData = asesores.map(a=>{
    const key = a.id||a.name;
    const mc  = clients.filter(c=>c.asesor===a.name||c.advisor_id===a.id);
    const mw  = mc.filter(c=>c.result==='won');
    const ml  = mc.filter(c=>c.result==='lost');
    const metaA = metas[key]||10;
    const pctA  = Math.min(Math.round((mw.length/metaA)*100),100);
    return{...a,key,ganados:mw.length,perdidos:ml.length,proceso:mc.filter(c=>!c.result).length,
      clientes:mc.length,meta:metaA,pctMeta:pctA,
      ingresos:mw.reduce((s,c)=>s+(Number(c.metadata?.saleAmount ?? c.saleAmount)||0),0),
      tasa:mc.length?Math.round((mw.length/mc.length)*100):0};
  }).sort((a,b)=>b.ganados-a.ganados);

  const card={background:'var(--card-bg,#fff)', borderRadius:16, border:'1px solid #e5e7eb',
    boxShadow:'0 1px 3px rgba(0,0,0,0.05)',overflow:'hidden'};

  return(
    <div style={{fontFamily:"'DM Sans',sans-serif",color:NAVY}}>

      <PageHead title="Ventas" sub="Ventas cerradas, metas del mes y avance por asesor" right={<ReporteVentasBtn/>}/>

      {/* Toast */}
      {showNotif&&(
        <div style={{position:'fixed',top:20,right:24,zIndex:99999,
          background:'var(--dark,#0f172a)',color:'#fff',borderRadius:12,padding:'12px 20px',
          boxShadow:'0 8px 24px rgba(0,0,0,0.2)',fontSize:13,fontWeight:600,
          display:'flex',alignItems:'center',gap:8}}>
          {showNotif}
        </div>
      )}

      {/* ── MODAL META INDIVIDUAL ── */}
      {showMetaModal&&editTarget&&(
        <div style={{position:'fixed',inset:0,background:'rgba(15,23,42,0.5)',
          backdropFilter:'blur(4px)',display:'flex',alignItems:'center',
          justifyContent:'center',zIndex:9999}}
          onClick={e=>{if(e.target===e.currentTarget)setShowMetaModal(false);}}>
          <div style={{background:'#fff',borderRadius:22,padding:30,width:'min(420px,92vw)',
            boxShadow:'0 24px 60px rgba(0,0,0,0.2)',border:'1px solid #eaecf0'}}>
            <div style={{display:'flex',alignItems:'center',gap:12,marginBottom:20}}>
              <div style={{width:48,height:48,borderRadius:'50%',
                background:`${p}15`,display:'flex',alignItems:'center',
                justifyContent:'center',fontSize:18,fontWeight:800,color:p}}>
                {editTarget.name?.charAt(0)?.toUpperCase()}
              </div>
              <div>
                <div style={{fontSize:16,fontWeight:800,color:'var(--dark,#0f172a)'}}>Meta de {editTarget.name}</div>
                <div style={{fontSize:12,color:'#94a3b8'}}>Ventas objetivo — {mesNombre} {new Date().getFullYear()}</div>
              </div>
              <button onClick={()=>setShowMetaModal(false)} style={{marginLeft:'auto',
                border:'none',background:'#f1f5f9',borderRadius:10,padding:8,cursor:'pointer',display:'flex'}}
                onMouseEnter={e=>e.currentTarget.style.background='#e2e8f0'}
                onMouseLeave={e=>e.currentTarget.style.background='#f1f5f9'}>
                <X size={14} color="#64748b"/>
              </button>
            </div>
            {/* Progress actual */}
            <div style={{background:`${p}08`,borderRadius:14,padding:'14px 16px',
              marginBottom:16,border:`1px solid ${p}15`}}>
              <div style={{display:'flex',justifyContent:'space-between',marginBottom:8}}>
                <span style={{fontSize:12,fontWeight:700,color:'var(--dark,#0f172a)'}}>Progreso actual</span>
                <span style={{fontSize:13,fontWeight:800,color:p}}>{editTarget.ganados} / {editTarget.meta}</span>
              </div>
              <div style={{background:'rgba(255,255,255,0.6)',borderRadius:999,height:8,overflow:'hidden'}}>
                <div style={{width:`${editTarget.pctMeta}%`,height:'100%',
                  background:editTarget.pctMeta>=80?'#10b981':editTarget.pctMeta>=50?p:'#f59e0b',
                  borderRadius:999,transition:'width .6s ease'}}/>
              </div>
              <div style={{fontSize:10,color:'#94a3b8',marginTop:5}}>{editTarget.pctMeta}% completado</div>
            </div>
            {/* Input */}
            <label style={{fontSize:11,fontWeight:700,color:'#64748b',letterSpacing:0.5,
              textTransform:'uppercase',display:'block',marginBottom:8}}>
              Nueva meta de ventas
            </label>
            <input type="number" min="1" max="200" defaultValue={editTarget.meta}
              id={`meta-ventas-${editTarget.key}`}
              style={{width:'100%',padding:'14px 16px',border:'2px solid #e2e8f0',
                borderRadius:12,fontSize:28,fontWeight:800,textAlign:'center',
                color:'var(--dark,#0f172a)',outline:'none',boxSizing:'border-box',
                marginBottom:12,transition:'border .2s'}}
              onFocus={e=>e.target.style.borderColor=p}
              onBlur={e=>e.target.style.borderColor='#e2e8f0'}/>
            <div style={{display:'flex',gap:6,flexWrap:'wrap',marginBottom:16}}>
              {[5,8,10,12,15,20,25,30].map(n=>(
                <button key={n}
                  onClick={()=>{document.getElementById(`meta-ventas-${editTarget.key}`).value=n;}}
                  style={{padding:'5px 12px',borderRadius:8,border:`1.5px solid ${p}20`,
                    background:`${p}08`,color:p,fontSize:12,fontWeight:700,cursor:'pointer',transition:'all .15s'}}
                  onMouseEnter={e=>{e.currentTarget.style.background=p;e.currentTarget.style.color='#fff';}}
                  onMouseLeave={e=>{e.currentTarget.style.background=`${p}08`;e.currentTarget.style.color=p;}}>
                  {n}
                </button>
              ))}
            </div>
            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:10}}>
              <button onClick={()=>setShowMetaModal(false)}
                style={{padding:'12px',borderRadius:12,border:'1.5px solid #e2e8f0',
                  background:'#f8fafc',color:'#64748b',fontWeight:700,fontSize:13,
                  cursor:'pointer',transition:'all .15s'}}
                onMouseEnter={e=>e.currentTarget.style.background='#e2e8f0'}
                onMouseLeave={e=>e.currentTarget.style.background='#f8fafc'}>
                Cancelar
              </button>
              <button onClick={()=>saveMeta(editTarget.key, document.getElementById(`meta-ventas-${editTarget.key}`).value)}
                style={{padding:'12px',borderRadius:12,border:'none',
                  background:`linear-gradient(135deg,${p},${p}cc)`,
                  color:'#fff',fontWeight:800,fontSize:13,cursor:'pointer',
                  boxShadow:`0 4px 14px ${p}35`,transition:'all .2s'}}
                onMouseEnter={e=>e.currentTarget.style.transform='translateY(-1px)'}
                onMouseLeave={e=>e.currentTarget.style.transform=''}>
                Guardar meta
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div style={{display:'flex',alignItems:'flex-start',justifyContent:'space-between',marginBottom:22}}>
        <div>
          <div style={{fontSize:11,color:p,fontWeight:700,letterSpacing:1.5,
            textTransform:'uppercase',marginBottom:4}}>Administrador · Ventas</div>
          <h1 style={{margin:0,fontSize:24,fontWeight:800,color:'var(--dark,#0f172a)',letterSpacing:'-0.5px'}}>
            Reporte de Ventas
          </h1>
          <p style={{margin:'5px 0 0',fontSize:13,color:'#94a3b8'}}>
            {mesNombre} {new Date().getFullYear()} · {asesores.length} asesores
          </p>
        </div>
        <div style={{display:'flex',gap:8}}>
          <button onClick={()=>{setGlobalInput(String(totalMeta));setShowGlobalEdit(true);}}
            style={{padding:'9px 18px',borderRadius:11,border:`1.5px solid ${p}30`,
              background:`${p}08`,color:p,fontSize:12,fontWeight:700,cursor:'pointer',
              display:'flex',alignItems:'center',gap:6,transition:'all .2s'}}
            onMouseEnter={e=>{e.currentTarget.style.background=p;e.currentTarget.style.color='#fff';}}
            onMouseLeave={e=>{e.currentTarget.style.background=`${p}08`;e.currentTarget.style.color=p;}}>
            <Star size={13}/> Meta global
          </button>
        </div>
      </div>

      {/* ── META GLOBAL BANNER ── */}
      {showGlobalEdit&&(
        <div style={{background:NAVY,borderRadius:16,
          padding:'20px 24px',marginBottom:20,color:'#fff',
          display:'flex',alignItems:'center',gap:16,flexWrap:'wrap'}}>
          <div style={{flex:1}}>
            <div style={{fontSize:13,fontWeight:800,marginBottom:4}}>Meta global de ventas — {mesNombre}</div>
            <div style={{fontSize:11,color:'rgba(255,255,255,0.7)'}}>
              Esta meta se distribuye entre todos los asesores. También puedes asignar metas individuales abajo.
            </div>
          </div>
          <input type="number" value={globalInput} onChange={e=>setGlobalInput(e.target.value)}
            style={{width:100,padding:'10px',border:'2px solid rgba(255,255,255,0.4)',
              borderRadius:10,fontSize:22,fontWeight:800,textAlign:'center',
              background:'rgba(255,255,255,0.15)',color:'#fff',outline:'none',
              boxSizing:'border-box'}}
            onFocus={e=>e.target.style.borderColor='rgba(255,255,255,0.9)'}
            onBlur={e=>e.target.style.borderColor='rgba(255,255,255,0.4)'}/>
          <div style={{display:'flex',gap:8}}>
            <button onClick={()=>saveGlobalMeta(globalInput)}
              style={{padding:'10px 20px',borderRadius:10,border:'none',
                background:'rgba(255,255,255,0.25)',color:'#fff',fontWeight:800,
                fontSize:13,cursor:'pointer',transition:'all .15s'}}
              onMouseEnter={e=>e.currentTarget.style.background='rgba(255,255,255,0.35)'}
              onMouseLeave={e=>e.currentTarget.style.background='rgba(255,255,255,0.25)'}>
              Guardar
            </button>
            <button onClick={()=>setShowGlobalEdit(false)}
              style={{padding:'10px 16px',borderRadius:10,border:'1.5px solid rgba(255,255,255,0.3)',
                background:'transparent',color:'rgba(255,255,255,0.7)',fontWeight:700,
                fontSize:13,cursor:'pointer',transition:'all .15s'}}>
              Cancelar
            </button>
          </div>
        </div>
      )}

      {/* ── KPI CARDS con META PRINCIPAL ── */}
      <div style={{display:'grid',gridTemplateColumns:MOB?'repeat(2,1fr)':'1.2fr 1fr 1fr 1fr 1fr',gap:MOB?10:14,marginBottom:20}}>

        {/* META CARD — destacada */}
        <div style={{background:NAVY,borderRadius:16,
          padding:'20px',color:'#fff',position:'relative',overflow:'hidden',cursor:'pointer'}}
          onClick={()=>setShowGlobalEdit(!showGlobalEdit)}>
          <div style={{position:'relative'}}>
            <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',marginBottom:12}}>
              <div style={{width:40,height:40,borderRadius:11,background:'rgba(255,255,255,0.2)',
                display:'flex',alignItems:'center',justifyContent:'center'}}>
                <Target size={20} color="#fff"/>
              </div>
              <span style={{fontSize:10,color:'rgba(255,255,255,0.7)',background:'rgba(255,255,255,0.15)',
                borderRadius:999,padding:'3px 10px',fontWeight:700,cursor:'pointer'}}>Editar</span>
            </div>
            <div style={{fontSize:10,color:'rgba(255,255,255,0.75)',fontWeight:700,
              marginBottom:4,letterSpacing:0.5,textTransform:'uppercase'}}>Meta del mes</div>
            <div style={{fontSize:38,fontWeight:900,lineHeight:1,marginBottom:4,color:p}}>{totalMeta}</div>
            <div style={{fontSize:11,color:'rgba(255,255,255,0.7)',marginBottom:12}}>ventas objetivo</div>
            {/* Progress */}
            <div style={{background:'rgba(255,255,255,0.2)',borderRadius:999,height:8,overflow:'hidden',marginBottom:6}}>
              <div style={{width:`${pctMeta}%`,height:'100%',background:p,
                borderRadius:999,transition:'width .8s ease',
                boxShadow:'0 0 8px rgba(255,255,255,0.5)'}}/>
            </div>
            <div style={{display:'flex',justifyContent:'space-between',fontSize:10,color:'rgba(255,255,255,0.75)'}}>
              <span>{totalVentas} logradas</span><span>{pctMeta}% completo</span>
            </div>
          </div>
        </div>

        {/* Otros KPIs */}
        {[
          {Icon:Trophy,    l:'Ventas cerradas',  v:totalVentas,          c:'#10b981',sub:'este mes'},
          {Icon:XCircle,   l:'Perdidas',          v:lost.length,          c:'#ef4444',sub:'a recuperar'},
          {Icon:DollarSign,l:'Ingresos est.',     v:fmtMXN(ingrEstimados),c:'#f59e0b',sub:'generados',sm:true},
          {Icon:BarChart2, l:'Conversión',        v:`${clients.length?Math.round((totalVentas/clients.length)*100):0}%`,c:'#8b5cf6',sub:'tasa global'},
        ].map((k,i)=>(
          <div key={i} style={{background:'var(--card-bg,#fff)',borderRadius:16,padding:'18px',border:'1px solid #e5e7eb'}}>
            <div style={{display:'flex',justifyContent:'space-between',marginBottom:12}}>
              <div style={{width:38,height:38,borderRadius:10,background:`${k.c}12`,
                border:'1px solid #e5e7eb',display:'flex',alignItems:'center',justifyContent:'center'}}>
                <k.Icon size={17} color={k.c} strokeWidth={2}/>
              </div>
            </div>
            <div style={{fontSize:k.sm?18:28,fontWeight:800,color:'var(--dark,#0f172a)',lineHeight:1,marginBottom:3}}>{k.v}</div>
            <div style={{fontSize:11,fontWeight:700,color:'var(--dark,#0f172a)',marginBottom:2}}>{k.l}</div>
            <div style={{fontSize:10,color:'#94a3b8'}}>{k.sub}</div>
          </div>
        ))}
      </div>

      {/* ── TABLA ASESORES CON METAS INDIVIDUALES ── */}
      <div style={{...card,marginBottom:16}}>
        <div style={{padding:'16px 20px',borderBottom:'1px solid #eef0f3',display:'flex',justifyContent:'space-between',alignItems:'center'}}>
          <div style={{display:'flex',alignItems:'center',gap:8}}>
            <UserCheck size={14} color={p}/>
            <div>
              <div style={{fontSize:14,fontWeight:800,color:'var(--dark,#0f172a)'}}>Metas individuales por asesor</div>
              <div style={{fontSize:11,color:'#94a3b8'}}>Haz clic en "Meta" para editar · Los cambios se reflejan en Pipeline</div>
            </div>
          </div>
          <div style={{fontSize:11,color:'#94a3b8'}}>
            Total meta: <b style={{color:p}}>{totalMeta}</b> · Logrado: <b style={{color:'#10b981'}}>{totalVentas}</b>
          </div>
        </div>

        {/* Headers */}
        <div style={{display:'grid',gridTemplateColumns:MOB?'1fr 55px 55px 55px 55px 110px 70px':'1fr 80px 80px 80px 80px 160px 90px',
          gap:10,padding:'10px 20px',background:'#f8fafc',borderBottom:'1px solid #f1f5f9'}}>
          {['Asesor','Clientes','Ganados','Perdidos','Tasa','Progreso meta','Acción'].map((h,i)=>(
            <div key={i} style={{fontSize:9,fontWeight:700,color:'#94a3b8',
              letterSpacing:0.5,textAlign:i>0?'center':'left'}}>{h}</div>
          ))}
        </div>

        {asesorData.length===0&&(
          <div style={{padding:'32px',textAlign:'center',color:'#94a3b8',fontSize:13}}>
            Sin asesores registrados
          </div>
        )}
        {asesorData.map((a,i)=>(
          <div key={a.key||i}
            style={{display:'grid',gridTemplateColumns:MOB?'1fr 55px 55px 55px 55px 110px 70px':'1fr 80px 80px 80px 80px 160px 90px',
              gap:10,padding:'14px 20px',borderBottom:'1px solid #f8fafc',alignItems:'center',
              transition:'background .15s',cursor:'default'}}
            onMouseEnter={e=>e.currentTarget.style.background='#f8fafc'}
            onMouseLeave={e=>e.currentTarget.style.background='transparent'}>

            {/* Asesor */}
            <div style={{display:'flex',alignItems:'center',gap:10}}>
              <div style={{width:36,height:36,borderRadius:'50%',
                background:`linear-gradient(135deg,${p}22,${p}10)`,
                border:`2px solid ${p}25`,display:'flex',alignItems:'center',
                justifyContent:'center',fontSize:14,fontWeight:800,color:p,flexShrink:0}}>
                {a.name?.charAt(0)?.toUpperCase()}
              </div>
              <div>
                <div style={{fontSize:13,fontWeight:700,color:'var(--dark,#0f172a)'}}>{a.name}</div>
                <div style={{fontSize:10,color:'#94a3b8'}}>Meta: {a.meta} ventas · {fmtMXN(a.ingresos)} est.</div>
              </div>
            </div>

            <div style={{textAlign:'center',fontSize:13,fontWeight:700,color:'#64748b'}}>{a.clientes}</div>
            <div style={{textAlign:'center',fontSize:14,fontWeight:800,color:'#10b981'}}>{a.ganados}</div>
            <div style={{textAlign:'center',fontSize:14,fontWeight:700,color:'#ef4444'}}>{a.perdidos}</div>

            {/* Tasa */}
            <div style={{textAlign:'center'}}>
              <span style={{fontSize:12,fontWeight:800,padding:'3px 10px',borderRadius:999,
                color:a.tasa>=60?'#10b981':a.tasa>=30?'#f59e0b':'#ef4444',
                background:a.tasa>=60?'#f0fdf9':a.tasa>=30?'#fffbeb':'#fef2f2'}}>
                {a.tasa}%
              </span>
            </div>

            {/* Barra de progreso meta */}
            <div style={{padding:'0 4px'}}>
              <div style={{display:'flex',justifyContent:'space-between',marginBottom:5}}>
                <span style={{fontSize:10,color:'#94a3b8'}}>{a.ganados}/{a.meta}</span>
                <span style={{fontSize:10,fontWeight:800,
                  color:a.pctMeta>=80?'#10b981':a.pctMeta>=50?p:'#f59e0b'}}>{a.pctMeta}%</span>
              </div>
              <div style={{background:'#f1f5f9',borderRadius:999,height:8,overflow:'hidden'}}>
                <div style={{width:`${a.pctMeta}%`,height:'100%',
                  background:a.pctMeta>=80?'#10b981':a.pctMeta>=50?p:'#f59e0b',
                  borderRadius:999,transition:'width .6s ease',
                  boxShadow:a.pctMeta>=80?'0 0 6px #10b98155':''}}/>
              </div>
              {a.pctMeta>=100&&(
                <div style={{fontSize:9,color:'#10b981',fontWeight:700,marginTop:3,textAlign:'center'}}>
                  ¡Meta cumplida!
                </div>
              )}
            </div>

            {/* Botón editar meta */}
            <div style={{textAlign:'center'}}>
              <button
                onClick={()=>{setEditTarget(a);setShowMetaModal(true);}}
                style={{padding:'7px 14px',borderRadius:9,border:`1.5px solid ${p}30`,
                  background:`${p}08`,color:p,fontSize:11,fontWeight:700,cursor:'pointer',
                  display:'inline-flex',alignItems:'center',gap:5,transition:'all .15s'}}
                onMouseEnter={e=>{e.currentTarget.style.background=p;e.currentTarget.style.color='#fff';e.currentTarget.style.borderColor=p;}}
                onMouseLeave={e=>{e.currentTarget.style.background=`${p}08`;e.currentTarget.style.color=p;e.currentTarget.style.borderColor=`${p}30`;}}>
                <Star size={11}/> Meta
              </button>
            </div>
          </div>
        ))}

        {/* Footer totales */}
        {asesorData.length > 0 && (
          <div style={{display:'grid',gridTemplateColumns:MOB?'1fr 55px 55px 55px 55px 110px 70px':'1fr 80px 80px 80px 80px 160px 90px',
            gap:10,padding:'12px 20px',background:`${p}06`,borderTop:`2px solid ${p}20`,alignItems:'center'}}>
            <div style={{fontSize:12,fontWeight:800,color:p,display:'flex',alignItems:'center',gap:6}}>
              <BarChart2 size={13}/> Totales del equipo
            </div>
            <div style={{textAlign:'center',fontSize:13,fontWeight:800,color:'var(--dark,#0f172a)'}}>
              {asesorData.reduce((s,a)=>s+a.clientes,0)}
            </div>
            <div style={{textAlign:'center',fontSize:14,fontWeight:900,color:'#10b981'}}>
              {totalVentas}
            </div>
            <div style={{textAlign:'center',fontSize:14,fontWeight:800,color:'#ef4444'}}>
              {asesorData.reduce((s,a)=>s+a.perdidos,0)}
            </div>
            <div style={{textAlign:'center'}}>
              <span style={{fontSize:12,fontWeight:800,padding:'4px 12px',borderRadius:999,
                color:pctMeta>=60?'#10b981':pctMeta>=30?p:'#ef4444',
                background:pctMeta>=60?'#f0fdf9':pctMeta>=30?`${p}10`:'#fef2f2'}}>
                {clients.length?Math.round((totalVentas/clients.length)*100):0}%
              </span>
            </div>
            <div style={{padding:'0 4px'}}>
              <div style={{display:'flex',justifyContent:'space-between',marginBottom:4}}>
                <span style={{fontSize:10,color:p,fontWeight:700}}>{totalVentas}/{totalMeta}</span>
                <span style={{fontSize:10,fontWeight:800,color:p}}>{pctMeta}%</span>
              </div>
              <div style={{background:'rgba(255,255,255,0.5)',borderRadius:999,height:8,overflow:'hidden'}}>
                <div style={{width:`${pctMeta}%`,height:'100%',
                  background:`linear-gradient(90deg,${p},#10b981)`,borderRadius:999}}/>
              </div>
            </div>
            <div/>
          </div>
        )}
      </div>

      {/* ── RESUMEN VENTAS POR MES (datos reales del sistema) ── */}
      <div style={card}>
        <div style={{padding:'14px 20px',borderBottom:'1px solid #f1f5f9',
          borderLeft:'3px solid #10b981',display:'flex',alignItems:'center',gap:8}}>
          <TrendingUp size={14} color="#10b981"/>
          <div>
            <div style={{fontSize:14,fontWeight:800,color:'var(--dark,#0f172a)'}}>Resumen del período actual</div>
            <div style={{fontSize:11,color:'#94a3b8'}}>Datos reales del sistema</div>
          </div>
        </div>
        <div style={{padding:'20px',display:'grid',gridTemplateColumns:MOB?'repeat(2,1fr)':'repeat(4,1fr)',gap:MOB?10:16}}>
          {[
            {l:'Clientes totales',    v:clients.length,                     c:p,        Icon:Users},
            {l:'Ventas cerradas',     v:totalVentas,                        c:'#10b981', Icon:Trophy},
            {l:'Ingresos estimados',  v:fmtMXN(ingrEstimados),              c:'#f59e0b', Icon:DollarSign, sm:true},
            {l:'Faltan para meta',    v:Math.max(0,totalMeta-totalVentas),  c:'#ef4444', Icon:Target},
          ].map((s,i)=>(
            <div key={i} style={{textAlign:'center',padding:'18px 12px',background:'#f8fafc',
              borderRadius:14,border:`1px solid ${s.c}15`,transition:'all .2s',cursor:'default'}}
              onMouseEnter={e=>{e.currentTarget.style.background=`${s.c}06`;e.currentTarget.style.transform='translateY(-2px)';}}
              onMouseLeave={e=>{e.currentTarget.style.background='#f8fafc';e.currentTarget.style.transform='';}}>
              <div style={{width:40,height:40,borderRadius:11,background:`${s.c}12`,
                border:'1px solid #e5e7eb',display:'flex',alignItems:'center',justifyContent:'center',
                margin:'0 auto 10px'}}>
                <s.Icon size={18} color={s.c} strokeWidth={2}/>
              </div>
              <div style={{fontSize:s.sm?16:26,fontWeight:800,color:s.c,lineHeight:1,marginBottom:4}}>{s.v}</div>
              <div style={{fontSize:11,color:'#64748b',fontWeight:600}}>{s.l}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function TabMensajes({ users }) {
  const [msgs,     setMsgs]   = useState([]);
  const [activeTo, setTo]     = useState(null);
  const [texto,    setTexto]  = useState('');
  const [loading,  setLoading]= useState(true);
  const endRef = useRef(null);

  const supervisores = users.filter(u=>u.role==='supervisor');
  const asesores     = users.filter(u=>u.role==='advisor'||u.role==='asesor');
  const contactoActivo = users.find(u=>u.id===activeTo);

  useEffect(()=>{
    if(users.length>0 && !activeTo) setTo(users[0]?.id||null);
  },[users.length]);

  useEffect(()=>{
    setLoading(true);
    apiFetch('GET','/api/admin/messages')
      .then(r=>setMsgs(Array.isArray(r)?r:[]))
      .catch(()=>setMsgs([]))
      .finally(()=>setLoading(false));
  },[]);

  useEffect(()=>{ endRef.current?.scrollIntoView({behavior:'smooth'}); },[msgs,activeTo]);

  async function enviar() {
    if(!texto.trim()||!activeTo) return;
    try {
      const r = await apiFetch('POST','/api/admin/messages',{ toId:activeTo, texto:texto.trim() });
      setMsgs(p=>[...p,r]);
      setTexto('');
    } catch(e){ alert(e.message); }
  }

  const conv = msgs.filter(m=>m.from_id===activeTo||m.to_id===activeTo).sort((a,b)=>new Date(a.created_at)-new Date(b.created_at));

  return (
    <div style={{ display:MOB?'block':'grid', gridTemplateColumns:'250px 1fr', gap:0, background:'var(--card-bg,#fff)', borderRadius:16, border:'1px solid #e5e7eb', overflow:'hidden', minHeight:MOB?'auto':520 }}>
      {/* Contactos */}
      <div style={{ borderRight:MOB?'none':'1px solid rgba(var(--primary-rgb),0.08)', borderBottom:MOB?'1px solid rgba(var(--primary-rgb),0.08)':'none', overflowY:'auto', maxHeight:MOB?220:'none' }}>
        <div style={{ padding:'14px 16px 10px', borderBottom:'1px solid #e2e8f0', fontSize:16,fontWeight:700,color:NAVY }}>Conversaciones</div>

        {/* Supervisores */}
        {supervisores.length>0&&<div style={{ padding:'6px 16px', fontSize:12, fontWeight:600, color:'#94a3b8' }}>Supervisores</div>}
        {supervisores.map(u=>{
          const unread=msgs.filter(m=>m.from_id===u.id&&!m.leido).length;
          const last=[...msgs].filter(m=>m.from_id===u.id||m.to_id===u.id).sort((a,b)=>new Date(b.created_at)-new Date(a.created_at))[0];
          return(
            <div key={u.id} onClick={()=>setTo(u.id)} style={{ padding:'10px 16px', display:'flex', gap:9, alignItems:'center', cursor:'pointer', background:activeTo===u.id?'#f5f3ff':'transparent', borderBottom:'1px solid #e2e8f0' }}>
              <div style={{ position:'relative', flexShrink:0 }}>
                <Av ini={u.ini||(u.name||'?').charAt(0)} color="#8b5cf6" size={34}/>
                <div style={{ position:'absolute', bottom:0, right:0, width:9, height:9, borderRadius:'50%', background:u.active||u.status==='active'?'#10b981':'#94a3b8', border:'2px solid #fff' }}/>
              </div>
              <div style={{ flex:1, minWidth:0 }}>
                <div style={{ display:'flex', justifyContent:'space-between' }}>
                  <span style={{ fontSize:12, fontWeight:700, color:'var(--dark,#0f172a)' }}>{(u.name||'').split(' ')[0]}</span>
                  {last&&<span style={{ fontSize:9, color:'#9aa5be' }}>{new Date(last.created_at).toLocaleTimeString('es-MX',{hour:'2-digit',minute:'2-digit'})}</span>}
                </div>
                <div style={{ fontSize:10, color:'#9aa5be', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{last?.texto||'Sin mensajes'}</div>
              </div>
              {unread>0&&<div style={{ width:16, height:16, borderRadius:'50%', background:'#8b5cf6', display:'flex', alignItems:'center', justifyContent:'center', fontSize:9, fontWeight:800, color:'#fff' }}>{unread}</div>}
            </div>
          );
        })}

        {/* Asesores */}
        {asesores.length>0&&<div style={{ padding:'6px 16px', fontSize:12, fontWeight:600, color:'#94a3b8', marginTop:4 }}>Asesores</div>}
        {asesores.map(u=>{
          const unread=msgs.filter(m=>m.from_id===u.id&&!m.leido).length;
          const last=[...msgs].filter(m=>m.from_id===u.id||m.to_id===u.id).sort((a,b)=>new Date(b.created_at)-new Date(a.created_at))[0];
          return(
            <div key={u.id} onClick={()=>setTo(u.id)} style={{ padding:'10px 16px', display:'flex', gap:9, alignItems:'center', cursor:'pointer', background:activeTo===u.id?'rgba(var(--primary-rgb),0.18)':'transparent', borderBottom:'1px solid #e2e8f0' }}>
              <div style={{ position:'relative', flexShrink:0 }}>
                <Av ini={u.ini||(u.name||'?').charAt(0)} color={DS.primary} size={34}/>
                <div style={{ position:'absolute', bottom:0, right:0, width:9, height:9, borderRadius:'50%', background:u.active||u.status==='active'?'#10b981':'#94a3b8', border:'2px solid #fff' }}/>
              </div>
              <div style={{ flex:1, minWidth:0 }}>
                <div style={{ display:'flex', justifyContent:'space-between' }}>
                  <span style={{ fontSize:12, fontWeight:700, color:'var(--dark,#0f172a)' }}>{(u.name||'').split(' ')[0]}</span>
                  {last&&<span style={{ fontSize:9, color:'#9aa5be' }}>{new Date(last.created_at).toLocaleTimeString('es-MX',{hour:'2-digit',minute:'2-digit'})}</span>}
                </div>
                <div style={{ fontSize:10, color:'#9aa5be', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{last?.texto||'Sin mensajes'}</div>
              </div>
              {unread>0&&<div style={{ width:16, height:16, borderRadius:'50%', background:DS.primary, display:'flex', alignItems:'center', justifyContent:'center', fontSize:9, fontWeight:800, color:'#fff' }}>{unread}</div>}
            </div>
          );
        })}
        {supervisores.length===0&&asesores.length===0&&<div style={{ padding:20, textAlign:'center', color:'#9aa5be', fontSize:12 }}>Sin contactos</div>}
      </div>

      {/* Chat */}
      <div style={{ display:'flex', flexDirection:'column', height:'100%' }}>
        {contactoActivo?(
          <div style={{ padding:'12px 18px', borderBottom:'1px solid #e2e8f0', display:'flex', alignItems:'center', gap:10, background:'#f5f7fb' }}>
            <Av ini={contactoActivo.ini||(contactoActivo.name||'?').charAt(0)} color={contactoActivo.role==='supervisor'?'#8b5cf6':DS.primary} size={36}/>
            <div>
              <div style={{ fontSize:14, fontWeight:700, color:'var(--dark,#0f172a)' }}>{contactoActivo.name}</div>
              <div style={{ fontSize:11, color:contactoActivo.role==='supervisor'?'#8b5cf6':DS.primary, fontWeight:600 }}>
                {contactoActivo.role==='supervisor'?'Supervisor':'Asesor'}
              </div>
            </div>
          </div>
        ):(
          <div style={{ padding:'12px 18px', borderBottom:'1px solid #e2e8f0', background:'#f5f7fb', color:'#9aa5be', fontSize:12 }}>Selecciona un contacto</div>
        )}

        <div style={{ flex:1, overflowY:'auto', padding:'16px 18px', display:'flex', flexDirection:'column', gap:10, minHeight:300 }}>
          {loading&&<div style={{ color:'#9aa5be', fontSize:12, textAlign:'center', marginTop:40 }}>Cargando mensajes...</div>}
          {!loading&&conv.length===0&&<div style={{ color:'#9aa5be', fontSize:12, textAlign:'center', marginTop:40 }}>Sin mensajes todavía. ¡Inicia la conversación!</div>}
          {conv.map(m=>{
            const mine=m.from_role==='admin';
            return(
              <div key={m.id} style={{ display:'flex', justifyContent:mine?'flex-end':'flex-start' }}>
                <div style={{ maxWidth:'72%', padding:'10px 14px', borderRadius:mine?'14px 14px 4px 14px':'14px 14px 14px 4px', background:mine?DS.primary:'#f1f5f9', color:mine?'#fff':'#374151', fontSize:13, lineHeight:1.5 }}>
                  {!mine&&<div style={{ fontSize:10, fontWeight:700, color:DS.primary, marginBottom:2 }}>{m.from_name}</div>}
                  {m.texto}
                  <div style={{ fontSize:10, marginTop:4, opacity:0.65, textAlign:'right' }}>{new Date(m.created_at).toLocaleTimeString('es-MX',{hour:'2-digit',minute:'2-digit'})}</div>
                </div>
              </div>
            );
          })}
          <div ref={endRef}/>
        </div>

        <div style={{ padding:'10px 16px', borderTop:'1px solid #e2e8f0', display:'flex', gap:10, alignItems:'flex-end' }}>
          <textarea value={texto} onChange={e=>setTexto(e.target.value)}
            onKeyDown={e=>{if(e.key==='Enter'&&!e.shiftKey){e.preventDefault();enviar();}}}
            placeholder={contactoActivo?`Escribe a ${(contactoActivo.name||'').split(' ')[0]}... (Enter para enviar)`:'Selecciona un contacto'}
            rows={2} disabled={!activeTo}
            style={{ flex:1, padding:'9px 13px', border:'1px solid #cbd5e1', borderRadius:10, fontSize:13, resize:'none', outline:'none', background:!activeTo?'#f8fafc':'#fff' }}/>
          <button onClick={enviar} disabled={!texto.trim()||!activeTo}
            style={{ padding:'12px 16px', borderRadius:10, border:'none', background:!texto.trim()||!activeTo?'#f1f5f9':DS.primary, color:!texto.trim()||!activeTo?'#94a3b8':'#fff', cursor:'pointer', display:'flex', alignItems:'center', gap:5, fontWeight:700 }}>
            <Send size={14}/> Enviar
          </button>
        </div>
      </div>
    </div>
  );
}

/* ══════════════════════════ TAB USUARIOS (Crear/Editar/Eliminar) ══════════════════════════ */
function TabUsuarios({ users, setUsers, planInfo, onRefresh }) {
  const [modal,  setModal]  = useState(false);
  const [editId, setEditId] = useState(null);
  const [fRole,  setFRole]  = useState('all');
  const [form,   setForm]   = useState({ name:'', email:'', phone:'', role:'asesor', pass:'', active:true });
  const [verPass,setVerPass]= useState(false);
  const [saving, setSaving] = useState(false);
  const [errMsg, setErrMsg] = useState('');

  const PLAN_LABELS = { starter:'Starter', growth:'Growth', enterprise:'Enterprise' };
  const maxAdvisors    = planInfo?.max_advisors    ?? 12;
  const maxSupervisors = planInfo?.max_supervisors ?? 1;
  const plan           = planInfo?.plan            ?? 'starter';
  const curAdvisors    = users.filter(u=>u.role==='advisor'||u.role==='asesor').length;
  const curSupervisors = users.filter(u=>u.role==='supervisor').length;

  function abrirNuevo() {
    setForm({ name:'', email:'', phone:'', role:'asesor', pass:'', active:true });
    setEditId(null); setErrMsg(''); setModal(true);
  }
  function abrirEditar(u) {
    setForm({ name:u.name, email:u.email, phone:u.phone||'', role:u.role, pass:'', active:u.active });
    setEditId(u.id); setErrMsg(''); setModal(true);
  }
  async function guardar() {
    if(!form.name||!form.email){ setErrMsg('Nombre y email son obligatorios'); return; }
    if(!editId&&!form.pass){ setErrMsg('La contraseña es obligatoria'); return; }
    setSaving(true); setErrMsg('');
    try {
      const body = { name:form.name, email:form.email, phone:form.phone, role:form.role==='asesor'?'advisor':form.role, status:form.active?'active':'inactive' };
      if(form.pass) body.password = form.pass;
      if(editId) {
        const r = await apiFetch('PUT',`/api/admin/users/${editId}`, body);
        setUsers(p=>p.map(u=>u.id===editId?{...u,...r,active:r.status==='active',ini:r.name.split(' ').map(n=>n[0]).join('').slice(0,2).toUpperCase()}:u));
      } else {
        const r = await apiFetch('POST','/api/admin/users', body);
        setUsers(p=>[...p,{...r,active:r.status==='active',pass:'',ini:r.name.split(' ').map(n=>n[0]).join('').slice(0,2).toUpperCase(),lastLogin:'Nunca',created:r.created_at?.split('T')[0]||'—'}]);
      }
      setModal(false);
    } catch(e) { setErrMsg(e.message); }
    setSaving(false);
  }
  async function toggleActivo(id, currentActive) {
    const aplicar = async () => {
      try {
        await apiFetch('PUT',`/api/admin/users/${id}`,{status:currentActive?'inactive':'active'});
        setUsers(p=>p.map(u=>u.id===id?{...u,active:!currentActive,status:currentActive?'inactive':'active'}:u));
      } catch(e) { alert(e.message); }
    };
    // Desactivar cierra el acceso y saca al asesor del reparto del bot: se confirma.
    if (currentActive) askConfirm('Al desactivar, el asesor no podrá iniciar sesión y el bot dejará de asignarle clientes nuevos. Sus clientes actuales no se tocan.', aplicar);
    else aplicar();
  }
  function eliminar(id) {
    askConfirm('Este usuario será eliminado y ya no podrá acceder al sistema.', async () => {
      try {
        await apiFetch('DELETE',`/api/admin/users/${id}`);
        setUsers(p=>p.filter(u=>u.id!==id));
      } catch(e) { alert(e.message); }
    });
  }

  const lista = users.filter(u=>fRole==='all'||(fRole==='asesor'?(u.role==='asesor'||u.role==='advisor'):u.role===fRole));

  return (
    <div style={{ display:'grid', gridTemplateColumns:MOB?'1fr':'1fr 250px', gap:18 }}>
      <div>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:14 }}>
          <div style={{ display:'flex', gap:8 }}>
            {[{k:'all',l:'Todos'},{k:'supervisor',l:'Supervisores'},{k:'asesor',l:'Asesores'}].map(f=>(
              <button key={f.k} onClick={()=>setFRole(f.k)} style={{ padding:'7px 14px', borderRadius:8, border:'none', fontSize:12, fontWeight:700, cursor:'pointer', background:fRole===f.k?'var(--dark,#0f172a)':'#fff', color:fRole===f.k?'#fff':'#475569', boxShadow:'inset 0 0 0 1px #e5e7eb' }}>
                {f.l} ({f.k==='all'?users.length:users.filter(u=>f.k==='asesor'?(u.role==='asesor'||u.role==='advisor'):u.role===f.k).length})
              </button>
            ))}
          </div>
          <div style={{ fontSize:12, color:'#64748b', background:'#f8fafc', border:'1px solid #e2e8f0', borderRadius:10, padding:'8px 14px', display:'flex', alignItems:'center', gap:6 }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
            Solo el Super Admin puede crear usuarios
          </div>
        </div>

        <div style={{ background:'var(--card-bg,#fff)', borderRadius:16, border:'1px solid #e5e7eb', overflowX:'auto' }}>
          <table style={{ width:'100%', borderCollapse:'collapse', minWidth:640 }}>
            <thead>
              <tr style={{ borderBottom:'1px solid #eef0f3' }}>
                {['Usuario','Email','Teléfono','Rol','Acceso','Creado','Acciones'].map(h=>(
                  <th key={h} style={{ padding:'9px 12px', textAlign:'left', fontSize:12, fontWeight:600, color:'#94a3b8' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {lista.map(u=>(
                <tr key={u.id} style={{ borderTop:'1px solid #e2e8f0' }}>
                  <td style={{ padding:'11px 12px' }}>
                    <div style={{ display:'flex', gap:9, alignItems:'center' }}>
                      <Av ini={u.ini} color={u.role==='supervisor'?'#8b5cf6':DS.primary} size={32}/>
                      <div>
                        <div style={{ fontSize:13, fontWeight:700, color:'var(--dark,#0f172a)' }}>{u.name}</div>
                        <div style={{ fontSize:10, color:'#9aa5be' }}>{u.lastLogin}</div>
                      </div>
                    </div>
                  </td>
                  <td style={{ padding:'11px 12px', fontSize:12, color:'#4a5568' }}>{u.email}</td>
                  <td style={{ padding:'11px 12px', fontSize:12, color:'#4a5568' }}>{u.phone}</td>
                  <td style={{ padding:'11px 12px' }}>
                    <Tag label={u.role==='supervisor'?'Supervisor':'Asesor'} color={u.role==='supervisor'?'#6d28d9':'#4338ca'} bg={u.role==='supervisor'?'#f5f3ff':'#eef2ff'}/>
                  </td>
                  <td style={{ padding:'11px 12px' }}>
                    <button onClick={()=>toggleActivo(u.id, u.active)} style={{ display:'flex', alignItems:'center', gap:5, padding:'5px 10px', borderRadius:999, border:'none', background:u.active?'#d1fae5':'#f1f5f9', color:u.active?'#065f46':'#64748b', fontSize:11, fontWeight:700, cursor:'pointer' }}>
                      <div style={{ width:7, height:7, borderRadius:'50%', background:u.active?'#10b981':'#94a3b8' }}/>
                      {u.active?'Activo':'Inactivo'}
                    </button>
                  </td>
                  <td style={{ padding:'11px 12px', fontSize:12, color:'#9aa5be' }}>{u.created}</td>
                  <td style={{ padding:'11px 12px' }}>
                    <div style={{ display:'flex', gap:5 }}>
                      <button onClick={()=>abrirEditar(u)} style={{ padding:'6px 9px', borderRadius:7, border:'none', background:'rgba(var(--primary-rgb),0.10)', cursor:'pointer', display:'flex' }}><Edit2 size={13} color={DS.primary}/></button>
                      <button onClick={()=>eliminar(u.id)} style={{ padding:'6px 9px', borderRadius:7, border:'none', background:'#fef2f2', cursor:'pointer', display:'flex' }}><Trash2 size={13} color="#ef4444"/></button>
                    </div>
                  </td>
                </tr>
              ))}
              {!lista.length&&<tr><td colSpan={7} style={{ padding:32, textAlign:'center', color:'#9aa5be' }}>Sin usuarios</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      {/* Sidebar */}
      <div style={{ display:'flex', flexDirection:'column', gap:14 }}>
        <Card title="Resumen y límites" Icon={Users}>
          {[
            {l:'Total usuarios',   v:users.length,        c:DS.primary, Ic:Users},
            {l:'Supervisores',     v:curSupervisors,      c:'#8b5cf6', Ic:Shield},
            {l:'Asesores',         v:curAdvisors,         c:DS.primary, Ic:UserCheck},
            {l:'Activos',          v:users.filter(u=>u.active).length, c:'#10b981', Ic:Activity},
          ].map((s,i)=>(
            <div key={i} style={{ display:'flex', gap:10, alignItems:'center', padding:'8px 0', borderBottom:'1px solid #e2e8f0' }}>
              <div style={{ width:28, height:28, borderRadius:7, background:`${s.c}18`, display:'flex', alignItems:'center', justifyContent:'center' }}><s.Ic size={12} color={s.c}/></div>
              <div style={{ flex:1, fontSize:12, fontWeight:600, color:'#4a5568' }}>{s.l}</div>
              <div style={{ fontSize:16, fontWeight:800, color:s.c }}>{s.v}</div>
            </div>
          ))}
          <div style={{ marginTop:10, background:'#f5f7fb', borderRadius:9, padding:'10px 12px' }}>
            <div style={{ fontSize:10, fontWeight:800, color:'#4338ca', marginBottom:7 }}>LÍMITES DEL PLAN {(PLAN_LABELS[plan]||plan).toUpperCase()}</div>
            {[
              {l:'Asesores',     v:curAdvisors,     max:maxAdvisors,    c:DS.primary},
              {l:'Supervisores', v:curSupervisors,  max:maxSupervisors, c:'#8b5cf6'},
            ].map((s,i)=>{
              const pct=s.max>=9999?20:Math.min((s.v/s.max)*100,100);
              const over=s.max<9999&&s.v>=s.max;
              return(
                <div key={i} style={{ marginBottom:8 }}>
                  <div style={{ display:'flex', justifyContent:'space-between', fontSize:11, marginBottom:3 }}>
                    <span style={{ fontWeight:600, color:'#4a5568' }}>{s.l}</span>
                    <span style={{ fontWeight:800, color:over?'#ef4444':s.c }}>{s.v}/{s.max>=9999?'∞':s.max}</span>
                  </div>
                  <div style={{ background:'rgba(var(--primary-rgb),0.10)', borderRadius:999, height:5 }}>
                    <div style={{ width:`${pct}%`, height:'100%', background:over?'#ef4444':s.c, borderRadius:999 }}/>
                  </div>
                  {over&&<div style={{ fontSize:9, color:'#ef4444', marginTop:2 }}>️ Límite alcanzado — mejora tu plan</div>}
                </div>
              );
            })}
          </div>
        </Card>
        <button onClick={abrirNuevo} style={{ padding:'12px 0', borderRadius:12, border:'none', background:DS.primary, color:'#fff', fontWeight:700, fontSize:14, cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', gap:8 }}>
          <UserPlus size={16}/> Crear nuevo usuario
        </button>
      </div>

      {/* Modal */}
      {modal && (
        <div style={{ position:'fixed', inset:0, backdropFilter:'blur(12px)', WebkitBackdropFilter:'blur(8px)', background:'rgba(var(--primary-rgb),0.35)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:9999 }}>
          <div style={{ background:'#ffffff', borderRadius:22, padding:30, width:'min(440px,92vw)', boxShadow:'0 24px 60px rgba(0,0,0,0.3)', maxHeight:'90vh', overflowY:'auto' }}>
            <div style={{ display:'flex', justifyContent:'space-between', marginBottom:20 }}>
              <h3 style={{ margin:0, fontSize:18, fontWeight:800, color:'var(--dark,#0f172a)' }}>{editId?'Editar usuario':'Nuevo usuario'}</h3>
              <button onClick={()=>setModal(false)} style={{ border:'none', background:'none', cursor:'pointer' }}><X size={18} color="#94a3b8"/></button>
            </div>

            {/* Rol */}
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:8, marginBottom:16 }}>
              {[{k:'asesor',l:'Asesor',Ic:UserCheck},{k:'supervisor',l:'Supervisor',Ic:Shield}].map(r=>(
                <button key={r.k} onClick={()=>setForm(p=>({...p,role:r.k}))} style={{ padding:'10px 0', borderRadius:10, border:form.role===r.k?'2px solid #6366f1':'2px solid #e2e8f0', background:form.role===r.k?'#eef2ff':'#fafafa', color:form.role===r.k?DS.primary:'#64748b', fontWeight:700, fontSize:13, cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', gap:6 }}>
                  <r.Ic size={14}/> {r.l}
                </button>
              ))}
            </div>

            <div style={{ display:'grid', gap:10, marginBottom:16 }}>
              {[
                {p:'Nombre completo *', k:'name',  t:'text'},
                {p:'Correo electrónico *', k:'email', t:'email'},
                {p:'Teléfono',          k:'phone', t:'tel'},
              ].map(f=>(
                <input key={f.k} type={f.t} value={form[f.k]} onChange={e=>setForm(p=>({...p,[f.k]:e.target.value}))} placeholder={f.p}
                  style={{ padding:'10px 13px', border:'1px solid #e5e7eb', borderRadius:10, background:'#fff', fontSize:13, outline:'none' }}/>
              ))}
              <div style={{ position:'relative' }}>
                <input type={verPass?'text':'password'} value={form.pass} onChange={e=>setForm(p=>({...p,pass:e.target.value}))} placeholder={editId?'Nueva contraseña (vacío = sin cambio)':'Contraseña de acceso *'}
                  style={{ width:'100%', padding:'10px 40px 10px 13px', border:'1px solid #e5e7eb', borderRadius:10, background:'#fff', fontSize:13, outline:'none', boxSizing:'border-box' }}/>
                <button onClick={()=>setVerPass(v=>!v)} style={{ position:'absolute', right:11, top:'50%', transform:'translateY(-50%)', border:'none', background:'none', cursor:'pointer', color:'#9aa5be', display:'flex' }}>
                  <Eye size={16}/>
                </button>
              </div>
            </div>

            {/* Toggle activo */}
            <div style={{ display:'flex', alignItems:'center', gap:10, padding:'10px 14px', background:'#f5f7fb', borderRadius:10, marginBottom:16, cursor:'pointer' }} onClick={()=>setForm(p=>({...p,active:!p.active}))}>
              <div style={{ width:42, height:23, borderRadius:999, background:form.active?DS.primary:'#d1d5db', position:'relative', transition:'background .2s', flexShrink:0 }}>
                <div style={{ position:'absolute', top:3, left:form.active?20:3, width:17, height:17, borderRadius:'50%', background:'#ffffff', transition:'left .2s' }}/>
              </div>
              <span style={{ fontSize:13, fontWeight:600, color:form.active?DS.primary:'#94a3b8' }}>{form.active?'Activo — puede acceder al sistema':'Inactivo — sin acceso al sistema'}</span>
            </div>

            <div style={{ display:'flex', gap:10 }}>
              {errMsg && <div style={{ gridColumn:'1/-1', background:'#fef2f2', border:'1px solid #fecaca', borderRadius:8, padding:'9px 12px', color:'#dc2626', fontSize:12, marginBottom:4 }}>{errMsg}</div>}
              <button onClick={()=>setModal(false)} style={{ flex:1, padding:'11px 0', borderRadius:10, border:'1px solid #cbd5e1', background:'#f5f7fb', color:'#9aa5be', fontSize:13, fontWeight:700, cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', gap:5 }}><X size={14}/> Cancelar</button>
              <button onClick={guardar} disabled={saving} style={{ flex:2, padding:'11px 0', borderRadius:10, border:'none', background:saving?'#a5b4fc':DS.primary, color:'#fff', fontWeight:700, fontSize:13, cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', gap:6 }}><Save size={14}/> {saving?'Guardando...':editId?'Guardar cambios':'Crear usuario'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ══════════════════════════ TAB EMPLEADOS ══════════════════════════ */
const DEPT_COLORS = { Ventas:'#4f6ef7', Supervisión:'#8b5cf6', Administración:'#0ea5e9', Marketing:'#f59e0b', Operaciones:'#10b981' };
const DEPTS = Object.keys(DEPT_COLORS);

function TabEmpleados({ employees:initEmps, setEmployees, onRefresh }) {
  const [emps, setEmps] = useState(Array.isArray(initEmps) ? initEmps : []);
  const [saving, setSaving] = useState(false);
  const [errMsg, setErrMsg] = useState('');
  
  useEffect(() => {
    if (initEmps && initEmps.length > 0) setEmps(initEmps);
  }, [initEmps]);
  
  // Override guardar to use API
  const guardarAPI = async (form, editId, onSuccess) => {
    setSaving(true); setErrMsg('');
    try {
      const body = { name:form.name, email:form.email, phone:form.phone, puesto:form.puesto||form.cargo, department:form.dept||form.department, salary:form.salario||form.salary, hire_date:form.ingreso||form.hire_date };
      let r;
      if (editId) { r = await apiFetch('PUT',`/api/admin/employees/${editId}`, body); }
      else        { r = await apiFetch('POST','/api/admin/employees', body); }
      if (onRefresh) onRefresh();
      onSuccess(r);
    } catch(e) { setErrMsg(e.message); }
    setSaving(false);
  };
  const eliminarAPI = (id, onSuccess) => {
    askConfirm('Este empleado será eliminado permanentemente.', async () => {
      try { await apiFetch('DELETE',`/api/admin/employees/${id}`); onSuccess(id); }
      catch(e) { alert(e.message); }
    });
  };

  const [modal,   setModal]  = useState(false);
  const [editId,  setEditId] = useState(null);
  const [detail,  setDetail] = useState(null);
  const [search,  setSearch] = useState('');
  const [fStatus, setFS]     = useState('all');
  const [form,    setForm]   = useState({ name:'', puesto:'', dept:'Ventas', phone:'', email:'', ingreso:'', salario:'', status:'activo' });

  function abrirNuevo() {
    setForm({ name:'', puesto:'', dept:'Ventas', phone:'', email:'', ingreso:new Date().toISOString().split('T')[0], salario:'', status:'activo' });
    setEditId(null); setModal(true);
  }
  function abrirEditar(e, ev) { ev.stopPropagation(); setForm({ name:e.name, puesto:e.puesto, dept:e.dept, phone:e.phone, email:e.email, ingreso:e.ingreso, salario:e.salario, status:e.status }); setEditId(e.id); setModal(true); }
  function guardar() {
    if(!form.name||!form.puesto){ alert('Nombre y puesto son obligatorios'); return; }
    if(editId) {
      setEmps(p=>p.map(e=>e.id===editId?{...e,...form}:e));
    } else {
      const ini=form.name.split(' ').map(n=>n[0]).join('').slice(0,2).toUpperCase();
      setEmps(p=>[...p,{ id:'e'+Date.now(), ...form, ini }]);
    }
    setModal(false);
  }
  function eliminar(id, ev) { ev.stopPropagation(); askConfirm('Este empleado será eliminado permanentemente.', ()=>setEmps(p=>p.filter(e=>e.id!==id))); }

  const lista = emps.filter(e => {
    const ms=e.name.toLowerCase().includes(search.toLowerCase())||e.puesto.toLowerCase().includes(search.toLowerCase());
    const mf=fStatus==='all'||e.status===fStatus;
    return ms&&mf;
  });

  return (
    <div style={{ display:'grid', gridTemplateColumns:MOB?'1fr':'1fr 255px', gap:18 }}>
      <div>
        <ExportButtons roles={['leads','clients','ventas','equipo']}/>
      {/* KPIs */}
        <div style={{ display:'grid', gridTemplateColumns:MOB?'repeat(2,1fr)':'repeat(4,1fr)', gap:16, marginBottom:16 }}>
          {[
            {l:'Total empleados', v:emps.length,                        c:DS.primary},
            {l:'Activos',         v:emps.filter(e=>e.status==='activo').length, c:'#10b981'},
            {l:'Inactivos',       v:emps.filter(e=>e.status!=='activo').length, c:'#94a3b8'},
            {l:'Departamentos',   v:[...new Set(emps.map(e=>e.dept))].length,   c:'#f59e0b'},
          ].map((s,i)=>(
            <Kpi key={i} {...s}/>
          ))}
        </div>

        {/* Filtros */}
        <div style={{ display:'flex', gap:8, marginBottom:12, alignItems:'center' }}>
          <div style={{ flex:1, position:'relative' }}>
            <Search size={13} color="#94a3b8" style={{ position:'absolute', left:10, top:'50%', transform:'translateY(-50%)' }}/>
            <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="Buscar empleado o puesto..."
              style={{ width:'100%', padding:'9px 12px 9px 30px', border:'1px solid #e5e7eb', borderRadius:10, background:'#fff', fontSize:13, outline:'none', boxSizing:'border-box' }}/>
          </div>
          <select value={fStatus} onChange={e=>setFS(e.target.value)} style={{ padding:'9px 12px', border:'1px solid #e5e7eb', borderRadius:10, background:'#fff', fontSize:13, outline:'none', cursor:'pointer' }}>
            <option value="all">Todos</option>
            <option value="activo">Activos</option>
            <option value="inactivo">Inactivos</option>
          </select>
          <button onClick={abrirNuevo} style={{ padding:'9px 16px', borderRadius:9, border:'none', background:DS.primary, color:'#fff', fontWeight:700, fontSize:13, cursor:'pointer', display:'flex', alignItems:'center', gap:6 }}>
            <Plus size={14}/> Nuevo empleado
          </button>
        </div>

        <div style={{ background:'var(--card-bg,#fff)', borderRadius:16, border:'1px solid #e5e7eb', overflowX:'auto' }}>
          <table style={{ width:'100%', borderCollapse:'collapse', minWidth:640 }}>
            <thead>
              <tr style={{ borderBottom:'1px solid #eef0f3' }}>
                {['Empleado','Puesto','Departamento','Teléfono','Ingreso','Salario','Estado',''].map(h=>(
                  <th key={h} style={{ padding:'9px 12px', textAlign:'left', fontSize:12, fontWeight:600, color:'#94a3b8' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {lista.map(e=>{
                const dc=DEPT_COLORS[e.dept]||DS.primary;
                return (
                  <tr key={e.id} style={{ borderTop:'1px solid #e2e8f0', cursor:'pointer' }} onClick={()=>setDetail(e)}>
                    <td style={{ padding:'11px 12px' }}>
                      <div style={{ display:'flex', gap:9, alignItems:'center' }}>
                        <Av ini={e.ini} color={dc} size={32}/>
                        <div>
                          <div style={{ fontSize:13, fontWeight:700, color:'var(--dark,#0f172a)' }}>{e.name}</div>
                          <div style={{ fontSize:10, color:'#9aa5be' }}>{e.email}</div>
                        </div>
                      </div>
                    </td>
                    <td style={{ padding:'11px 12px', fontSize:12, fontWeight:600, color:'#4a5568' }}>{e.puesto}</td>
                    <td style={{ padding:'11px 12px' }}><Tag label={e.dept} color={dc} bg={`${dc}18`}/></td>
                    <td style={{ padding:'11px 12px', fontSize:12, color:'#4a5568' }}>{e.phone}</td>
                    <td style={{ padding:'11px 12px', fontSize:12, color:'#9aa5be' }}>{e.ingreso}</td>
                    <td style={{ padding:'11px 12px', fontSize:12, fontWeight:700, color:'#10b981' }}>{e.salario}</td>
                    <td style={{ padding:'11px 12px' }}><Tag label={e.status==='activo'?'● Activo':'○ Inactivo'} color={e.status==='activo'?'#065f46':'#64748b'} bg={e.status==='activo'?'#d1fae5':'#f1f5f9'}/></td>
                    <td style={{ padding:'11px 12px' }} onClick={ev=>ev.stopPropagation()}>
                      <div style={{ display:'flex', gap:5 }}>
                        <button onClick={ev=>abrirEditar(e,ev)} style={{ padding:'5px 8px', borderRadius:7, border:'none', background:'rgba(var(--primary-rgb),0.10)', cursor:'pointer', display:'flex' }}><Edit2 size={12} color={DS.primary}/></button>
                        <button onClick={ev=>eliminar(e.id,ev)} style={{ padding:'5px 8px', borderRadius:7, border:'none', background:'#fef2f2', cursor:'pointer', display:'flex' }}><Trash2 size={12} color="#ef4444"/></button>
                      </div>
                    </td>
                  </tr>
                );
              })}
              {!lista.length&&<tr><td colSpan={8} style={{ padding:32, textAlign:'center', color:'#9aa5be' }}>Sin empleados</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      {/* Sidebar */}
      <div style={{ display:'flex', flexDirection:'column', gap:14 }}>
        <Card title="Por departamento" Icon={Grid}>
          {[...new Set(emps.map(e=>e.dept))].map(dept=>{
            const cnt=emps.filter(e=>e.dept===dept).length;
            const c=DEPT_COLORS[dept]||DS.primary;
            return (
              <div key={dept} style={{ display:'flex', alignItems:'center', gap:10, padding:'8px 0', borderBottom:'1px solid #e2e8f0' }}>
                <div style={{ width:10, height:10, borderRadius:'50%', background:c, flexShrink:0 }}/>
                <span style={{ flex:1, fontSize:12, fontWeight:600, color:'#4a5568' }}>{dept}</span>
                <span style={{ fontSize:14, fontWeight:800, color:c }}>{cnt}</span>
              </div>
            );
          })}
        </Card>

        <Card title="Últimos añadidos" Icon={UserPlus} iconColor={DS.primary}>
          {[...emps].sort((a,b)=>b.ingreso.localeCompare(a.ingreso)).slice(0,4).map(e=>(
            <div key={e.id} style={{ display:'flex', gap:9, padding:'8px 0', borderBottom:'1px solid #e2e8f0', alignItems:'center' }}>
              <Av ini={e.ini} color={DEPT_COLORS[e.dept]||DS.primary} size={30}/>
              <div>
                <div style={{ fontSize:12, fontWeight:700, color:'var(--dark,#0f172a)' }}>{e.name.split(' ')[0]}</div>
                <div style={{ fontSize:10, color:'#9aa5be' }}>{e.puesto} · {e.ingreso}</div>
              </div>
            </div>
          ))}
        </Card>

        <button onClick={abrirNuevo} style={{ padding:'11px 0', borderRadius:12, border:'none', background:DS.primary, color:'#fff', fontWeight:700, fontSize:13, cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', gap:7 }}>
          <Plus size={14}/> Agregar empleado
        </button>
      </div>

      {/* Modal crear/editar */}
      {modal && (
        <div style={{ position:'fixed', inset:0, backdropFilter:'blur(12px)', WebkitBackdropFilter:'blur(8px)', background:'rgba(var(--primary-rgb),0.35)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:9999 }}>
          <div style={{ background:'#ffffff', borderRadius:22, padding:28, width:'min(460px,92vw)', boxShadow:'0 24px 60px rgba(0,0,0,0.3)', maxHeight:'90vh', overflowY:'auto' }}>
            <div style={{ display:'flex', justifyContent:'space-between', marginBottom:18 }}>
              <h3 style={{ margin:0, fontSize:18, fontWeight:800, color:'var(--dark,#0f172a)' }}>{editId?'Editar empleado':'Nuevo empleado'}</h3>
              <button onClick={()=>setModal(false)} style={{ border:'none', background:'none', cursor:'pointer' }}><X size={18} color="#94a3b8"/></button>
            </div>
            <div style={{ display:'grid', gap:10, marginBottom:14 }}>
              {[
                {p:'Nombre completo *', k:'name',    t:'text'},
                {p:'Puesto / cargo *',  k:'puesto',  t:'text'},
                {p:'Teléfono',          k:'phone',   t:'tel'},
                {p:'Email',             k:'email',   t:'email'},
                {p:'Salario (ej: $18,000)', k:'salario', t:'text'},
              ].map(f=>(
                <input key={f.k} type={f.t} value={form[f.k]} onChange={e=>setForm(p=>({...p,[f.k]:e.target.value}))} placeholder={f.p}
                  style={{ padding:'10px 13px', border:'1px solid #e5e7eb', borderRadius:10, background:'#fff', fontSize:13, outline:'none' }}/>
              ))}
              <select value={form.dept} onChange={e=>setForm(p=>({...p,dept:e.target.value}))} style={{ padding:'10px 13px', border:'1px solid #e5e7eb', borderRadius:10, background:'#fff', fontSize:13, outline:'none', cursor:'pointer' }}>
                {DEPTS.map(d=><option key={d} value={d}>{d}</option>)}
              </select>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10 }}>
                <div>
                  <label style={{ fontSize:11, color:'#9aa5be', fontWeight:700, display:'block', marginBottom:4 }}>FECHA DE INGRESO</label>
                  <input type="date" value={form.ingreso} onChange={e=>setForm(p=>({...p,ingreso:e.target.value}))} style={{ width:'100%', padding:'10px 13px', border:'1px solid #e5e7eb', borderRadius:10, background:'#fff', fontSize:13, outline:'none', boxSizing:'border-box' }}/>
                </div>
                <div>
                  <label style={{ fontSize:11, color:'#9aa5be', fontWeight:700, display:'block', marginBottom:4 }}>ESTADO</label>
                  <select value={form.status} onChange={e=>setForm(p=>({...p,status:e.target.value}))} style={{ width:'100%', padding:'10px 13px', border:'1px solid #e5e7eb', borderRadius:10, background:'#fff', fontSize:13, outline:'none', cursor:'pointer' }}>
                    <option value="activo">Activo</option>
                    <option value="inactivo">Inactivo</option>
                  </select>
                </div>
              </div>
            </div>
            <div style={{ display:'flex', gap:10 }}>
              <button onClick={()=>setModal(false)} style={{ flex:1, padding:'11px 0', borderRadius:10, border:'1px solid #cbd5e1', background:'#f5f7fb', color:'#9aa5be', fontSize:13, fontWeight:700, cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', gap:5 }}><X size={14}/> Cancelar</button>
              <button onClick={guardar} style={{ flex:2, padding:'11px 0', borderRadius:10, border:'none', background:DS.primary, color:'#fff', fontWeight:700, fontSize:13, cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', gap:6 }}><Save size={14}/> {editId?'Guardar':'Agregar empleado'}</button>
            </div>
          </div>
        </div>
      )}

      {/* Modal detalle empleado */}
      {detail && (
        <div style={{ position:'fixed', inset:0, backdropFilter:'blur(12px)', WebkitBackdropFilter:'blur(8px)', background:'rgba(var(--primary-rgb),0.3)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:9999 }}>
          <div style={{ background:'#ffffff', borderRadius:20, padding:26, width:'min(420px,92vw)', boxShadow:'0 20px 60px rgba(0,0,0,0.25)' }}>
            <div style={{ display:'flex', justifyContent:'space-between', marginBottom:16 }}>
              <div style={{ display:'flex', gap:12, alignItems:'center' }}>
                <Av ini={detail.ini} color={DEPT_COLORS[detail.dept]||DS.primary} size={48}/>
                <div>
                  <div style={{ fontSize:16, fontWeight:800, color:'var(--dark,#0f172a)' }}>{detail.name}</div>
                  <Tag label={detail.dept} color={DEPT_COLORS[detail.dept]||DS.primary} bg={`${DEPT_COLORS[detail.dept]||DS.primary}18`}/>
                </div>
              </div>
              <button onClick={()=>setDetail(null)} style={{ border:'none', background:'none', cursor:'pointer' }}><X size={18} color="#94a3b8"/></button>
            </div>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10, marginBottom:16 }}>
              {[
                {l:'PUESTO',    v:detail.puesto},
                {l:'TELÉFONO',  v:detail.phone},
                {l:'EMAIL',     v:detail.email},
                {l:'INGRESO',   v:detail.ingreso},
                {l:'SALARIO',   v:detail.salario},
                {l:'ESTADO',    v:detail.status},
              ].map((f,i)=>(
                <div key={i} style={{ background:'#f5f7fb', borderRadius:9, padding:'10px 12px' }}>
                  <div style={{ fontSize:10, color:'#9aa5be', fontWeight:700, marginBottom:3 }}>{f.l}</div>
                  <div style={{ fontSize:13, fontWeight:600, color:'#4a5568' }}>{f.v}</div>
                </div>
              ))}
            </div>
            <div style={{ display:'flex', gap:10 }}>
              <button onClick={ev=>{ abrirEditar(detail,{stopPropagation:()=>{}}); setDetail(null); }} style={{ flex:1, padding:'10px 0', borderRadius:10, border:'none', background:'rgba(var(--primary-rgb),0.10)', color:DS.primary, fontWeight:700, fontSize:13, cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', gap:6 }}><Edit2 size={14}/> Editar</button>
              <a href={`tel:${detail.phone}`} style={{ flex:1, padding:'10px 0', borderRadius:10, background:'#f0fdf4', color:'#16a34a', fontWeight:700, fontSize:13, display:'flex', alignItems:'center', justifyContent:'center', gap:6, textDecoration:'none' }}><Phone size={14}/> Llamar</a>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ══════════════════════════ TAB LEADS (Admin — solo lectura + reasignar) ══════════════════════════ */
/* ════════════════════════════════════════════
   PIPELINE — ADMINISTRADOR
   Tablero (kanban por temperatura, antes "Leads Bot") + Embudo (vista anterior con metas/CSV)
════════════════════════════════════════════ */
// me: usuario del supervisor → añade "Mi tablero" (solo sus propios leads/clientes)
export function TabPipelineAdmin({ leads, setLeads, clients, users, me }) {
  const [vista, setVista] = useState('tablero');
  const misLeads = me ? (leads||[]).filter(l => l.advisor_id === me.id) : [];
  return (
    <div style={{ fontFamily:"'DM Sans',sans-serif", color:NAVY }}>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-end', gap:12, flexWrap:'wrap', marginBottom:20 }}>
        <div>
          <h1 style={{ margin:0, fontSize:MOB?22:28, fontWeight:800, letterSpacing:'-0.5px' }}>Pipeline de ventas</h1>
          <p style={{ margin:'4px 0 0', fontSize:14, color:'#64748b' }}>
            {vista === 'tablero' ? `${(leads||[]).length.toLocaleString('es-EC')} leads del bot · arrastra una tarjeta para cambiarla de etapa`
              : vista === 'mio' ? `${misLeads.length.toLocaleString('es-EC')} leads tuyos · arrastra una tarjeta para cambiarla de etapa` : 'Embudo por asesor, metas y exportación'}
          </p>
        </div>
        <div style={{ display:'flex', background:'#fff', border:'1px solid #e5e7eb', borderRadius:10, padding:3 }}>
          {[...(me ? [['mio','Mi tablero',UserCheck]] : []), ['tablero','Tablero',Grid],['embudo','Embudo',TrendingUp]].map(([k,l,Ic]) => (
            <button key={k} onClick={() => setVista(k)} style={{ display:'flex', alignItems:'center', gap:6, padding:'7px 14px', borderRadius:8, border:'none', cursor:'pointer', fontSize:13, fontWeight:600,
              background:vista===k?NAVY:'transparent', color:vista===k?'#fff':'#475569' }}><Ic size={14}/>{l}</button>
          ))}
        </div>
      </div>
      {vista === 'mio'
        ? <PipelineTablero key="mio" mine meId={me.id} leads={misLeads} setLeads={setLeads} clients={(clients||[]).filter(c => c.assigned_advisor_id === me.id)} users={[]}/>
        : vista === 'tablero'
        ? <PipelineTablero key="todo" leads={leads||[]} setLeads={setLeads} clients={clients||[]} users={users||[]}/>
        : <TabPipelineEmbudo leads={leads} clients={clients} users={users}/>}
    </div>
  );
}

// mine: vista del asesor (solo sus leads, sin filtro/reasignación de asesor, rutas /api/asesor)
export function PipelineTablero({ leads, setLeads, clients, users, mine, meId }) {
  const api = mine ? '/api/asesor' : '/api/supervisor';
  const p = DS.primary;
  const dragRef = useRef(null);
  const [dragOver, setDragOver] = useState(null);
  const [detail,   setDetail]   = useState(null);
  const [q,        setQ]        = useState('');
  const [fAsesor,  setFAsesor]  = useState('all');
  const [fCanal,   setFCanal]   = useState('all');
  const [fTemp,    setFTemp]    = useState('all');
  const [solo24,   setSolo24]   = useState(false);
  const P = usePeriodo('todo');
  // Con miles de leads por columna, montar todas las tarjetas traba la pestaña: tandas de 40.
  const [visibleBy, setVisibleBy] = useState({ caliente:40, tibio:40, frio:40 });
  const { metas, metaGlobal } = useMetas();

  // Nombres y colores de siempre del CRM (antes pestaña Leads Bot)
  const COLS = {
    caliente:{ label:'¡A cerrar!',     tag:'A cerrar',    c:'#10b981', acc:'#059669', light:'#f0fdf9', border:'#a7f3d0' },
    tibio:   { label:'En seguimiento', tag:'Seguimiento', c:'#f59e0b', acc:'#d97706', light:'#fefce8', border:'#fde68a' },
    frio:    { label:'¡Rescátalos!',   tag:'Rescatar',    c:'#ef4444', acc:'#dc2626', light:'#fff1f2', border:'#fecaca' },
  };
  const team = users.filter(u => ['advisor','asesor','supervisor'].includes(u.role) && u.status === 'active');
  const contacted = l => !!(l.metadata?.asesor_contacto || l.asesor_contacto);
  const contactAt = l => l.metadata?.asesor_contacto_at || l.asesor_contacto_at;
  const horas     = d => (Date.now() - new Date(d)) / 3600000;
  const sinResp   = l => !contacted(l) && horas(l.created_at || l.fecha) > 24;
  const keyOf = s => { const c = (s||'').toLowerCase();
    return c.includes('facebook')||c.includes('fb') ? 'facebook' : c.includes('instagram')||c.includes('ig') ? 'instagram'
         : c.includes('tiktok')||c.includes('tik') ? 'tiktok' : 'whatsapp'; };
  const valor = l => Number(l.metadata?.sm_valor) || 0;
  const fmt   = n => n.toLocaleString('es-EC');
  const money = v => v >= 10000 ? `$${Math.round(v/1000)}k` : `$${fmt(v)}`;
  const iniciales = n => (n||'?').split(' ').filter(Boolean).map(w => w[0]).join('').slice(0,2).toUpperCase();
  const MODAL = { estrella:'Estrella', super_estrella:'Súper Estrella', mega_estrella:'Mega Estrella' };

  const qd = q.trim().toLowerCase(), qn = q.replace(/\D/g,'');
  const pl = P.period === 'todo' ? leads : leads.filter(l => P.inP(l.created_at || l.fecha));
  const fl = pl.filter(l =>
    (!qd || (l.nombre||'').toLowerCase().includes(qd) || (qn && (l.telefono||'').replace(/\D/g,'').includes(qn))) &&
    (fAsesor === 'all' || (l.advisor_id || l.asesorId) === fAsesor) &&
    (fCanal === 'all' || keyOf(l.canal || l.origen) === fCanal) &&
    (fTemp === 'all' || l.nivel === fTemp) &&
    (!solo24 || sinResp(l)));

  // ── KPIs ──
  const cotizados  = fl.filter(l => valor(l) > 0);
  const valorTotal = cotizados.reduce((s,l) => s + valor(l), 0);
  const contactados = fl.filter(l => contacted(l) && contactAt(l));
  const rapidos    = contactados.filter(l => new Date(contactAt(l)) - new Date(l.created_at) <= 86400000);
  const pctRapido  = contactados.length ? Math.round(rapidos.length / contactados.length * 100) : 0;
  const nTemp      = Object.fromEntries(Object.keys(COLS).map(k => [k, fl.filter(l => l.nivel === k).length]));
  const totalTemp  = Object.values(nTemp).reduce((a,b) => a + b, 0) || 1;
  const sinResp24  = pl.filter(sinResp).length;
  const ini = new Date(); ini.setDate(1); ini.setHours(0,0,0,0);
  // Todo = ventas del mes en curso (la meta es mensual); con periodo, las ventas de ese periodo
  const delMes   = c => P.period === 'todo' ? new Date(fechaVenta(c)) >= ini : P.inP(fechaVenta(c));
  const ganados  = clients.filter(c => (c.result || c.metadata?.result) === 'won' && delMes(c)).sort((a,b) => new Date(fechaVenta(b)) - new Date(fechaVenta(a)));
  const perdidos = clients.filter(c => (c.result || c.metadata?.result) === 'lost' && delMes(c)).length;
  const meta     = mine ? (metas[meId] || 0) : (metaGlobal || team.reduce((s,a) => s + (metas[a.id] || 0), 0));

  // ── Drag & drop: persiste el nivel (bloquea al bot con manual_score) ──
  async function moverA(id, nivel) {
    const antes = leads.find(l => l.id === id);
    if (!antes || antes.nivel === nivel) return;
    setLeads(prev => prev.map(l => l.id === id ? { ...l, nivel } : l));
    try { await apiFetch('PUT', `${api}/leads/${id}`, { nivel }); }
    catch (e) { setLeads(prev => prev.map(l => l.id === id ? { ...l, nivel: antes.nivel } : l)); alert('No se pudo mover el lead: ' + e.message); }
  }
  async function reasignar(id, advisorId) {
    const a = team.find(u => u.id === advisorId);
    try {
      await apiFetch('PUT', `/api/supervisor/leads/${id}`, { advisor_id: advisorId });
      const upd = l => l.id === id ? { ...l, advisor_id: advisorId, asesorId: advisorId, asesor: a?.name || l.asesor, advisor_name: a?.name } : l;
      setLeads(prev => prev.map(upd)); setDetail(d => d && upd(d));
    } catch (e) { alert('No se pudo reasignar: ' + e.message); }
  }
  function del(id) {
    askConfirm('Este lead será eliminado permanentemente.', async () => {
      try { await apiFetch('DELETE', `${api}/leads/${id}`); setLeads(prev => prev.filter(l => l.id !== id)); setDetail(null); }
      catch (e) { alert('Error al eliminar: ' + e.message); }
    });
  }

  // ── Estilos ──
  const card = { background:'var(--card-bg,#fff)', borderRadius:16, border:'1px solid #e5e7eb', padding:'18px 22px' };
  const sel  = { padding:'9px 12px', borderRadius:10, border:'1px solid #e5e7eb', background:'#fff', fontSize:13, color:NAVY, fontFamily:'inherit', cursor:'pointer' };
  const colBox = { background:'#f1f3f6', borderRadius:16, padding:12, display:'flex', flexDirection:'column', gap:10, minHeight:420, transition:'all .2s' };
  const ColHead = ({ dot, label, n, pct }) => (
    <div style={{ display:'flex', alignItems:'center', gap:8, padding:'4px 6px 6px' }}>
      <span style={{ width:9, height:9, borderRadius:'50%', background:dot }}/>
      <span style={{ fontSize:15, fontWeight:700 }}>{label}</span>
      <span style={{ fontSize:12, fontWeight:700, background:'#fff', borderRadius:999, padding:'1px 9px' }}>{fmt(n)}</span>
      <span style={{ marginLeft:'auto', fontSize:12, color:'#94a3b8' }}>{pct}%</span>
    </div>
  );
  const Avatar = ({ name }) => (
    <span title={name} style={{ width:26, height:26, borderRadius:'50%', background:`${p}1f`, color:p, fontSize:10, fontWeight:800, display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>{iniciales(name)}</span>
  );

  return (
    <>
      {detail && (
        <div onClick={e => e.target === e.currentTarget && setDetail(null)} style={{ position:'fixed', inset:0, background:'rgba(15,23,42,0.5)', backdropFilter:'blur(4px)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:9000 }}>
          <div style={{ background:'#fff', borderRadius:20, padding:28, width:'min(480px,92vw)', maxHeight:'88vh', overflowY:'auto', boxShadow:'0 24px 64px rgba(0,0,0,0.2)' }}>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:16 }}>
              <div>
                <h2 style={{ margin:'0 0 6px', fontSize:20, fontWeight:800 }}>{detail.nombre}</h2>
                <span style={{ padding:'3px 10px', borderRadius:6, fontSize:12, fontWeight:700, color:COLS[detail.nivel]?.acc, background:COLS[detail.nivel]?.light }}>{COLS[detail.nivel]?.label}</span>
              </div>
              <button onClick={() => setDetail(null)} style={{ border:'none', background:'#f1f5f9', borderRadius:10, padding:8, cursor:'pointer', display:'flex' }}><X size={14} color="#64748b"/></button>
            </div>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10, marginBottom:14 }}>
              {[['TELÉFONO', detail.telefono], ['EMAIL', detail.email || '—'], ['VALOR', valor(detail) ? `$${fmt(valor(detail))}` : '—'], ['RECIBIDO', `hace ${ago(detail.created_at || detail.fecha)}`]].map(([l,v]) => (
                <div key={l} style={{ background:'#f8fafc', borderRadius:10, padding:'10px 14px', border:'1px solid #eaecf0' }}>
                  <div style={{ fontSize:9, color:'#94a3b8', fontWeight:700, marginBottom:3 }}>{l}</div>
                  <div style={{ fontSize:13, fontWeight:700 }}>{v}</div>
                </div>
              ))}
            </div>
            {detail.msgBot && <div style={{ background:'#f8fafc', borderRadius:12, padding:'12px 14px', border:'1px solid #eaecf0', marginBottom:14, fontSize:12, color:'#475569', fontStyle:'italic' }}>"{detail.msgBot}"</div>}
            {!mine && <><label style={{ fontSize:10, color:'#94a3b8', fontWeight:700, display:'block', marginBottom:5 }}>ASESOR</label>
            <select value={detail.advisor_id || detail.asesorId || ''} onChange={e => reasignar(detail.id, e.target.value)} style={{ ...sel, width:'100%', marginBottom:16 }}>
              {!(detail.advisor_id || detail.asesorId) && <option value="">Sin asignar</option>}
              {team.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
            </select></>}
            <div style={{ display:'flex', gap:8 }}>
              <button onClick={() => window.open(`https://wa.me/${toWA(detail.telefono)}?text=Hola%20${encodeURIComponent(detail.nombre || '')}`, '_blank')} style={{ flex:1, padding:10, borderRadius:10, border:'none', background:'#f0fdf9', color:'#059669', fontWeight:700, fontSize:13, cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', gap:6 }}><MessageCircle size={14}/> WhatsApp</button>
              <button onClick={() => del(detail.id)} style={{ padding:'10px 16px', borderRadius:10, border:'none', background:'#fef2f2', color:'#ef4444', fontWeight:700, fontSize:13, cursor:'pointer', display:'flex', alignItems:'center', gap:5 }}><Trash2 size={14}/> Eliminar</button>
            </div>
          </div>
        </div>
      )}

      {/* KPIs */}
      <div style={{ display:'grid', gridTemplateColumns:MOB?'1fr 1fr':'1fr 1fr 1.4fr 1fr', gap:16, marginBottom:16 }}>
        <div style={card}>
          <div style={{ fontSize:14, color:'#475569' }}>Valor en pipeline</div>
          <div style={{ fontSize:MOB?24:30, fontWeight:800, margin:'6px 0 4px', letterSpacing:'-0.5px' }}>{money(valorTotal)}</div>
          <div style={{ fontSize:12, color:'#94a3b8' }}>{fmt(cotizados.length)} leads cotizados</div>
        </div>
        <div style={card}>
          <div style={{ fontSize:14, color:'#475569' }}>Respuesta &lt; 24 h</div>
          <div style={{ fontSize:MOB?24:30, fontWeight:800, margin:'6px 0 4px', letterSpacing:'-0.5px', color:pctRapido >= 80 ? '#16a34a' : pctRapido >= 50 ? '#d97706' : '#dc2626' }}>{pctRapido}%</div>
          <div style={{ fontSize:12, color:'#94a3b8' }}>{fmt(rapidos.length)} de {fmt(contactados.length)} contactados</div>
        </div>
        <div style={{ ...card, gridColumn:MOB?'1 / -1':'auto' }}>
          <div style={{ fontSize:14, color:'#475569', marginBottom:12 }}>Temperatura de los leads</div>
          <div style={{ display:'flex', gap:3, height:9, marginBottom:12 }}>
            {Object.entries(COLS).map(([k,c]) => nTemp[k] > 0 && <div key={k} style={{ flex:nTemp[k] / totalTemp, background:c.c, borderRadius:99 }}/>)}
          </div>
          <div style={{ display:'flex', gap:16, flexWrap:'wrap', fontSize:13 }}>
            {Object.entries(COLS).map(([k,c]) => (
              <button key={k} onClick={() => setFTemp(fTemp === k ? 'all' : k)} style={{ display:'flex', alignItems:'center', gap:6, background:'none', border:'none', padding:0, cursor:'pointer', fontSize:13, color:NAVY, fontFamily:'inherit', opacity:fTemp === 'all' || fTemp === k ? 1 : 0.4 }}>
                <span style={{ width:8, height:8, borderRadius:'50%', background:c.c }}/>{c.tag} <b>{fmt(nTemp[k])}</b>
              </button>
            ))}
          </div>
        </div>
        <div style={{ ...card, background:NAVY, border:'none', color:'#fff', gridColumn:MOB?'1 / -1':'auto' }}>
          <div style={{ fontSize:14, color:'rgba(255,255,255,0.8)' }}>{['todo','mes'].includes(P.period) ? 'Meta del mes' : 'Ventas del periodo'}</div>
          <div style={{ fontSize:MOB?24:30, fontWeight:800, margin:'6px 0 10px' }}><span style={{ color:p }}>{ganados.length}</span> / {meta || '—'}</div>
          <div style={{ height:6, borderRadius:99, background:'rgba(255,255,255,0.12)' }}><div style={{ width:`${meta ? Math.min(ganados.length / meta * 100, 100) : 0}%`, minWidth:6, height:'100%', borderRadius:99, background:p }}/></div>
        </div>
      </div>

      {/* Filtros */}
      <div style={{ display:'flex', justifyContent:'flex-end', marginBottom:12 }}><PeriodoPicker P={P}/></div>
      <div style={{ display:'flex', gap:10, flexWrap:'wrap', alignItems:'center', marginBottom:16 }}>
        <div style={{ position:'relative', flex:MOB?'1 1 100%':'0 1 300px' }}>
          <Search size={15} color="#94a3b8" style={{ position:'absolute', left:12, top:'50%', transform:'translateY(-50%)' }}/>
          <input value={q} onChange={e => setQ(e.target.value)} placeholder="Buscar por nombre o teléfono" style={{ ...sel, width:'100%', paddingLeft:36, cursor:'text', boxSizing:'border-box' }}/>
        </div>
        {!mine && <select value={fAsesor} onChange={e => setFAsesor(e.target.value)} style={sel}>
          <option value="all">Asesor: Todos</option>{team.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
        </select>}
        <select value={fCanal} onChange={e => setFCanal(e.target.value)} style={sel}>
          <option value="all">Canal: Todos</option>{[['facebook','Facebook'],['whatsapp','WhatsApp'],['instagram','Instagram'],['tiktok','TikTok']].map(([k,l]) => <option key={k} value={k}>{l}</option>)}
        </select>
        <select value={fTemp} onChange={e => setFTemp(e.target.value)} style={sel}>
          <option value="all">Temperatura: Todas</option>{Object.entries(COLS).map(([k,c]) => <option key={k} value={k}>{c.label}</option>)}
        </select>
        {(q || fAsesor !== 'all' || fCanal !== 'all' || fTemp !== 'all' || solo24) &&
          <button onClick={() => { setQ(''); setFAsesor('all'); setFCanal('all'); setFTemp('all'); setSolo24(false); }} style={{ ...sel, color:'#64748b' }}>Limpiar</button>}
        {sinResp24 > 0 && (
          <button onClick={() => setSolo24(!solo24)} style={{ marginLeft:MOB?0:'auto', display:'flex', alignItems:'center', gap:8, padding:'9px 14px', borderRadius:10, border:`1px solid ${solo24 ? '#f59e0b' : '#fde68a'}`, background:'#fffbeb', color:'#b45309', fontSize:13, cursor:'pointer', fontFamily:'inherit' }}>
            <Clock size={15}/><b>{fmt(sinResp24)} leads</b> llevan más de 24 h sin respuesta <b style={{ color:p }}>{solo24 ? 'Quitar ✕' : 'Ver →'}</b>
          </button>
        )}
      </div>

      {/* Kanban */}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(4, minmax(260px, 1fr))', gap:14, overflowX:'auto', paddingBottom:6 }}>
        {Object.entries(COLS).map(([nivel, col]) => {
          const all = fl.filter(l => l.nivel === nivel).sort((a,b) => (b.score||0) - (a.score||0));
          const vis = all.slice(0, visibleBy[nivel]);
          const over = dragOver === nivel;
          return (
            <div key={nivel} onDragOver={e => { e.preventDefault(); setDragOver(nivel); }} onDragLeave={() => setDragOver(null)}
              onDrop={e => { e.preventDefault(); setDragOver(null); if (dragRef.current) moverA(dragRef.current, nivel); dragRef.current = null; }}
              style={{ ...colBox, background:over ? col.light : colBox.background, outline:over ? `2px dashed ${col.acc}` : 'none' }}>
              <ColHead dot={col.c} label={col.label} n={all.length} pct={Math.round(all.length / (fl.length || 1) * 100)}/>
              {vis.length === 0 && <div style={{ textAlign:'center', padding:'28px 0', color:'#94a3b8', fontSize:13 }}>Arrastra leads aquí</div>}
              {vis.map(l => {
                const sub = [MODAL[l.metadata?.sm_modalidad] || l.metadata?.sm_modalidad, l.metadata?.sm_modelo].filter(Boolean).join(' · ') || l.msgBot || l.telefono;
                const atrasado = sinResp(l);
                const at = contactAt(l);
                const estado = !contacted(l)
                  ? <span style={{ color:atrasado ? '#dc2626' : '#64748b', fontWeight:atrasado ? 700 : 400 }}>{atrasado ? '⚠ ' : ''}Hace {ago(l.created_at || l.fecha)} · sin contactar</span>
                  : at && new Date(at) - new Date(l.created_at) <= 86400000
                    ? <span style={{ color:'#16a34a', fontWeight:700 }}>Contactado en {ago(new Date(Date.now() - (new Date(at) - new Date(l.created_at))))}</span>
                    : <span style={{ color:'#64748b' }}>Contactado {at ? `hace ${ago(at)}` : ''}</span>;
                return (
                  <div key={l.id} draggable onDragStart={e => { dragRef.current = l.id; e.dataTransfer.effectAllowed = 'move'; e.currentTarget.style.opacity = '0.5'; }}
                    onDragEnd={e => { e.currentTarget.style.opacity = '1'; }} onClick={() => setDetail(l)}
                    style={{ background:'#fff', borderRadius:12, padding:'14px 16px', border:'1px solid #e5e7eb', borderLeft:atrasado ? '3px solid #dc2626' : '1px solid #e5e7eb', cursor:'grab', boxShadow:'0 1px 2px rgba(15,23,42,0.04)', transition:'box-shadow .15s, transform .15s' }}
                    onMouseEnter={e => { e.currentTarget.style.boxShadow = '0 6px 18px rgba(15,23,42,0.08)'; e.currentTarget.style.transform = 'translateY(-1px)'; }}
                    onMouseLeave={e => { e.currentTarget.style.boxShadow = '0 1px 2px rgba(15,23,42,0.04)'; e.currentTarget.style.transform = ''; }}>
                    <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', gap:8 }}>
                      <span style={{ fontSize:14, fontWeight:700, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{l.nombre || l.telefono}</span>
                      <span style={{ flexShrink:0, fontSize:11, fontWeight:700, padding:'2px 7px', borderRadius:6, color:col.acc, background:col.light }}>{col.tag}</span>
                    </div>
                    <div style={{ fontSize:13, color:'#64748b', marginTop:4, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{sub}</div>
                    {valor(l) > 0 && <div style={{ fontSize:17, fontWeight:800, marginTop:8 }}>${fmt(valor(l))}</div>}
                    <div style={{ display:'flex', alignItems:'center', gap:8, marginTop:10, paddingTop:10, borderTop:'1px solid #f1f5f9', fontSize:12 }}>
                      <span style={{ display:'flex', flexShrink:0 }}>{canalLogo(keyOf(l.canal || l.origen), 18)}</span>
                      <span style={{ flex:1, minWidth:0, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{estado}</span>
                      {!mine && <Avatar name={l.advisor_name || l.asesor}/>}
                    </div>
                  </div>
                );
              })}
              {all.length > vis.length && (
                <button onClick={() => setVisibleBy(v => ({ ...v, [nivel]: v[nivel] + 40 }))} style={{ padding:10, borderRadius:10, border:'1px dashed #cbd5e1', background:'transparent', color:'#475569', fontWeight:600, fontSize:13, cursor:'pointer' }}>
                  + {fmt(all.length - vis.length)} leads más
                </button>
              )}
            </div>
          );
        })}

        {/* Ganados (clientes vendidos este mes) */}
        <div style={{ ...colBox, background:'#ecfdf3' }}>
          <ColHead dot="#16a34a" label="Ganados" n={ganados.length} pct={meta ? Math.round(ganados.length / meta * 100) : 0}/>
          {ganados.length === 0 ? (
            <div style={{ border:'1.5px dashed #86efac', borderRadius:12, padding:'24px 18px', textAlign:'center', background:'#fff' }}>
              <div style={{ width:44, height:44, borderRadius:12, background:'#f0fdf4', display:'flex', alignItems:'center', justifyContent:'center', margin:'0 auto 12px' }}><Trophy size={20} color="#16a34a"/></div>
              <div style={{ fontSize:15, fontWeight:700, marginBottom:6 }}>Aún no hay ventas este mes</div>
              <div style={{ fontSize:13, color:'#64748b', marginBottom:14 }}>Hay {fmt(nTemp.caliente)} leads listos para cerrar.</div>
              <button onClick={() => setFTemp('caliente')} style={{ padding:'9px 16px', borderRadius:9, border:'none', background:NAVY, color:'#fff', fontWeight:700, fontSize:13, cursor:'pointer' }}>Ver leads a cerrar</button>
            </div>
          ) : ganados.slice(0, 30).map(c => (
            <div key={c.id} style={{ background:'#fff', borderRadius:12, padding:'14px 16px', border:'1px solid #bbf7d0' }}>
              <div style={{ display:'flex', justifyContent:'space-between', gap:8 }}>
                <span style={{ fontSize:14, fontWeight:700, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{c.name}</span>
                <span style={{ flexShrink:0, fontSize:11, fontWeight:700, padding:'2px 7px', borderRadius:6, color:'#16a34a', background:'#f0fdf4' }}>Ganado</span>
              </div>
              {(c.metadata?.producto || c.metadata?.saleConcept) && <div style={{ fontSize:13, color:'#64748b', marginTop:4 }}>{c.metadata?.producto || c.metadata?.saleConcept}</div>}
              {Number(c.metadata?.saleAmount) > 0 && <div style={{ fontSize:17, fontWeight:800, marginTop:8 }}>${fmt(Number(c.metadata.saleAmount))}</div>}
              <div style={{ display:'flex', alignItems:'center', gap:8, marginTop:10, paddingTop:10, borderTop:'1px solid #f1f5f9', fontSize:12, color:'#64748b' }}>
                <span style={{ flex:1 }}>Vendido hace {ago(fechaVenta(c))}</span>{!mine && <Avatar name={c.asesor || c.advisor_name}/>}
              </div>
            </div>
          ))}
          <div style={{ background:'#fff', borderRadius:10, padding:'10px 14px', fontSize:13, color:'#475569' }}>
            Perdidos este mes · <b style={{ color:'#dc2626' }}>{perdidos}</b>
          </div>
        </div>
      </div>
    </>
  );
}

function TabPipelineEmbudo({ leads, clients, users }) {
  const p = DS.primary;
  // ── State ──
  const [activeStage,    setActiveStage]    = React.useState(null);
  const [selectedAsesor, setSelectedAsesor] = React.useState(null);
  const [showDetail,     setShowDetail]     = React.useState(false);
  const [showMetaModal,  setShowMetaModal]  = React.useState(false);
  const [editMeta,       setEditMeta]       = React.useState(null); // {id, name, meta}
  const { metas, metaGlobal, saveMeta: saveMetaApi }    = useMetas(); // persiste en la BD, compartido con TabVentas/supervisor
  const [period,  setPeriod]  = React.useState('mes');
  const [refDate, setRefDate] = React.useState(()=>new Date().toISOString().split('T')[0]);
  const [fromDate,setFromDate]= React.useState(()=>new Date().toISOString().slice(0,8)+'01');
  const [toDate,  setToDate]  = React.useState(()=>new Date().toISOString().split('T')[0]);
  const [hovRow,  setHovRow]  = React.useState(null);
  const [showNotif, setShowNotif] = React.useState(null);

  // ── Data ──
  const asesores  = users?.filter(u=>['advisor','asesor'].includes(u.role))||[];
  // ── Filtro por día / mes / año / rango desde-hasta (input date nativo) ──
  const inPeriod = d => {
    if(period==='todo') return true;
    if(!d) return false;
    const x=new Date(d);
    if(period==='rango'){
      const f=new Date(fromDate+'T00:00:00'), t=new Date(toDate+'T23:59:59');
      return x>=f && x<=t;
    }
    const r=new Date(refDate+'T00:00:00');
    if(period==='año') return x.getFullYear()===r.getFullYear();
    if(period==='mes') return x.getFullYear()===r.getFullYear()&&x.getMonth()===r.getMonth();
    return x.toDateString()===r.toDateString(); // dia
  };
  const periodLabel = period==='todo'?'Histórico completo'
    : period==='rango'?`${fromDate} al ${toDate}`
    : period==='año'?`Año ${refDate.slice(0,4)}`
    : period==='mes'?`Mes ${refDate.slice(0,7)}`
    : refDate;
  const fLeads   = (leads||[]).filter(l=>inPeriod(l.created_at));
  const fClients = (clients||[]).filter(c=>inPeriod(c.created_at));
  const won    = fClients.filter(c=>c.result==='won');
  const lost   = fClients.filter(c=>c.result==='lost');
  const activo = fClients.filter(c=>!c.result);
  const calientes = fLeads.filter(l=>l.nivel==='caliente');
  const tibios    = fLeads.filter(l=>l.nivel==='tibio');
  const frios     = fLeads.filter(l=>l.nivel==='frio');
  const totalL = fLeads.length;
  const totalC = fClients.length;
  const metaEmp = metaGlobal || asesores.reduce((s,a)=>s+(metas[a.id||a.name]||10),0)||asesores.length*10||10;
  const pct = Math.min(Math.round((won.length/metaEmp)*100),100);
  const ingresos = won.reduce((s,c)=>s+(Number(c.metadata?.saleAmount ?? c.saleAmount)||0),0);
  // Contactado = el asesor abrió WA (/contactar) o respondió desde el CRM. Se filtra por la FECHA de contacto.
  const contactAt  = l => l.metadata?.asesor_contacto_at || l.asesor_contacto_at;
  const allContactados = (leads||[]).filter(l=>(l.metadata?.asesor_contacto||l.asesor_contacto)&&inPeriod(contactAt(l)));
  const within24h  = l => { const at=contactAt(l); return !!at && (new Date(at)-new Date(l.created_at)) <= 86400000; };
  const contactVia = l => (l.metadata?.asesor_contacto_via || l.asesor_contacto_via)==='crm' ? 'crm' : 'whatsapp';
  const srcCount   = arr => ({ crm: arr.filter(l=>contactVia(l)==='crm').length, wa: arr.filter(l=>contactVia(l)==='whatsapp').length });
  const contactados24hArr    = allContactados.filter(within24h);
  const contactadosTardioArr = allContactados.filter(l=>!within24h(l));
  const contactados24h    = contactados24hArr.length;
  const contactadosTardio = contactadosTardioArr.length;
  const stageSrc   = { contact: srcCount(contactados24hArr), tardio: srcCount(contactadosTardioArr) };
  const stageLeads = { contact: contactados24hArr, tardio: contactadosTardioArr };
  const propuestas  = fClients.filter(c=>(c.metadata?.producto||c.producto)||(c.metadata?.saleAmount!=null)).length;

  const saveMeta = (id,val) => {
    saveMetaApi(id, val).catch(e=>alert('Error al guardar meta: '+e.message));
    setShowMetaModal(false);
    setShowNotif('Meta guardada correctamente');
    setTimeout(()=>setShowNotif(null),2500);
  };

  const asesorMetrics = asesores.map(a=>{
    const key = a.id||a.name;
    const mc = fClients.filter(c=>c.asesorId===a.id||c.assigned_advisor_id===a.id||c.advisor_id===a.id||c.asesor===a.name);
    const mw = mc.filter(c=>c.result==='won');
    const ml = fLeads.filter(l=>l.asesorId===a.id||l.advisor_id===a.id||l.asesor===a.name);
    const metaA = metas[key]||10;
    const pctA  = Math.min(Math.round((mw.length/metaA)*100),100);
    return{...a,key,clientes:mc.length,ganados:mw.length,leads:ml.length,
      perdidos:mc.filter(c=>c.result==='lost').length,
      en_proceso:mc.filter(c=>!c.result).length,
      tasa:mc.length?Math.round((mw.length/mc.length)*100):0,
      meta:metaA, pctMeta:pctA};
  }).sort((a,b)=>b.ganados-a.ganados);

  const selA = asesorMetrics.find(a=>a.key===selectedAsesor);

  const stages = [
    {key:'leads',    label:'Leads captados',  v:totalL,                     c:'#6366f1',Icon:Target,  desc:'Bot WhatsApp '},
    {key:'contact',  label:'Contactados <24h', v:contactados24h,             c:p,         Icon:Phone,   desc:'Respuesta rápida del asesor'},
    {key:'tardio',   label:'Contactados >24h', v:contactadosTardio,          c:'#f97316', Icon:Clock,   desc:'Respuesta tardía'},
    {key:'proceso',  label:'En negociación',  v:activo.length,              c:'#f59e0b',Icon:Activity, desc:'Proceso activo'},
    {key:'propuesta',label:'Propuestas',      v:propuestas,                 c:'#8b5cf6',Icon:FileText,desc:'Producto/cotización'},
    {key:'won',      label:'Ganados',         v:won.length,                 c:'#10b981',Icon:Trophy,   desc:'Venta confirmada'},
    {key:'lost',     label:'Perdidos',        v:lost.length,                c:'#ef4444',Icon:XCircle,  desc:'No concretados'},
  ];

  // Mismo estilo que el Tablero
  const box  = {background:'var(--card-bg,#fff)', borderRadius:16, border:'1px solid #e5e7eb',padding:'18px 22px'};
  const h2   = {fontSize:16,fontWeight:700,color:NAVY};
  const sub2 = {fontSize:12,color:'#94a3b8',marginTop:3};
  const ctl  = {padding:'8px 12px',borderRadius:10,border:'1px solid #e5e7eb',background:'#fff',fontSize:13,color:NAVY,fontFamily:'inherit',cursor:'pointer'};

  // ── Descargar embudo del período en CSV (client-side, sin backend) ──
  const descargarCSV = () => {
    const q = s => `"${String(s??'').replace(/"/g,'""')}"`;
    let csv = '﻿';
    csv += `EMBUDO DE VENTAS\nPeriodo,${q(periodLabel)}\n\n`;
    csv += `ETAPA,CANTIDAD\n`;
    stages.forEach(s=>{ csv += `${q(s.label)},${s.v}\n`; });
    csv += `\nCONTACTADOS - DETALLE\nCliente,Asesor,Medio,Respuesta,Fecha\n`;
    allContactados.forEach(l=>{
      const via = contactVia(l)==='crm'?'CRM':'WhatsApp';
      const ok  = within24h(l)?'A tiempo (<24h)':'Tardio (>24h)';
      const at  = contactAt(l);
      csv += `${q(l.nombre||'Sin nombre')},${q(l.asesor||'Sin asesor')},${via},${ok},${at?new Date(at).toLocaleDateString('es-EC'):''}\n`;
    });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(new Blob([csv],{type:'text/csv;charset=utf-8'}));
    a.download = `embudo_${periodLabel.replace(/[^0-9a-zA-Z]/g,'_')}.csv`;
    a.click();
  };

  return(
    <div style={{fontFamily:"'DM Sans',sans-serif",color:NAVY}}>

      {/* ── NOTIFICACIÓN TOAST ── */}
      {showNotif&&(
        <div style={{position:'fixed',top:20,right:24,zIndex:99999,background:'var(--dark,#0f172a)',color:'#fff',
          borderRadius:12,padding:'12px 20px',boxShadow:'0 8px 24px rgba(0,0,0,0.2)',
          fontSize:13,fontWeight:600,display:'flex',alignItems:'center',gap:8,
          animation:'fadeUp .25s ease'}}>
          <CheckCircle size={16} color="#10b981"/>{showNotif}
        </div>
      )}

      {/* ── MODAL META POR ASESOR ── */}
      {showMetaModal&&editMeta&&(
        <div style={{position:'fixed',inset:0,background:'rgba(15,23,42,0.5)',backdropFilter:'blur(4px)',
          display:'flex',alignItems:'center',justifyContent:'center',zIndex:9999}}
          onClick={e=>{if(e.target===e.currentTarget)setShowMetaModal(false);}}>
          <div style={{background:'#fff',borderRadius:22,padding:30,width:'min(400px,92vw)',
            boxShadow:'0 24px 60px rgba(0,0,0,0.2)',border:'1px solid #eaecf0'}}>
            <div style={{display:'flex',alignItems:'center',gap:12,marginBottom:20}}>
              <div style={{width:46,height:46,borderRadius:'50%',background:`${p}15`,
                display:'flex',alignItems:'center',justifyContent:'center',fontSize:18,fontWeight:800,color:p}}>
                {editMeta.name?.charAt(0)?.toUpperCase()}
              </div>
              <div>
                <div style={{fontSize:16,fontWeight:800,color:'var(--dark,#0f172a)'}}>Meta de {editMeta.name}</div>
                <div style={{fontSize:12,color:'#94a3b8'}}>Ventas objetivo para este período</div>
              </div>
            </div>
            <div style={{marginBottom:20}}>
              <label style={{fontSize:11,fontWeight:700,color:'#64748b',letterSpacing:0.5,
                textTransform:'uppercase',display:'block',marginBottom:8}}>
                Número de ventas objetivo
              </label>
              <input type="number" min="1" max="200"
                defaultValue={editMeta.meta}
                id="meta-input-admin"
                style={{width:'100%',padding:'14px 16px',border:'2px solid #e2e8f0',
                  borderRadius:12,fontSize:28,fontWeight:800,textAlign:'center',
                  color:'var(--dark,#0f172a)',outline:'none',boxSizing:'border-box',transition:'border .2s'}}
                onFocus={e=>e.target.style.borderColor=p}
                onBlur={e=>e.target.style.borderColor='#e2e8f0'}/>
              <div style={{marginTop:10,display:'flex',gap:6,flexWrap:'wrap'}}>
                {[5,10,15,20,25,30].map(n=>(
                  <button key={n} onClick={()=>{document.getElementById('meta-input-admin').value=n;}}
                    style={{padding:'5px 12px',borderRadius:8,border:`1.5px solid ${p}20`,
                      background:`${p}08`,color:p,fontSize:12,fontWeight:700,cursor:'pointer',
                      transition:'all .15s'}}
                    onMouseEnter={e=>{e.currentTarget.style.background=p;e.currentTarget.style.color='#fff';}}
                    onMouseLeave={e=>{e.currentTarget.style.background=`${p}08`;e.currentTarget.style.color=p;}}>
                    {n}
                  </button>
                ))}
              </div>
            </div>
            <div style={{background:'#f8fafc',borderRadius:12,padding:'12px 14px',
              marginBottom:20,border:'1px solid #eaecf0'}}>
              <div style={{fontSize:11,color:'#64748b',marginBottom:4}}>
                <b style={{color:'var(--dark,#0f172a)'}}>Meta actual:</b> {editMeta.meta} ventas
              </div>
              <div style={{fontSize:11,color:'#64748b'}}>
                <b style={{color:'var(--dark,#0f172a)'}}>Progreso:</b> {editMeta.ganados} ganadas
                ({Math.round((editMeta.ganados/(editMeta.meta||1))*100)}%)
              </div>
            </div>
            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:10}}>
              <button onClick={()=>setShowMetaModal(false)}
                style={{padding:'12px',borderRadius:12,border:'1.5px solid #e2e8f0',
                  background:'#f8fafc',color:'#64748b',fontWeight:700,fontSize:13,
                  cursor:'pointer',transition:'all .15s'}}
                onMouseEnter={e=>e.currentTarget.style.background='#e2e8f0'}
                onMouseLeave={e=>e.currentTarget.style.background='#f8fafc'}>
                Cancelar
              </button>
              <button onClick={()=>saveMeta(editMeta.key, document.getElementById('meta-input-admin').value)}
                style={{padding:'12px',borderRadius:12,border:'none',
                  background:`linear-gradient(135deg,${p},${p}cc)`,
                  color:'#fff',fontWeight:800,fontSize:13,cursor:'pointer',
                  boxShadow:`0 4px 14px ${p}35`,transition:'all .2s'}}
                onMouseEnter={e=>{e.currentTarget.style.transform='translateY(-1px)';}}
                onMouseLeave={e=>{e.currentTarget.style.transform='';}}>
                Guardar meta
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── MODAL DETALLE ASESOR ── */}
      {selA&&showDetail&&(
        <div style={{position:'fixed',inset:0,background:'rgba(15,23,42,0.45)',backdropFilter:'blur(4px)',
          display:'flex',alignItems:'center',justifyContent:'center',zIndex:9000}}
          onClick={e=>{if(e.target===e.currentTarget){setShowDetail(false);setSelectedAsesor(null);}}}>
          <div style={{background:'#fff',borderRadius:22,padding:30,width:'min(500px,92vw)',
            boxShadow:'0 24px 64px rgba(0,0,0,0.2)',border:'1px solid #eaecf0',maxHeight:'90vh',overflowY:'auto'}}>
            <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:22}}>
              <div style={{display:'flex',alignItems:'center',gap:14}}>
                <div style={{width:52,height:52,borderRadius:'50%',
                  background:`linear-gradient(135deg,${p},${p}cc)`,
                  display:'flex',alignItems:'center',justifyContent:'center',
                  fontSize:20,fontWeight:800,color:'#fff',boxShadow:`0 4px 14px ${p}35`}}>
                  {selA.name?.charAt(0)?.toUpperCase()}
                </div>
                <div>
                  <div style={{fontSize:17,fontWeight:800,color:'var(--dark,#0f172a)'}}>{selA.name}</div>
                  <span style={{fontSize:11,color:'#94a3b8',background:'#f1f5f9',
                    borderRadius:999,padding:'2px 10px',fontWeight:600}}>Asesor de ventas</span>
                </div>
              </div>
              <button onClick={()=>{setShowDetail(false);setSelectedAsesor(null);}}
                style={{border:'none',background:'#f1f5f9',borderRadius:10,padding:8,cursor:'pointer',display:'flex'}}
                onMouseEnter={e=>e.currentTarget.style.background='#e2e8f0'}
                onMouseLeave={e=>e.currentTarget.style.background='#f1f5f9'}>
                <X size={14} color="#64748b"/>
              </button>
            </div>

            {/* Meta progress */}
            <div style={{background:`${p}08`,borderRadius:14,padding:'16px 18px',
              marginBottom:18,border:`1px solid ${p}20`}}>
              <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:10}}>
                <div>
                  <div style={{fontSize:12,fontWeight:700,color:'var(--dark,#0f172a)'}}>Meta asignada</div>
                  <div style={{fontSize:11,color:'#64748b'}}>{selA.ganados} de {selA.meta} ventas</div>
                </div>
                <div style={{display:'flex',alignItems:'center',gap:10}}>
                  <span style={{fontSize:22,fontWeight:800,color:p}}>{selA.pctMeta}%</span>
                  <button onClick={()=>{setEditMeta(selA);setShowMetaModal(true);setShowDetail(false);}}
                    style={{padding:'6px 14px',borderRadius:9,border:'none',
                      background:p,color:'#fff',fontSize:11,fontWeight:700,cursor:'pointer',
                      boxShadow:`0 3px 10px ${p}35`,transition:'all .15s'}}
                    onMouseEnter={e=>e.currentTarget.style.transform='translateY(-1px)'}
                    onMouseLeave={e=>e.currentTarget.style.transform=''}>
                    Editar meta
                  </button>
                </div>
              </div>
              <div style={{background:'rgba(255,255,255,0.6)',borderRadius:999,height:10,overflow:'hidden'}}>
                <div style={{width:`${selA.pctMeta}%`,height:'100%',
                  background:selA.pctMeta>=80?'#10b981':selA.pctMeta>=50?p:'#f59e0b',
                  borderRadius:999,transition:'width .7s ease'}}/>
              </div>
            </div>

            {/* Stats */}
            <div style={{display:'grid',gridTemplateColumns:MOB?'repeat(2,1fr)':'repeat(4,1fr)',gap:10,marginBottom:18}}>
              {[{l:'Clientes',v:selA.clientes,c:p},{l:'Ganados',v:selA.ganados,c:'#10b981'},
                {l:'En proceso',v:selA.en_proceso,c:'#f59e0b'},{l:'Leads',v:selA.leads,c:'#6366f1'}
              ].map((s,i)=>(
                <div key={i} style={{textAlign:'center',background:'#f8fafc',borderRadius:12,
                  padding:'13px 8px',border:`1px solid ${s.c}15`}}>
                  <div style={{fontSize:24,fontWeight:800,color:s.c,lineHeight:1,marginBottom:3}}>{s.v}</div>
                  <div style={{fontSize:9,color:'#94a3b8'}}>{s.l}</div>
                </div>
              ))}
            </div>

            {/* Tasa */}
            <div style={{marginBottom:16}}>
              <div style={{display:'flex',justifyContent:'space-between',marginBottom:7}}>
                <span style={{fontSize:12,fontWeight:700,color:'var(--dark,#0f172a)'}}>Tasa de conversión</span>
                <span style={{fontSize:15,fontWeight:800,
                  color:selA.tasa>=60?'#10b981':selA.tasa>=30?'#f59e0b':'#ef4444'}}>{selA.tasa}%</span>
              </div>
              <div style={{background:'#f1f5f9',borderRadius:999,height:10,overflow:'hidden'}}>
                <div style={{width:`${selA.tasa}%`,height:'100%',
                  background:selA.tasa>=60?'#10b981':selA.tasa>=30?'#f59e0b':'#ef4444',
                  borderRadius:999,transition:'width .7s ease'}}/>
              </div>
            </div>

            {/* Diagnóstico */}
            <div style={{padding:'14px 16px',background:'#f8fafc',borderRadius:14,
              border:'1px solid #eaecf0',fontSize:12,color:'#64748b',lineHeight:1.6,marginBottom:16}}>
              <b style={{color:'var(--dark,#0f172a)'}}>Diagnóstico ejecutivo:</b>{' '}
              {selA.tasa>=60?`${selA.name} supera las expectativas. Considera asignarle una meta más alta y leads premium.`:
               selA.tasa>=30?`${selA.name} muestra progreso constante. Refuerza técnicas de cierre para alcanzar el siguiente nivel.`:
               `${selA.name} requiere atención inmediata. Programa coaching y revisa su cartera de clientes esta semana.`}
            </div>

            {/* Acciones */}
            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:8}}>
              <button onClick={()=>{setEditMeta(selA);setShowMetaModal(true);setShowDetail(false);}}
                style={{padding:'10px',borderRadius:11,border:'none',background:`${p}10`,color:p,
                  fontWeight:700,fontSize:12,cursor:'pointer',display:'flex',alignItems:'center',
                  justifyContent:'center',gap:5,transition:'all .15s'}}
                onMouseEnter={e=>{e.currentTarget.style.background=p;e.currentTarget.style.color='#fff';}}
                onMouseLeave={e=>{e.currentTarget.style.background=`${p}10`;e.currentTarget.style.color=p;}}>
                <Star size={13}/> Meta
              </button>
              <button onClick={()=>window.open(`mailto:${selA.email||''}?subject=Revisión de pipeline`,'_blank')}
                style={{padding:'10px',borderRadius:11,border:'none',background:'#f0fdf9',color:'#059669',
                  fontWeight:700,fontSize:12,cursor:'pointer',display:'flex',alignItems:'center',
                  justifyContent:'center',gap:5,transition:'all .15s'}}
                onMouseEnter={e=>{e.currentTarget.style.background='#059669';e.currentTarget.style.color='#fff';}}
                onMouseLeave={e=>{e.currentTarget.style.background='#f0fdf9';e.currentTarget.style.color='#059669';}}>
                <MessageCircle size={13}/> Contactar
              </button>
              <button onClick={()=>{setShowDetail(false);}}
                style={{padding:'10px',borderRadius:11,border:'1.5px solid #e2e8f0',background:'#fff',
                  color:'#64748b',fontWeight:700,fontSize:12,cursor:'pointer',transition:'all .15s'}}
                onMouseEnter={e=>{e.currentTarget.style.background='#f1f5f9';}}
                onMouseLeave={e=>{e.currentTarget.style.background='#fff';}}>
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── FILTROS (mismo estilo que el Tablero) ── */}
      <div style={{display:'flex',gap:8,alignItems:'center',flexWrap:'wrap',justifyContent:'flex-end',marginBottom:16}}>
        {period==='rango' ? (<>
          <input type="date" value={fromDate} max={toDate} onChange={e=>setFromDate(e.target.value)} style={ctl}/>
          <span style={{fontSize:13,color:'#94a3b8'}}>a</span>
          <input type="date" value={toDate} min={fromDate} onChange={e=>setToDate(e.target.value)} style={ctl}/>
        </>) : period!=='todo' && (
          <input type="date" value={refDate} onChange={e=>setRefDate(e.target.value)} style={ctl}/>
        )}
        <button onClick={descargarCSV} style={{...ctl,display:'flex',alignItems:'center',gap:6,fontWeight:600}}><Download size={14}/> CSV</button>
        <div style={{display:'flex',background:'#fff',border:'1px solid #e5e7eb',borderRadius:10,padding:3}}>
          {[['dia','Día'],['mes','Mes'],['año','Año'],['rango','Rango'],['todo','Todo']].map(([per,lbl])=>(
            <button key={per} onClick={()=>setPeriod(per)} style={{padding:'7px 14px',borderRadius:8,border:'none',cursor:'pointer',fontSize:13,fontWeight:600,
              background:period===per?NAVY:'transparent',color:period===per?'#fff':'#475569'}}>{lbl}</button>
          ))}
        </div>
      </div>

      {/* ── KPI CARDS ── */}
      <div style={{display:'grid',gridTemplateColumns:MOB?'1fr 1fr':'repeat(4,1fr) 1.2fr',gap:16,marginBottom:16}}>
        {[
          {label:'Leads totales', v:totalL,           sub:'del bot'},
          {label:'¡A cerrar!',    v:calientes.length, sub:'prioritarios'},
          {label:'En proceso',    v:activo.length,    sub:'negociando'},
          {label:'Ganados',       v:won.length,       sub:`$${(ingresos/1000).toFixed(0)}k estimados`},
        ].map((k,i)=>(
          <div key={i} style={box}>
            <div style={{fontSize:14,color:'#475569'}}>{k.label}</div>
            <div style={{fontSize:MOB?24:30,fontWeight:800,margin:'6px 0 4px',letterSpacing:'-0.5px'}}>{Number(k.v).toLocaleString('es-EC')}</div>
            <div style={{fontSize:12,color:'#94a3b8'}}>{k.sub}</div>
          </div>
        ))}
        <div style={{...box,background:NAVY,border:'none',color:'#fff',gridColumn:MOB?'1 / -1':'auto'}}>
          <div style={{fontSize:14,color:'rgba(255,255,255,0.8)'}}>Progreso de la empresa</div>
          <div style={{fontSize:MOB?24:30,fontWeight:800,margin:'6px 0 10px'}}><span style={{color:p}}>{won.length}</span> / {metaEmp}</div>
          <div style={{height:6,borderRadius:99,background:'rgba(255,255,255,0.12)'}}><div style={{width:`${pct}%`,minWidth:6,height:'100%',borderRadius:99,background:p}}/></div>
          <div style={{fontSize:12,color:'rgba(255,255,255,0.6)',marginTop:8}}>{pct}% de la meta</div>
        </div>
      </div>

      {/* ── FUNNEL + TEMPERATURA ── */}
      <div style={{display:'grid',gridTemplateColumns:MOB?'1fr':'1.4fr 1fr',gap:16,marginBottom:16}}>
        <div style={box}>
          <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',gap:10,marginBottom:16}}>
            <div><div style={h2}>Embudo de conversión</div><div style={sub2}>Clic en una etapa para ver el detalle</div></div>
            <span style={{fontSize:12,fontWeight:700,color:p,background:`rgba(var(--primary-rgb),0.1)`,borderRadius:6,padding:'3px 9px',whiteSpace:'nowrap'}}>{pct}% completado</span>
          </div>
          {stages.map((s,i,arr)=>{
            const maxV=arr[0].v||1;
            const barW=s.v>0?Math.max((s.v/maxV)*100,3):0;
            const rate=i>0&&arr[i-1].v>0?Math.round((s.v/arr[i-1].v)*100):100;
            const isAct=activeStage===s.key;
            return(
              <div key={s.key} onClick={()=>setActiveStage(isAct?null:s.key)}
                style={{padding:'12px 14px',borderRadius:12,marginBottom:8,cursor:'pointer',border:`1px solid ${isAct?'#e5e7eb':'transparent'}`,background:isAct?'#f8fafc':'transparent',transition:'background .15s'}}
                onMouseEnter={e=>{if(!isAct)e.currentTarget.style.background='#f8fafc';}} onMouseLeave={e=>{if(!isAct)e.currentTarget.style.background='transparent';}}>
                <div style={{display:'flex',alignItems:'center',gap:10,marginBottom:8}}>
                  <span style={{width:9,height:9,borderRadius:'50%',background:s.c,flexShrink:0}}/>
                  <div style={{flex:1,minWidth:0}}>
                    <div style={{fontSize:14,fontWeight:700}}>{s.label}</div>
                    <div style={{fontSize:12,color:'#94a3b8'}}>{s.desc}</div>
                  </div>
                  <div style={{textAlign:'right'}}>
                    <div style={{fontSize:18,fontWeight:800}}>{s.v.toLocaleString('es-EC')}</div>
                    {i>0&&<div style={{fontSize:11,color:'#94a3b8'}}>{rate}% conv.</div>}
                  </div>
                </div>
                <div style={{background:'#f1f5f9',borderRadius:99,height:8}}><div style={{width:`${barW}%`,height:'100%',background:s.c,borderRadius:99,transition:'width .6s ease'}}/></div>
                {isAct&&(
                  <div style={{marginTop:12,paddingTop:12,borderTop:'1px solid #eef0f3'}}>
                    <div style={{display:'grid',gridTemplateColumns:MOB?'repeat(2,1fr)':'repeat(4,1fr)',gap:8,textAlign:'center'}}>
                      {[
                        {l:'Cantidad',v:s.v},
                        {l:'% del total',v:`${totalL?Math.round((s.v/totalL)*100):0}%`},
                        {l:'Conversión',v:`${rate}%`},
                        {l:'Ingresos',v:s.key==='won'?`$${ingresos.toLocaleString('es-EC')}`:'—'},
                      ].map((d,j)=>(
                        <div key={j} style={{background:'#fff',border:'1px solid #e5e7eb',borderRadius:10,padding:'8px 4px'}}>
                          <div style={{fontSize:15,fontWeight:800}}>{d.v}</div>
                          <div style={{fontSize:11,color:'#94a3b8'}}>{d.l}</div>
                        </div>
                      ))}
                    </div>
                    {stageSrc[s.key]&&(
                      <div style={{marginTop:10,display:'flex',justifyContent:'center',gap:14,flexWrap:'wrap',fontSize:12,color:'#475569'}}>
                        <span>💬 <b>{stageSrc[s.key].crm}</b> desde el CRM</span>
                        <span>📱 <b>{stageSrc[s.key].wa}</b> directo a WhatsApp</span>
                      </div>
                    )}
                    {stageLeads[s.key]&&(
                      <div style={{marginTop:10,maxHeight:280,overflowY:'auto',display:'flex',flexDirection:'column',gap:6}}>
                        {stageLeads[s.key].length===0&&<div style={{fontSize:12,color:'#94a3b8',textAlign:'center',padding:6}}>Sin contactos en este período</div>}
                        {stageLeads[s.key].map(l=>{const via=contactVia(l),at=contactAt(l),ok=within24h(l);return(
                          <div key={l.id} style={{display:'flex',alignItems:'center',gap:8,padding:'9px 12px',background:'#fff',borderRadius:10,border:'1px solid #e5e7eb',flexWrap:'wrap'}}>
                            <div style={{flex:1,minWidth:120}}>
                              <div style={{fontSize:13,fontWeight:700,whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis'}}>{l.nombre||'Sin nombre'}</div>
                              <div style={{fontSize:11,color:'#94a3b8'}}>{l.asesor||'Sin asesor'}</div>
                            </div>
                            <span style={{fontSize:11,fontWeight:700,color:'#16a34a',background:'#f0fdf4',borderRadius:6,padding:'2px 7px'}}>{via==='crm'?'CRM':'WhatsApp'}</span>
                            <span style={{fontSize:11,fontWeight:700,color:ok?'#16a34a':'#d97706',background:ok?'#f0fdf4':'#fffbeb',borderRadius:6,padding:'2px 7px'}}>{ok?'A tiempo':'Tardío'}</span>
                            <span style={{fontSize:11,color:'#94a3b8'}}>{at?new Date(at).toLocaleDateString('es-EC',{day:'2-digit',month:'2-digit',year:'numeric'}):'—'}</span>
                          </div>
                        );})}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <div style={{display:'flex',flexDirection:'column',gap:16}}>
          {(()=>{
            // Mismos nombres y colores que las columnas del Tablero
            const T=[
              {l:'¡A cerrar!',     v:calientes.length, c:'#10b981'},
              {l:'En seguimiento', v:tibios.length,    c:'#f59e0b'},
              {l:'¡Rescátalos!',   v:frios.length,     c:'#ef4444'},
            ];
            const tot=T.reduce((a,b)=>a+b.v,0)||1;
            return(
              <div style={box}>
                <div style={{...h2,marginBottom:14}}>Temperatura de los leads</div>
                <div style={{display:'flex',gap:3,height:9,marginBottom:16}}>
                  {T.map(t=>t.v>0&&<div key={t.l} style={{flex:t.v/tot,background:t.c,borderRadius:99}}/>)}
                </div>
                {T.map(t=>(
                  <div key={t.l} style={{display:'flex',alignItems:'center',gap:10,padding:'10px 0',borderTop:'1px solid #f3f4f6',fontSize:14}}>
                    <span style={{width:9,height:9,borderRadius:'50%',background:t.c}}/>
                    <span style={{flex:1,fontWeight:600}}>{t.l}</span>
                    <b>{t.v.toLocaleString('es-EC')}</b>
                    <span style={{width:42,textAlign:'right',fontSize:12,color:'#94a3b8'}}>{Math.round(t.v/tot*100)}%</span>
                  </div>
                ))}
              </div>
            );
          })()}
          <div style={box}>
            <div style={{...h2,marginBottom:14}}>Estado de clientes</div>
            <div style={{display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:10}}>
              {[{l:'En proceso',v:activo.length,c:NAVY},{l:'Ganados',v:won.length,c:'#16a34a'},{l:'Perdidos',v:lost.length,c:'#dc2626'}].map((s,i)=>(
                <div key={i} style={{textAlign:'center',padding:'12px 6px',background:'#f8fafc',borderRadius:12,border:'1px solid #eef0f3'}}>
                  <div style={{fontSize:22,fontWeight:800,color:s.c,lineHeight:1,marginBottom:4}}>{s.v.toLocaleString('es-EC')}</div>
                  <div style={{fontSize:12,color:'#475569',fontWeight:600}}>{s.l}</div>
                  <div style={{fontSize:11,color:'#94a3b8',marginTop:2}}>{totalC?Math.round((s.v/totalC)*100):0}%</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* ── RANKING ASESORES CON METAS ── */}
      <div style={box}>
        <div style={{marginBottom:14}}>
          <div style={h2}>Ranking de asesores · Metas individuales</div>
          <div style={sub2}>Clic en un asesor para ver su detalle o gestionar su meta</div>
        </div>
        <div style={{overflowX:'auto'}}>
          <div style={{display:'grid',gridTemplateColumns:'36px 1fr 70px 70px 70px 70px 140px 90px',minWidth:680,gap:10,padding:'0 0 10px',borderBottom:'1px solid #eef0f3'}}>
            {['#','Asesor','Clientes','Ganados','Leads','Tasa','Prog. meta',''].map((h,i)=>(
              <div key={i} style={{fontSize:12,color:'#94a3b8',textAlign:i>1?'center':'left'}}>{h}</div>
            ))}
          </div>
          {asesorMetrics.length===0&&<div style={{padding:32,textAlign:'center',color:'#94a3b8',fontSize:13}}>Sin asesores registrados</div>}
          {asesorMetrics.map((a,i)=>(
            <div key={a.key||i} onClick={()=>{setSelectedAsesor(a.key);setShowDetail(true);}}
              onMouseEnter={()=>setHovRow(i)} onMouseLeave={()=>setHovRow(null)}
              style={{display:'grid',gridTemplateColumns:'36px 1fr 70px 70px 70px 70px 140px 90px',minWidth:680,gap:10,padding:'12px 0',borderBottom:'1px solid #f3f4f6',alignItems:'center',cursor:'pointer',background:hovRow===i?'#f8fafc':'transparent',fontSize:14}}>
              <span style={{width:26,height:26,borderRadius:'50%',background:i<3?`rgba(var(--primary-rgb),0.12)`:'#f1f5f9',color:i<3?p:'#64748b',fontSize:12,fontWeight:700,display:'flex',alignItems:'center',justifyContent:'center'}}>{i+1}</span>
              <div style={{minWidth:0}}>
                <div style={{fontWeight:600,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{a.name}</div>
                <div style={{fontSize:12,color:'#94a3b8'}}>Meta: {a.meta} ventas</div>
              </div>
              <div style={{textAlign:'center'}}>{a.clientes}</div>
              <div style={{textAlign:'center',fontWeight:700,color:a.ganados?'#16a34a':'#94a3b8'}}>{a.ganados}</div>
              <div style={{textAlign:'center'}}>{a.leads}</div>
              <div style={{textAlign:'center'}}>
                <span style={{fontSize:12,fontWeight:700,padding:'2px 8px',borderRadius:6,
                  color:a.tasa>=60?'#16a34a':a.tasa>=30?'#d97706':'#dc2626',
                  background:a.tasa>=60?'#f0fdf4':a.tasa>=30?'#fffbeb':'#fef2f2'}}>{a.tasa}%</span>
              </div>
              <div>
                <div style={{display:'flex',alignItems:'center',gap:6}}>
                  <div style={{flex:1,background:'#f1f5f9',borderRadius:99,height:6}}><div style={{width:`${a.pctMeta}%`,height:'100%',background:p,borderRadius:99}}/></div>
                  <span style={{fontSize:12,fontWeight:700}}>{a.pctMeta}%</span>
                </div>
                <div style={{fontSize:11,color:'#94a3b8',marginTop:2,textAlign:'center'}}>{a.ganados}/{a.meta}</div>
              </div>
              <div style={{textAlign:'center'}}>
                <button onClick={e=>{e.stopPropagation();setEditMeta(a);setShowMetaModal(true);}}
                  style={{padding:'6px 12px',borderRadius:8,border:'1px solid #e5e7eb',background:'#fff',color:NAVY,fontSize:12,fontWeight:700,cursor:'pointer',display:'inline-flex',alignItems:'center',gap:4}}>
                  <Star size={11} color={p}/> Meta
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function TabRendimientoEquipo({ clients, users, leads }) {
  const p = DS.primary;
  const [orden, setOrden] = useState('conversion');

  const asesores = (users||[]).filter(u => u.role==='advisor'||u.role==='asesor'||u.role==='supervisor');
  const resultadoDe = c => c.result || c.metadata?.result;

  const dataAsesores = asesores.map(a => {
    const ac       = clients.filter(c => c.asesorId===a.id || c.assigned_advisor_id===a.id || c.advisor_id===a.id);
    const leadsA   = leads.filter(l => l.advisor_id===a.id);
    const ganados  = ac.filter(c => resultadoDe(c)==='won').length;
    const perdidos = ac.filter(c => resultadoDe(c)==='lost').length;
    const proceso  = ac.filter(c => !resultadoDe(c)).length;
    const conv     = ac.length ? Math.round((ganados/ac.length)*100) : 0;
    return { ...a, total_clientes:ac.length, ganados, perdidos, proceso, conv, leads:leadsA.length };
  });

  const sorted = [...dataAsesores].sort((a,b) => {
    if (orden==='conversion') return b.conv - a.conv;
    if (orden==='ganados')    return b.ganados - a.ganados;
    if (orden==='leads')      return b.leads - a.leads;
    return b.total_clientes - a.total_clientes;
  });

  const totalGanados  = dataAsesores.reduce((s,a)=>s+a.ganados,0);
  const totalClientes = dataAsesores.reduce((s,a)=>s+a.total_clientes,0)||1;
  const convEquipo    = Math.round((totalGanados/totalClientes)*100);

  return (
    <div style={{fontFamily:"'DM Sans',sans-serif",color:NAVY}}>
      <PageHead title="Rendimiento del equipo" sub="Clientes, ventas y conversión por asesor" right={<ExportMenu endpoint="leads" label="Exportar leads" withMonth/>}/>
      <div style={{display:'grid',gridTemplateColumns:MOB?'repeat(2,1fr)':'repeat(4,1fr)',gap:16,marginBottom:16}}>
        {[
          {l:'Asesores',         v:asesores.length,  c:DS.primary},
          {l:'Clientes totales', v:totalClientes,     c:'#8b5cf6'},
          {l:'Ventas cerradas',  v:totalGanados,      c:'#10b981'},
          {l:'Tasa conversión',  v:`${convEquipo}%`,  c:'#f59e0b'},
        ].map((k,i)=>(
          <Kpi key={i} {...k}/>
        ))}
      </div>

      <div style={{background:'var(--card-bg,#fff)', borderRadius:16, border:'1px solid #e5e7eb',overflow:'hidden'}}>
        <div style={{padding:'14px 18px',borderBottom:'1px solid #f1f5f9',display:'flex',justifyContent:'space-between',alignItems:'center',flexWrap:'wrap',gap:8}}>
          <div style={{fontSize:16,fontWeight:700,color:NAVY}}>Ranking del equipo</div>
          <div style={{display:'flex',gap:6}}>
            {[{k:'conversion',l:'Conversión'},{k:'ganados',l:'Ganados'},{k:'leads',l:'Leads'},{k:'clientes',l:'Clientes'}].map(o=>(
              <button key={o.k} onClick={()=>setOrden(o.k)}
                style={{padding:'5px 12px',borderRadius:7,border:'none',fontSize:11,fontWeight:700,cursor:'pointer',
                  background:orden===o.k?NAVY:'#fff',color:orden===o.k?'#fff':'#475569',boxShadow:'inset 0 0 0 1px #e5e7eb'}}>
                {o.l}
              </button>
            ))}
          </div>
        </div>
        <div style={{overflowX:'auto'}}>
        <table style={{width:'100%',borderCollapse:'collapse',minWidth:680}}>
          <thead>
            <tr style={{borderBottom:'1px solid #eef0f3'}}>
              {['#','Asesor','Clientes','Ganados','Perdidos','En proceso','Leads','Conversión'].map(h=>(
                <th key={h} style={{padding:'10px 14px',fontSize:12,fontWeight:600,color:'#94a3b8',textAlign:'left'}}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {sorted.map((a,i)=>{
              const medal=i===0?'🥇':i===1?'🥈':i===2?'🥉':'';
              return(
                <tr key={a.id} style={{borderTop:'1px solid #f8fafc',background:i===0?`${p}04`:'transparent'}}>
                  <td style={{padding:'12px 14px',fontSize:13,fontWeight:800,color:p}}>{medal||i+1}</td>
                  <td style={{padding:'12px 14px'}}>
                    <div style={{display:'flex',alignItems:'center',gap:10}}>
                      <div style={{width:34,height:34,borderRadius:'50%',background:`${p}15`,display:'flex',alignItems:'center',justifyContent:'center',fontSize:13,fontWeight:800,color:p}}>
                        {(a.name||'?').charAt(0).toUpperCase()}
                      </div>
                      <div>
                        <div style={{fontSize:13,fontWeight:700,color:'var(--dark,#0f172a)'}}>{a.name}</div>
                        <div style={{fontSize:10,color:'#94a3b8'}}>{a.email||''}</div>
                      </div>
                    </div>
                  </td>
                  <td style={{padding:'12px 14px',fontSize:13,fontWeight:600,color:DS.primary}}>{a.total_clientes}</td>
                  <td style={{padding:'12px 14px',fontSize:13,fontWeight:700,color:'#10b981'}}>{a.ganados}</td>
                  <td style={{padding:'12px 14px',fontSize:13,fontWeight:600,color:'#ef4444'}}>{a.perdidos}</td>
                  <td style={{padding:'12px 14px',fontSize:13,fontWeight:600,color:'#f59e0b'}}>{a.proceso}</td>
                  <td style={{padding:'12px 14px',fontSize:13,fontWeight:600,color:'#8b5cf6'}}>{a.leads}</td>
                  <td style={{padding:'12px 14px'}}>
                    <div style={{display:'flex',alignItems:'center',gap:8}}>
                      <div style={{flex:1,background:'#f1f5f9',borderRadius:999,height:6,overflow:'hidden',minWidth:60}}>
                        <div style={{width:`${a.conv}%`,height:'100%',background:a.conv>=60?'#10b981':a.conv>=30?'#f59e0b':'#ef4444',borderRadius:999}}/>
                      </div>
                      <span style={{fontSize:12,fontWeight:800,color:a.conv>=60?'#10b981':a.conv>=30?'#f59e0b':'#ef4444',minWidth:36}}>{a.conv}%</span>
                    </div>
                  </td>
                </tr>
              );
            })}
            {sorted.length===0&&<tr><td colSpan={8} style={{padding:24,textAlign:'center',color:'#94a3b8',fontSize:12}}>Sin asesores registrados</td></tr>}
          </tbody>
        </table>
        </div>
      </div>
    </div>
  );
}

function TabConversaciones({ leads }) {
  const p = DS.primary;
  const [sel, setSel] = useState(null);
  const [convs, setConvs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate]     = useState('');
  const endRef = useRef(null);

  async function load(lead) {
    setSel(lead); setLoading(true);
    try { const d = await apiFetch('GET', `/api/webhook/conversations/${lead.id}`); setConvs(Array.isArray(d)?d:[]); }
    catch(e) { setConvs([]); }
    setLoading(false);
  }

  // Auto-scroll al último mensaje: así no hay que arrastrar la barra para ver lo más reciente.
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [convs, sel]);

  const filtered = leads.filter(l => {
    const matchTxt = l.nombre?.toLowerCase().includes(search.toLowerCase()) || l.telefono?.includes(search);
    if (!matchTxt) return false;
    const fecha = (l.created_at || l.fecha || '').slice(0,10);
    if (fromDate && fecha < fromDate) return false;
    if (toDate   && fecha > toDate)   return false;
    return true;
  });
  const NIVEL = { caliente:{c:'#10b981',bg:'#f0fdf9',l:'Caliente'}, tibio:{c:'#f59e0b',bg:'#fefce8',l:'Tibio'}, frio:{c:'#ef4444',bg:'#fef2f2',l:'Frio'} };
  // Paginado: con miles de leads, renderizarlos todos de una vez es lo que traba el clic en la pestaña.
  const { paged: pagedLeads, ...pgLeads } = usePaged(filtered, 30);

  return (
    <div style={{display:'grid',gridTemplateColumns:MOB?'1fr':'300px 1fr',gap:16,height:MOB?'auto':'75vh'}}>
      <div style={{background:'var(--card-bg,#fff)', borderRadius:16, border:'1px solid #e5e7eb',display:'flex',flexDirection:'column',overflow:'hidden'}}>
        <div style={{padding:'12px 14px',borderBottom:'1px solid #eaecf0'}}>
          <div style={{fontSize:11,color:p,fontWeight:700,textTransform:'uppercase',marginBottom:6}}>Conversaciones del bot</div>
          <input value={search} onChange={e=>setSearch(e.target.value)} placeholder="Buscar cliente..." style={{width:'100%',padding:'7px 11px',borderRadius:8,border:'1px solid #e2e8f0',fontSize:12,outline:'none',boxSizing:'border-box',marginBottom:6}}/>
          <div style={{display:'flex',gap:6,alignItems:'center'}}>
            <input type="date" value={fromDate} max={toDate||undefined} onChange={e=>setFromDate(e.target.value)}
              style={{flex:1,padding:'6px 8px',borderRadius:8,border:'1px solid #e2e8f0',fontSize:11,fontWeight:700,color:'#475569',outline:'none',cursor:'pointer'}}/>
            <span style={{fontSize:10,color:'#94a3b8',fontWeight:700}}>a</span>
            <input type="date" value={toDate} min={fromDate||undefined} onChange={e=>setToDate(e.target.value)}
              style={{flex:1,padding:'6px 8px',borderRadius:8,border:'1px solid #e2e8f0',fontSize:11,fontWeight:700,color:'#475569',outline:'none',cursor:'pointer'}}/>
            {(fromDate||toDate) && <button onClick={()=>{setFromDate('');setToDate('');}} title="Limpiar fechas" style={{background:'none',border:'none',cursor:'pointer',color:'#94a3b8',padding:2}}><X size={13}/></button>}
          </div>
        </div>
        <div style={{flex:1,overflowY:'auto'}}>
          {pagedLeads.map(lead=>{
            const nv=NIVEL[lead.nivel]||NIVEL.frio;
            const isSel=sel?.id===lead.id;
            return(<div key={lead.id} onClick={()=>load(lead)} style={{padding:'10px 14px',borderBottom:'1px solid #f1f5f9',cursor:'pointer',background:isSel?`${p}08`:'transparent',borderLeft:isSel?`3px solid ${p}`:'3px solid transparent'}}>
              <div style={{display:'flex',justifyContent:'space-between',marginBottom:3}}>
                <span style={{fontSize:12,fontWeight:700,color:'var(--dark,#0f172a)'}}>{lead.nombre}</span>
                <span style={{fontSize:10,fontWeight:700,color:nv.c,background:nv.bg,padding:'1px 7px',borderRadius:999}}>{nv.l}</span>
              </div>
              <div style={{fontSize:11,color:'#64748b'}}>{lead.telefono}</div>
            </div>);
          })}
          {filtered.length===0&&<div style={{padding:20,textAlign:'center',color:'#94a3b8',fontSize:12}}>Sin leads</div>}
          <Pagination {...pgLeads} primary={p} label="leads" />
        </div>
      </div>
      <div style={{background:'var(--card-bg,#fff)', borderRadius:16, border:'1px solid #e5e7eb',display:'flex',flexDirection:'column',overflow:'hidden'}}>
        {!sel?(<div style={{flex:1,display:'flex',alignItems:'center',justifyContent:'center',flexDirection:'column',gap:8}}>
          <MessageCircle size={36} color="#e2e8f0"/>
          <div style={{fontSize:13,color:'#94a3b8',fontWeight:600}}>Selecciona un lead para ver la conversacion</div>
        </div>):(
          <>
            <div style={{padding:'12px 16px',borderBottom:'1px solid #eaecf0',display:'flex',justifyContent:'space-between',alignItems:'center'}}>
              <div>
                <div style={{fontSize:14,fontWeight:700,color:'var(--dark,#0f172a)'}}>{sel.nombre}</div>
                <div style={{fontSize:11,color:'#64748b'}}>{sel.telefono} · Score: {sel.score}/100</div>
              </div>
              <button onClick={()=>window.open(`https://wa.me/${(()=>{let n=(sel.telefono||'').replace(/\D/g,'');if(n.startsWith('0')&&n.length===10)n='593'+n.substring(1);else if(n.length===9)n='593'+n;return n;})()}?text=Hola%20${encodeURIComponent(sel.nombre||'')}%2C%20soy%20del%20equipo%20BUSIMOTT`,'_blank')}
                style={{padding:'7px 14px',borderRadius:8,border:'none',background:'#f0fdf9',color:'#059669',fontWeight:700,fontSize:12,cursor:'pointer',display:'flex',alignItems:'center',gap:5}}>
                <MessageCircle size={12}/> Abrir WhatsApp
              </button>
            </div>
            <div style={{flex:1,overflowY:'auto',padding:'14px 16px',display:'flex',flexDirection:'column',gap:8,background:'#f8fafc'}}>
              {loading&&<div style={{textAlign:'center',color:'#94a3b8',fontSize:12,padding:20}}>Cargando...</div>}
              {!loading&&convs.length===0&&<div style={{textAlign:'center',color:'#94a3b8',fontSize:12,padding:20}}>Sin mensajes aun</div>}
              {convs.map((m,i)=>{
                const isBot=m.direction==='outbound';
                return(<div key={i} style={{display:'flex',justifyContent:isBot?'flex-start':'flex-end'}}>
                  <div style={{maxWidth:'70%'}}>
                    <div style={{fontSize:10,color:'#94a3b8',marginBottom:2,textAlign:isBot?'left':'right'}}>{isBot?'Claudia (Bot)':sel.nombre}</div>
                    <div style={{padding:'9px 13px',borderRadius:isBot?'4px 12px 12px 12px':'12px 4px 12px 12px',background:isBot?'#fff':p,color:isBot?'var(--dark,#0f172a)':'#fff',fontSize:12,lineHeight:1.5,border:isBot?'1px solid #e2e8f0':'none'}}>
                      {m.message_text&&m.message_text.startsWith('[IMG:')?(()=>{const u=m.message_text.match(/\[IMG:(.*?)\]/);return u?(<><img src={(process.env.REACT_APP_API_URL||'')+u[1]} alt="Imagen" style={{maxWidth:'100%',borderRadius:8,marginBottom:4,cursor:'pointer'}} onClick={()=>window.open((process.env.REACT_APP_API_URL||'')+u[1],'_blank')}/><div style={{fontSize:11}}>{m.message_text.replace(/\[IMG:.*?\]\s*/,'')}</div></>):m.message_text;})():m.message_text}
                    </div>
                    <div style={{fontSize:10,color:'#cbd5e1',marginTop:2,textAlign:isBot?'left':'right'}}>
                      {m.created_at?new Date(m.created_at).toLocaleString('es-EC',{month:'short',day:'numeric',hour:'2-digit',minute:'2-digit'}):''}
                    </div>
                  </div>
                </div>);
              })}
              <div ref={endRef}/>
            </div>
            <div style={{padding:'10px 16px',borderTop:'1px solid #eaecf0',background:'#fff',display:'flex',gap:8,alignItems:'flex-end'}}>
              <textarea id="adm-chat-input" rows={2} placeholder="Escribe un mensaje..." style={{flex:1,padding:'9px 12px',borderRadius:9,border:'1px solid #e2e8f0',fontSize:12,resize:'none',outline:'none',fontFamily:'inherit'}}
                onKeyDown={e=>{if(e.key==='Enter'&&!e.shiftKey){e.preventDefault();document.getElementById('adm-btn-send')?.click();}}}/>
              <div style={{display:'flex',flexDirection:'column',gap:5}}>
                <button id="adm-btn-send" onClick={async()=>{
                  const inp=document.getElementById('adm-chat-input');
                  const txt=inp?.value?.trim();
                  if(!txt)return;
                  try{
                    let sp=(sel.telefono||'').replace(/\D/g,'');
                    if(sp.startsWith('0')&&sp.length===10) sp='593'+sp.substring(1);
                    else if(sp.length===9) sp='593'+sp;
                    await apiFetch('POST','/api/messages/send',{leadId:sel.id,phone:sp,message:txt,companyId:sel.company_id});
                    inp.value='';load(sel);
                  }catch(e){alert('Error: '+e.message);}
                }} style={{padding:'9px 14px',borderRadius:8,border:'none',background:'#25d366',color:'#fff',fontWeight:700,fontSize:12,cursor:'pointer',display:'flex',alignItems:'center',gap:4}}>
                  <MessageCircle size={12}/> Enviar
                </button>
                <button onClick={()=>window.open(`https://wa.me/${toWA(sel.telefono)}?text=Hola`,'_blank')} style={{padding:'7px 14px',borderRadius:8,border:'none',background:'#f0fdf9',color:'#059669',fontWeight:700,fontSize:11,cursor:'pointer'}}>
                  Abrir WA
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}


// ── Canal Stats — Estadísticas por red social ─────────────────────────────
function CanalStatsCompleto({ leads = [], clients = [] }) {
  const total = leads.length || 1;

  const canales = { facebook:0, instagram:0, tiktok:0, whatsapp:0, otro:0 };
  leads.forEach(l => {
    const c = (l.canal || l.origen || '').toLowerCase();
    if      (c.includes('facebook')  || c.includes('fb'))  canales.facebook++;
    else if (c.includes('instagram') || c.includes('ig'))  canales.instagram++;
    else if (c.includes('tiktok')    || c.includes('tik')) canales.tiktok++;
    else if (c.includes('whatsapp')  || c.includes('wa') || c.includes('bot')) canales.whatsapp++;
    else canales.otro++;
  });

  const CFG = [
    { key:'whatsapp',  label:'WhatsApp',  color:'#25D366', bg:'#f0fdf9',
      icon:<svg viewBox="0 0 24 24" width="22" height="22" fill="#25D366"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg> },
    { key:'facebook',  label:'Facebook',  color:'#1877F2', bg:'#EBF5FF',
      icon:<svg viewBox="0 0 24 24" width="22" height="22" fill="#1877F2"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg> },
    { key:'instagram', label:'Instagram', color:'#E1306C', bg:'#FEE8F0',
      icon:<svg viewBox="0 0 24 24" width="22" height="22"><defs><radialGradient id="rg1" cx="30%" cy="107%"><stop offset="0%" stopColor="#fdf497"/><stop offset="45%" stopColor="#fd5949"/><stop offset="60%" stopColor="#d6249f"/><stop offset="90%" stopColor="#285AEB"/></radialGradient></defs><path fill="url(#rg1)" d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/></svg> },
    { key:'tiktok',    label:'TikTok',    color:'#010101', bg:'#f0f0f0',
      icon:<svg viewBox="0 0 24 24" width="22" height="22"><path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z" fill="#010101"/></svg> },
    { key:'otro',      label:'Otros',     color:'#94a3b8', bg:'#f8fafc',
      icon:<svg viewBox="0 0 24 24" width="22" height="22" fill="#94a3b8"><circle cx="12" cy="12" r="10"/><path fill="#fff" d="M12 6a1.5 1.5 0 110 3 1.5 1.5 0 010-3zm0 5a1 1 0 011 1v5a1 1 0 11-2 0v-5a1 1 0 011-1z"/></svg> },
  ];

  const activos = CFG.filter(c => canales[c.key] > 0);
  if (activos.length === 0) return (
    <div style={{background:'#fff',borderRadius:16,padding:'18px 20px',border:'1px solid #eaecf0',marginBottom:20}}>
      <div style={{fontSize:12,fontWeight:800,color:'var(--dark,#0f172a)',marginBottom:8}}>Canales de captacion</div>
      <div style={{fontSize:12,color:'#94a3b8',textAlign:'center',padding:'12px 0'}}>Sin leads registrados aun</div>
    </div>
  );

  const top = activos.sort((a,b)=>canales[b.key]-canales[a.key])[0];

  return (
    <div style={{background:'#fff',borderRadius:16,padding:'18px 20px',border:'1px solid #eaecf0',marginBottom:20}}>
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:14}}>
        <div style={{fontSize:12,fontWeight:800,color:'var(--dark,#0f172a)',display:'flex',alignItems:'center',gap:6}}>
          Canales de captacion
        </div>
        <div style={{fontSize:11,color:'#94a3b8'}}>{leads.length} leads totales</div>
      </div>
      {top && (
        <div style={{background:top.bg,borderRadius:10,padding:'10px 14px',marginBottom:12,border:`1px solid ${top.color}30`,display:'flex',alignItems:'center',gap:10}}>
          {top.icon}
          <div>
            <div style={{fontSize:11,color:'#94a3b8',fontWeight:600}}>Canal principal</div>
            <div style={{fontSize:13,fontWeight:800,color:top.color}}>{top.label} — {canales[top.key]} leads ({Math.round((canales[top.key]/leads.length)*100)}%)</div>
          </div>
        </div>
      )}
      <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(120px,1fr))',gap:8}}>
        {activos.map((c,i)=>{
          const cnt = canales[c.key];
          const pct = Math.round((cnt/leads.length)*100);
          return(
            <div key={i} style={{background:c.bg,borderRadius:12,padding:'10px 12px',border:`1px solid ${c.color}20`,textAlign:'center'}}>
              <div style={{display:'flex',justifyContent:'center',marginBottom:6}}>{c.icon}</div>
              <div style={{fontSize:11,fontWeight:700,color:'#475569',marginBottom:2}}>{c.label}</div>
              <div style={{fontSize:20,fontWeight:800,color:c.color,lineHeight:1}}>{cnt}</div>
              <div style={{fontSize:10,color:'#94a3b8',marginTop:2}}>{pct}%</div>
              <div style={{background:`${c.color}20`,borderRadius:999,height:3,marginTop:6,overflow:'hidden'}}>
                <div style={{width:`${pct}%`,height:'100%',background:c.color,borderRadius:999}}/>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Costo WhatsApp — mensajes cobrados por Meta (solo panel admin) ──────────
function CostoWhatsApp() {
  const [month, setMonth] = useState(() => new Date().toLocaleString('sv-SE',{timeZone:'America/Guayaquil'}).slice(0,7));
  const [d, setD] = useState(null);
  useEffect(() => { setD(null); apiFetch('GET', `/api/admin/wa-billing?month=${month}`).then(setD).catch(()=>setD({})); }, [month]);
  const m = d?.meta;
  const dias = Object.entries(m?.dias||{}).sort();
  return (
    <div style={{background:CB,borderRadius:16,padding:'18px 20px',border:`1px solid ${CBorder}`,boxShadow:CShadow,margin:'20px 0'}}>
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',flexWrap:'wrap',gap:8,marginBottom:12}}>
        <div style={{fontSize:13,fontWeight:800,color:NAVY}}>Costo de WhatsApp (Meta)</div>
        <input type="month" value={month} onChange={e=>e.target.value&&setMonth(e.target.value)} style={{border:'1px solid #e2e8f0',borderRadius:8,padding:'4px 8px',fontSize:12}}/>
      </div>
      {!d ? <div style={{fontSize:12,color:'#94a3b8'}}>Cargando…</div> : !m ? <div style={{fontSize:12,color:'#94a3b8'}}>No se pudo consultar a Meta</div> : <>
        <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(130px,1fr))',gap:8}}>
          {[['Cobrados',m.cobrados,'#ef4444'],['Costo',`$${m.costo.toFixed(2)}`,'#ef4444'],['Gratis (anuncio 72h)',m.gratis_anuncio,'#10b981'],['Gratis (respuesta)',m.gratis_servicio,'#10b981']].map(([l,v,c])=>(
            <div key={l} style={{background:`${c}10`,borderRadius:12,padding:'10px 12px',textAlign:'center'}}>
              <div style={{fontSize:11,fontWeight:700,color:'#475569'}}>{l}</div>
              <div style={{fontSize:20,fontWeight:800,color:c}}>{v}</div>
            </div>))}
        </div>
        {dias.length>0 && <div style={{fontSize:11,color:'#64748b',marginTop:10}}>Días con cobro: {dias.map(([k,v])=>`${k.slice(8)}/${k.slice(5,7)}: ${v}`).join(' · ')}</div>}
        <div style={{fontSize:12,fontWeight:700,color:NAVY,margin:'14px 0 6px'}}>Por canal del lead</div>
        {(d.canales||[]).length===0
          ? <div style={{fontSize:11,color:'#94a3b8'}}>Aún sin datos por canal (se registran desde el 28/10/2026)</div>
          : <div style={{overflowX:'auto'}}><table style={{width:'100%',fontSize:12,borderCollapse:'collapse'}}>
              <thead><tr style={{color:'#64748b',textAlign:'left'}}><th>Canal</th><th>Enviados</th><th>Cobrados</th><th>Gratis anuncio</th><th>Gratis respuesta</th></tr></thead>
              <tbody>{d.canales.map(c=><tr key={c.canal} style={{borderTop:'1px solid #f1f5f9'}}><td style={{padding:'5px 0',fontWeight:700}}>{c.canal}</td><td>{c.enviados}</td><td style={{color:'#ef4444',fontWeight:700}}>{c.cobrados}</td><td>{c.gratis_anuncio}</td><td>{c.gratis_servicio}</td></tr>)}</tbody>
            </table></div>}
      </>}
    </div>
  );
}

export default function DashboardAdmin({user,currentView,onNavigate,primary=DS.primary,pageBg='#f0f4ff',cardBg='#ffffff',cardBorder,cardShadow}) {
  DS = makeDS(primary);
  CB = cardBg || '#ffffff';
  CBorder = cardBorder || `${primary}20`;
  MOB = window.innerWidth < 768;
  CShadow = cardShadow || `0 2px 12px ${primary}14`;
  const [users,     setUsers]     = useState([]);
  const [leads,     setLeads]     = useState([]);
  const [clients,   setClients]   = useState([]);
  const [employees, setEmployees] = useState([]);
  const [stats,     setStats]     = useState(null);
  const [planInfo,  setPlanInfo]  = useState(null);
  const [loadingDB, setLoadingDB] = useState(true);
  const [confirmDlg, setConfirmDlg] = useState(null);
  _setConfirmA = setConfirmDlg;

  const nav  = onNavigate || (() => {});
  const view = currentView || 'dashboard';

  const cargarDatos = useCallback(async (silencioso) => {
    if (silencioso === true && document.hidden) return; // ponytail: no bajar ~6MB cada minuto con la pestaña en segundo plano
    if (silencioso !== true) setLoadingDB(true);
    // Cada respuesta se pinta en cuanto llega: antes Promise.all esperaba a la más lenta (clientes/leads)
    const err = e => console.error('Error cargando datos admin:', e.message);
    await Promise.all([
      apiFetch('GET','/api/admin/users').then(usersRes => { if (usersRes?.users) { setUsers(usersRes.users.map(u=>({...u,active:u.status==='active',pass:'',ini:(u.name||'??').split(' ').map(n=>n[0]).join('').slice(0,2).toUpperCase(),lastLogin:u.last_login?'Activo':'Nunca',created:u.created_at?.split('T')[0]||'—'}))); setPlanInfo(usersRes.plan||null); } }).catch(err),
      apiFetch('GET','/api/admin/clients?limit=100000000').then(clientsRes => { if (clientsRes?.clients) setClients(clientsRes.clients.map(c=>({...c, result:c.result||c.metadata?.result||null, heat:c.metadata?.heat||c.heat||'tibio', asesorId:c.assigned_advisor_id||c.asesorId||'', asesor:c.advisor_name||c.asesor||'', lossReason:c.lossReason||c.metadata?.lossReason||''}))); }).catch(err),
      apiFetch('GET','/api/admin/leads').then(leadsRes => { if (Array.isArray(leadsRes)) setLeads(leadsRes.map(l=>({...l,nivel:l.nivel||'tibio',score:l.score||50,msgBot:l.msg_bot||'',asesor:l.advisor_name||'Sin asignar',asesorId:l.advisor_id||''}))); }).catch(err),
      apiFetch('GET','/api/admin/employees').then(empsRes => { if (Array.isArray(empsRes)) setEmployees(empsRes.map(e=>({...e,status:e.status||'activo',ini:(e.name||'??').split(' ').map(n=>n[0]).join('').slice(0,2).toUpperCase()}))); }).catch(()=>{}),
      apiFetch('GET','/api/admin/stats').then(statsRes => { if (statsRes) setStats(statsRes); }).catch(()=>{}),
    ]);
    setLoadingDB(false);
  }, []);

  // Refresco cada 60s: el panel se deja abierto todo el día y los números del equipo
  // se quedaban congelados en la carga inicial hasta recargar a mano.
  useEffect(() => { cargarDatos(); const t = setInterval(() => cargarDatos(true), 120000); return () => clearInterval(t); }, [cargarDatos]); // 2 min como el asesor: cada refresco baja ~900 KB

  return (
    <>
      {view === 'dashboard'            && <TabDashboard        user={user} users={users} leads={leads} stats={stats} planInfo={planInfo} clients={clients} onNavigate={nav} />}
      {view === 'dashboard'            && <CostoWhatsApp />}
      {view === 'clients'              && <><PageHead title="Clientes" sub="Todos los clientes de la empresa · toca uno para ver el detalle o marcar la venta"/><TabClientes         clients={clients} users={users} onRefresh={cargarDatos} /></>}
      {view === 'sales'                && <TabVentas           clients={clients} users={users} />}
      {view === 'messages'             && <><PageHead title="Mensajes" sub="Chat interno con supervisores y asesores"/><TabMensajes         users={users} /></>}
      {view === 'users'                && <><PageHead title="Usuarios" sub="Accesos del equipo al CRM"/><TabUsuarios         users={users} setUsers={setUsers} planInfo={planInfo} onRefresh={cargarDatos} /></>}
      {view === 'employees'            && <><PageHead title="Empleados" sub="Personal de la empresa"/><TabEmpleados        employees={employees} setEmployees={setEmployees} onRefresh={cargarDatos} /></>}
      {view === 'pipeline'              && <TabPipelineAdmin    leads={leads} setLeads={setLeads} clients={clients} users={users} />}
      {view === 'rendimiento_equipo'    && <TabRendimientoEquipo clients={clients} users={users} leads={leads}/>}
      {view === 'conversaciones'        && <><PageHead title="Conversaciones" sub="Chats del bot de WhatsApp con cada lead"/><TabConversaciones  leads={leads}/></>}
      {view === 'actividades_equipo'    && <TabActividadesEquipo />}
      {confirmDlg&&<ConfirmModal msg={confirmDlg.msg} onYes={confirmDlg.onYes} onClose={()=>setConfirmDlg(null)}/>}
    </>
  );
}
 
